(() => {
  if (customElements.get('restposten-finder')) return;

  class RestpostenFinder extends HTMLElement {
    connectedCallback() {
      this.config = this.readConfig();
      this.form = this.querySelector('[data-finder-form]');
      this.modeInput = this.querySelector('[data-mode-input]');
      this.modeButtons = Array.from(this.querySelectorAll('[data-mode]'));
      this.resultsSection = this.querySelector('[data-results-section]');
      this.resultGrid = this.querySelector('[data-result-grid]');
      this.resultSlider = this.querySelector('[data-result-slider]');
      this.sliderControls = this.querySelector('[data-result-slider-controls]');
      this.sliderCurrent = this.querySelector('[data-slider-current]');
      this.sliderTotal = this.querySelector('[data-slider-total]');
      this.sliderLabel = this.querySelector('[data-slider-label]');
      this.sliderPrev = this.querySelector('[data-slider-prev]');
      this.sliderNext = this.querySelector('[data-slider-next]');
      this.resultsTitle = this.querySelector('[data-results-title]');
      this.resultsSummary = this.querySelector('[data-results-summary]');
      this.emptyState = this.querySelector('[data-empty]');
      this.planResult = this.querySelector('[data-plan-result]');
      this.catalogStatus = this.querySelector('[data-catalog-status]');
      this.visualKicker = this.querySelector('[data-visual-kicker]');
      this.visualTitle = this.querySelector('[data-visual-title]');
      this.visualText = this.querySelector('[data-visual-text]');
      this.visualMetrics = this.querySelector('[data-visual-metrics]');
      this.metricRaw = this.querySelector('[data-metric-raw]');
      this.metricSecondaryLabel = this.querySelector('[data-metric-secondary-label]');
      this.metricSecondary = this.querySelector('[data-metric-secondary]');
      this.metricWaste = this.querySelector('[data-metric-waste]');
      this.drawingLength = this.querySelector('[data-drawing-length]');
      this.drawingWidth = this.querySelector('[data-drawing-width]');
      this.drawingPanel = this.querySelector('[data-drawing-panel]');
      this.drawingGrid = this.querySelector('[data-drawing-grid]');
      this.drawingGlow = this.querySelector('[data-drawing-glow]');
      this.drawingLengthLine = this.querySelector('[data-drawing-length-line]');
      this.drawingWidthLine = this.querySelector('[data-drawing-width-line]');
      this.dimensionHint = this.querySelector('[data-dimension-hint]');
      this.areaOnlyFields = Array.from(this.querySelectorAll('[data-area-only]'));
      this.maxPieceLength = this.querySelector('[data-max-piece-length]');
      this.pieceRows = this.querySelector('[data-piece-rows]');
      this.addPieceButton = this.querySelector('[data-add-piece-row]');
      this.surfaceSelect = this.querySelector('[data-surface-select]');
      this.surfaceAny = this.querySelector('[data-surface-any]');
      this.surfaceMix = this.querySelector('[data-surface-mix]');
      this.surfaceModeSelect = this.querySelector('[data-surface-mode]');
      this.surfaceExplanation = this.querySelector('[data-surface-explanation]');
      this.constructionSelect = this.querySelector('[data-construction-select]');
      this.openSurfaceControls = this.querySelector('[data-open-surface-controls]');
      this.closedSurfaceNote = this.querySelector('[data-closed-surface-note]');
      this.meshSelect = this.querySelector('[data-mesh-select]');
      this.meshAny = this.querySelector('[data-mesh-any]');
      this.meshMix = this.querySelector('[data-mesh-mix]');
      this.meshModeSelect = this.querySelector('[data-mesh-mode]');
      this.meshSpecific = this.querySelector('[data-mesh-specific]');
      this.meshExplanation = this.querySelector('[data-mesh-explanation]');
      this.heightOptions = this.querySelector('[data-height-options]');
      this.heightManual = this.querySelector('[data-height-manual]');
      this.heightAny = this.querySelector('[data-height-any]');
      this.heightAvailabilityNote = this.querySelector('[data-height-availability-note]');
      this.wizardPanels = Array.from(this.querySelectorAll('[data-wizard-panel]'));
      this.wizardButtons = Array.from(this.querySelectorAll('[data-wizard-go]'));
      this.wizardSummary = this.querySelector('[data-wizard-summary]');
      this.wizardSummaryText = this.querySelector('[data-wizard-summary-text]');
      this.wizardNext = this.querySelector('[data-wizard-next]');
      this.wizardSubmit = this.querySelector('[data-wizard-submit]');
      this.wizardBack = this.querySelector('[data-wizard-back]');
      this.loadingOverlay = this.querySelector('[data-finder-loading]');

      this.products = [];
      this.inventoryProducts = [];
      this.lastCriteria = null;
      this.activePlan = null;
      this.cutServicePrices = { first: null, additional: null };
      this.storageKey = `fibrolux-restposten-finder:${window.location.pathname}`;
      this.sliderIndex = 0;
      this.sliderUpdateFrame = 0;
      this.planSliderIndex = 0;
      this.planSliderUpdateFrame = 0;
      this.searchPending = false;

      this.bindEvents();
      this.setMode('piece');
      this.syncPreferenceControls();
      this.setWizardStep('project');
      this.loadCatalog();
    }

    disconnectedCallback() {
      if (this.onSliderResize) window.removeEventListener('resize', this.onSliderResize);
      if (this.sliderUpdateFrame) window.cancelAnimationFrame(this.sliderUpdateFrame);
      if (this.planSliderUpdateFrame) window.cancelAnimationFrame(this.planSliderUpdateFrame);
    }

    readConfig() {
      try {
        return JSON.parse(this.querySelector('[data-finder-config]')?.textContent || '{}');
      } catch (error) {
        console.warn('Restposten-Finder: Konfiguration konnte nicht gelesen werden.', error);
        return {};
      }
    }

    bindEvents() {
      this.modeButtons.forEach((button) => {
        button.addEventListener('click', () => this.setMode(button.dataset.mode));
      });

      this.wizardButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const step = button.dataset.wizardGo;
          if (step === 'requirements' && this.wizardStep === 'project' && !this.validateProjectStep()) return;
          if (step === 'results' && this.resultsSection.hidden) return;
          this.setWizardStep(step);
        });
      });

      this.wizardNext?.addEventListener('click', () => {
        if (!this.validateProjectStep()) return;
        this.setWizardStep('requirements');
      });

      this.wizardBack?.addEventListener('click', () => {
        this.setWizardStep(this.wizardStep === 'requirements' ? 'project' : 'requirements');
      });

      this.addPieceButton?.addEventListener('click', () => this.addPieceRow());
      this.form?.addEventListener('click', (event) => {
        const shortcut = event.target.closest('[data-set-max-piece-length]');
        if (!shortcut || !this.maxPieceLength) return;
        this.maxPieceLength.value = shortcut.dataset.setMaxPieceLength || '2400';
        this.maxPieceLength.dispatchEvent(new Event('input', { bubbles: true }));
        this.maxPieceLength.focus();
      });
      this.pieceRows?.addEventListener('click', (event) => {
        const removeButton = event.target.closest('[data-remove-piece-row]');
        if (!removeButton) return;
        const row = removeButton.closest('[data-piece-row]');
        if (!row || this.getPieceRows().length <= 1) return;
        row.remove();
        this.syncPieceRows();
        this.updateDrawingLabels();
      });

      this.form?.addEventListener('submit', (event) => {
        event.preventDefault();
        if (!this.form.checkValidity()) {
          this.form.reportValidity();
          return;
        }
        this.runSearch({ moveToResults: true });
      });

      this.form?.addEventListener('reset', () => {
        window.setTimeout(() => {
          this.setMode('piece');
          this.resultsSection.hidden = true;
          this.resultGrid.innerHTML = '';
          this.planResult.hidden = true;
          this.planResult.innerHTML = '';
          if (this.sliderControls) this.sliderControls.hidden = true;
          this.activePlan = null;
          this.clearStoredCriteria();
          this.resetPieceRows();
          this.syncPreferenceControls();
          this.setWizardStep('project');
          this.resetVisual();
          this.setLoading(false);
        }, 0);
      });

      this.form?.addEventListener('input', (event) => {
        if (event.target.matches('[name="split_direction"]')) {
          this.syncSplitDirection();
          return;
        }
        if (event.target.matches('[data-surface-mode], [data-mesh-mode]')) {
          this.applyPreferenceMode(event.target);
          this.syncPreferenceControls();
          this.updateMeshOptions();
          return;
        }
        if (event.target.matches('[data-surface-any], [data-surface-mix], [data-mesh-any], [data-mesh-mix], [data-height-any], [data-construction-select]')) {
          if (event.target.matches('[data-height-any]') && !event.target.checked) {
            const options = Array.from(this.heightOptions?.querySelectorAll('[data-height-option]') || []);
            options.forEach((input, index) => { input.checked = index === 0; });
          }
          const source = event.target.matches('[data-construction-select]')
            ? 'construction'
            : event.target.matches('[data-height-any]')
              ? 'height-mode'
              : '';
          this.syncPreferenceControls({ source });
          this.updateMeshOptions();
          return;
        }
        if (event.target.matches('[data-height-option], [data-surface-select], [name="color"]')) {
          this.syncPreferenceControls({ source: event.target.matches('[data-height-option]') ? 'height' : '' });
          this.updateMeshOptions();
          return;
        }
        if (!event.target.matches('[data-piece-length], [data-piece-width], [data-piece-quantity]')) return;
        this.updateDrawingLabels();
      });

      this.resultGrid?.addEventListener('click', (event) => {
        const pieceButton = event.target.closest('[data-select-piece-plan]');
        const planButton = event.target.closest('[data-show-area-plan]');
        const alternativeButton = event.target.closest('[data-show-alternative-plan]');
        const recommendedButton = event.target.closest('[data-show-recommended-plan]');
        if (pieceButton && this.lastCriteria) {
          const match = this.visibleMatches?.[Number(pieceButton.dataset.selectPiecePlan)];
          if (!match) return;
          const finishMode = pieceButton.dataset.pieceFinish === 'as-is' ? 'as-is' : 'cut';
          const plan = this.buildSinglePiecePlan(match, this.lastCriteria, finishMode);
          if (!plan) return;
          this.activePlan = plan;
          this.renderAreaPlan(plan, this.lastCriteria);
          this.updatePlanVisual(plan);
          this.resultsTitle.textContent = `Ihre Maßlösung aus ${this.productCode(match.product)}`;
          this.setResultsSummary(plan.acceptedNearFit
            ? `Sie übernehmen das verfügbare Liefermaß ${plan.targetLength}×${plan.targetWidth} mm ohne Zuschnitt. Wunschmaß und Abweichung werden in Plan-PDF und Bestellung festgehalten.`
            : 'Dieser Restposten ist ausgewählt. Zuschnittsplan, Plan-PDF und Warenkorb bleiben vollständig im Restposten-Finder.');
          this.markSelectedPlan(match.product.variantId);
          this.markSelectedRecommended(false);
          this.placePlanNearCard(pieceButton.closest('[data-result-index]'));
          this.planResult.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
        if (recommendedButton && this.recommendedPlan && this.lastCriteria) {
          this.activePlan = this.recommendedPlan;
          this.renderAreaPlan(this.recommendedPlan, this.lastCriteria);
          this.updatePlanVisual(this.recommendedPlan);
          const splitPiece = this.lastCriteria.mode === 'piece';
          const multiPieceOrder = this.recommendedPlan.isMultiPieceOrder === true;
          this.resultsTitle.textContent = multiPieceOrder ? 'Ihre optimierte Bedarfsliste' : splitPiece ? 'Ihre geteilte Maßlösung' : 'Ihre ausgewählte Flächenlösung';
          this.setResultsSummary(multiPieceOrder
            ? `Diese Lösung fertigt ${this.recommendedPlan.requestedPieceCount} Roste aus ${this.recommendedPlan.placements.length} Rohplatten. Bedarfsliste und Zuschnittsplan finden Sie direkt darunter.`
            : splitPiece
              ? 'Diese Lösung ist ausgewählt. Den Teilungs- und Zuschnittsplan finden Sie direkt darunter.'
            : 'Diese Lösung ist ausgewählt. Den Verlege- und Zuschnittsplan finden Sie direkt darunter.');
          this.markSelectedPlan(null);
          this.markSelectedRecommended(true);
          this.placePlanBelowOptions();
          this.planResult.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
        if (planButton) {
          const match = this.visibleMatches?.[Number(planButton.dataset.showAreaPlan)];
          if (!match || !this.lastCriteria) return;
          const plan = this.buildPlanFromSingleMatch(match, this.lastCriteria);
          this.activePlan = plan;
          this.renderAreaPlan(plan, this.lastCriteria);
          this.updatePlanVisual(plan);
          this.resultsTitle.textContent = `Ihre Flächenlösung mit ${this.productCode(match.product)}`;
          const rawPlateCount = this.planRawPlateCount(plan);
          this.resultsSummary.textContent = `${rawPlateCount} ${rawPlateCount === 1 ? 'Rohplatte wird' : 'Rohplatten werden'} aus dem Restposten ${this.productCode(match.product)} für Ihre Fläche verwendet.`;
          this.markSelectedPlan(match.product.variantId);
          this.markSelectedRecommended(false);
          this.placePlanNearCard(planButton.closest('[data-result-index]'));
          this.planResult.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
        if (alternativeButton) {
          const alternative = this.alternativePlans?.[Number(alternativeButton.dataset.showAlternativePlan)];
          if (!alternative) return;
          this.activePlan = alternative.plan;
          this.renderAreaPlan(alternative.plan, alternative.criteria);
          this.updatePlanVisual(alternative.plan);
          this.resultsTitle.textContent = 'Passende Alternativen';
          this.setResultsSummary(
            alternative.relaxesMaxPieces
              ? `Eine einteilige Belegung ist aktuell nicht verfügbar. Ausgewählt ist die kleinstmögliche verfügbare Lösung mit ${alternative.plan.placements.length} Teilstücken.`
              : alternative.explanation || `Ihre Auswahl: ${this.alternativeRequestLabel(alternative)}. Verfügbare Lösung: ${this.alternativePlanLabel(alternative)}.`,
            {
              alternative: true,
              title: alternative.relaxesMaxPieces
                ? 'Geteilte Lösung ausgewählt'
                : alternative.kind === 'area-reduced'
                  ? 'Kleinere Fläche ausgewählt'
                  : alternative.kind === 'area-narrow-strip'
                    ? 'Randstreifen bewusst ausgewählt'
                    : 'Sie haben eine Alternative ausgewählt'
            }
          );
          this.markSelectedPlan(null);
          this.markSelectedAlternative(Number(alternativeButton.dataset.showAlternativePlan));
          this.placePlanBelowOptions();
          this.planResult.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });

      this.sliderPrev?.addEventListener('click', () => this.scrollResultSlider(-1));
      this.sliderNext?.addEventListener('click', () => this.scrollResultSlider(1));
      this.resultGrid?.addEventListener('scroll', () => this.scheduleSliderUpdate(), { passive: true });
      this.resultGrid?.addEventListener('focusin', () => this.scheduleSliderUpdate());
      this.onSliderResize = () => {
        this.scheduleSliderUpdate();
        this.schedulePlanSliderUpdate();
      };
      window.addEventListener('resize', this.onSliderResize, { passive: true });

      this.planResult?.addEventListener('click', (event) => {
        const planSlideButton = event.target.closest('[data-plan-slide-to]');
        const planSlidePrev = event.target.closest('[data-plan-slider-prev]');
        const planSlideNext = event.target.closest('[data-plan-slider-next]');
        if (planSlideButton) this.scrollPlanSlider(Number(planSlideButton.dataset.planSlideTo));
        if (planSlidePrev) this.scrollPlanSlider(Math.max(0, (this.planSliderIndex || 0) - 1));
        if (planSlideNext) this.scrollPlanSlider(Math.min(1, (this.planSliderIndex || 0) + 1));
        const cartButton = event.target.closest('[data-add-plan-to-cart]');
        if (cartButton) this.addPlanToCart(cartButton);
        if (event.target.closest('[data-print-plan]')) window.print();
        if (event.target.closest('[data-back-to-options]')) {
          this.resultGrid.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    }

    setMode(mode) {
      const safeMode = mode === 'area' ? 'area' : 'piece';
      if (this.modeInput) this.modeInput.value = safeMode;
      this.dataset.mode = safeMode;

      this.modeButtons.forEach((button) => {
        const active = button.dataset.mode === safeMode;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });

      if (safeMode === 'area') {
        this.dimensionHint.textContent = 'Geben Sie die gesamte Projektfläche ein. Wir berechnen die benötigten Platten und passende Randzuschnitte.';
        this.visualKicker.textContent = 'Flächenplanung';
        this.visualTitle.textContent = 'Ihre Fläche effizient geplant.';
        this.visualText.textContent = 'Wir vergleichen mögliche Aufteilungen und bewerten Teilstückzahl, Gesamtpreis und Verschnitt gemeinsam.';
      } else {
        this.dimensionHint.textContent = 'Geben Sie ein oder mehrere Wunschmaße mit der jeweiligen Menge ein. Wir planen alle Roste gemeinsam.';
        this.visualKicker.textContent = 'Roste nach Maß';
        this.visualTitle.textContent = 'Ihre Maße. Passend aus Restposten.';
        this.visualText.textContent = 'Gleiche und unterschiedliche Maße werden gemeinsam nach Rohplatten, Preis und Verschnitt optimiert.';
      }
      this.areaOnlyFields.forEach((field) => {
        field.hidden = safeMode !== 'area';
      });
      this.syncPieceRows();
      this.visualMetrics.hidden = true;
      this.updateDrawingLabels();
    }

    getPieceRows() {
      return Array.from(this.pieceRows?.querySelectorAll('[data-piece-row]') || []);
    }

    addPieceRow(values = {}) {
      const source = this.getPieceRows()[0];
      if (!source || !this.pieceRows || this.getPieceRows().length >= 12) return null;
      const row = source.cloneNode(true);
      const length = row.querySelector('[data-piece-length]');
      const width = row.querySelector('[data-piece-width]');
      const quantity = row.querySelector('[data-piece-quantity]');
      if (length) length.value = values.length ? String(values.length) : '';
      if (width) width.value = values.width ? String(values.width) : '';
      if (quantity) quantity.value = String(Math.max(1, Number(values.quantity || 1)));
      this.pieceRows.append(row);
      this.syncPieceRows();
      return row;
    }

    resetPieceRows() {
      const rows = this.getPieceRows();
      rows.slice(1).forEach((row) => row.remove());
      const first = this.getPieceRows()[0];
      const quantity = first?.querySelector('[data-piece-quantity]');
      if (quantity) quantity.value = '1';
      this.syncPieceRows();
    }

    syncPieceRows() {
      const pieceMode = this.modeInput?.value !== 'area';
      const rows = this.getPieceRows();
      rows.forEach((row, index) => {
        const active = pieceMode || index === 0;
        row.hidden = !active;
        row.querySelectorAll('input').forEach((input) => {
          input.disabled = !active || (!pieceMode && input.matches('[data-piece-quantity]'));
        });
        const number = row.querySelector('[data-piece-number]');
        if (number) number.textContent = `Maß ${index + 1}`;
        const remove = row.querySelector('[data-remove-piece-row]');
        if (remove) remove.hidden = !pieceMode || rows.length === 1;
        row.querySelectorAll('[data-piece-only]').forEach((element) => {
          if (!element.matches('[data-remove-piece-row]')) element.hidden = !pieceMode;
        });
      });
      if (this.addPieceButton) this.addPieceButton.hidden = !pieceMode || rows.length >= 12;
      this.syncSplitDirection();
    }

    splitDirection(criteria) {
      return criteria?.mode === 'area' && ['length', 'width'].includes(criteria.splitDirection)
        ? criteria.splitDirection : 'free';
    }

    splitDirectionLabel(criteria) {
      const direction = this.splitDirection(criteria);
      if (direction === 'length') return `Breite durchgehend: ${criteria.width} mm je Teil`;
      if (direction === 'width') return `Länge durchgehend: ${criteria.length} mm je Teil`;
      return '';
    }

    syncSplitDirection() {
      const field = this.querySelector('[data-split-direction]');
      if (!field) return;
      const rows = this.getPieceRows();
      const first = rows[0];
      const length = Number(first?.querySelector('[data-piece-length]')?.value || 0);
      const width = Number(first?.querySelector('[data-piece-width]')?.value || 0);
      const areaMode = this.modeInput?.value === 'area';
      field.hidden = !areaMode;
      field.disabled = !areaMode;
      const direction = this.form.elements.namedItem('split_direction')?.value || 'free';
      field.querySelector('[data-direction-width]').textContent = width ? `${width} mm je Teil` : 'Volle Breite je Teil';
      field.querySelector('[data-direction-length]').textContent = length ? `${length} mm je Teil` : 'Volle Länge je Teil';
      field.querySelector('[data-direction-hint]').textContent = direction === 'length'
        ? `Jedes Teil deckt die ${width ? `${width} mm ` : ''}Breite vollständig ab. Stöße sind nur entlang der Länge erlaubt.`
        : direction === 'width'
          ? `Jedes Teil deckt die ${length ? `${length} mm ` : ''}Länge vollständig ab. Stöße sind nur entlang der Breite erlaubt.`
          : 'Die Aufteilung darf in beide Richtungen erfolgen.';
    }

    syncPreferenceControls({ source = '' } = {}) {
      const closed = this.constructionSelect?.value === 'closed';
      if (this.openSurfaceControls) this.openSurfaceControls.hidden = closed;
      if (this.closedSurfaceNote) this.closedSurfaceNote.hidden = !closed;
      if (this.surfaceAny) this.surfaceAny.disabled = closed;
      if (closed) {
        if (this.surfaceSelect) this.surfaceSelect.disabled = true;
        if (this.surfaceMix) {
          this.surfaceMix.checked = false;
          this.surfaceMix.disabled = true;
        }
      } else {
        if (this.surfaceAny) this.surfaceAny.disabled = false;
        const surfaceMode = this.surfaceAny?.checked
          ? this.surfaceMix?.checked ? 'mix' : 'auto'
          : this.surfaceSelect?.value === 'besandet' ? 'besandet' : 'konkav';
        if (this.surfaceModeSelect) {
          this.surfaceModeSelect.disabled = false;
          this.surfaceModeSelect.value = surfaceMode;
        }
        if (this.surfaceSelect) this.surfaceSelect.disabled = this.surfaceAny?.checked === true;
        if (this.surfaceMix) this.surfaceMix.disabled = this.surfaceAny?.checked !== true;
        if (this.surfaceExplanation) {
          this.surfaceExplanation.textContent = surfaceMode === 'mix'
            ? 'Für mehr Auswahl dürfen konkave und besandete offene Roste gemeinsam in einem Vorschlag verwendet werden.'
            : surfaceMode === 'konkav'
              ? 'Es werden ausschließlich offene Roste mit konkaver Oberfläche berücksichtigt.'
              : surfaceMode === 'besandet'
                ? 'Es werden ausschließlich offene Roste mit besandeter Oberfläche berücksichtigt.'
                : 'Der Finder wählt konkav oder besandet und verwendet diese Oberfläche im gesamten Vorschlag.';
        }
      }
      const meshMode = this.meshAny?.checked
        ? this.meshMix?.checked ? 'mix' : 'auto'
        : 'specific';
      if (this.meshModeSelect) this.meshModeSelect.value = meshMode;
      if (this.meshSpecific) this.meshSpecific.hidden = meshMode !== 'specific';
      if (this.meshSelect) {
        this.meshSelect.disabled = meshMode !== 'specific';
        this.meshSelect.removeAttribute('hidden');
      }
      if (this.meshMix) this.meshMix.disabled = this.meshAny?.checked !== true;
      if (this.meshExplanation) {
        this.meshExplanation.textContent = meshMode === 'mix'
          ? 'Für mehr Auswahl dürfen unterschiedliche Maschenweiten gemeinsam in einem Vorschlag verwendet werden.'
          : meshMode === 'specific'
            ? 'Es werden nur Restposten mit der ausgewählten Maschenweite berücksichtigt.'
            : 'Der Finder wählt eine verfügbare Maschenweite und hält sie im gesamten Vorschlag einheitlich.';
      }
      const allHeights = this.heightAny?.checked !== false;
      if (this.heightManual) this.heightManual.hidden = allHeights;
      this.heightOptions?.classList.remove('is-disabled');
      const heightInputs = Array.from(this.heightOptions?.querySelectorAll('[data-height-option]') || []);
      this.syncConstructionHeightAvailability(allHeights, heightInputs, { source });
    }

    productConstruction(product) {
      if (product?.surface === 'geschlossen-besandet') return 'closed';
      if (['konkav', 'besandet'].includes(product?.surface)) return 'open';
      return 'unknown';
    }

    syncConstructionHeightAvailability(allHeights, heightInputs, { source = '' } = {}) {
      const catalogSource = this.inventoryProducts?.length ? this.inventoryProducts : this.products;
      const products = (catalogSource || [])
        .filter((product) => product.height)
        .filter((product) => !product.inventoryKnown || product.inventoryQuantity > 0);
      if (!products.length) {
        heightInputs.forEach((input) => { input.disabled = allHeights; });
        return;
      }

      const selectedConstruction = String(this.constructionSelect?.value || 'any');
      const matchesConstruction = (product, construction) => (
        construction === 'any' || this.productConstruction(product) === construction
      );

      const selectedBefore = heightInputs
        .filter((input) => input.checked)
        .map((input) => Number(input.value))
        .filter(Number.isFinite);
      const removedHeights = [];

      heightInputs.forEach((input) => {
        const available = products.some((product) => (
          String(product.height) === String(input.value)
          && matchesConstruction(product, selectedConstruction)
        ));
        const label = input.closest('label');
        input.disabled = allHeights || !available;
        if (!available && input.checked) {
          removedHeights.push(Number(input.value));
          input.checked = false;
        }
        label?.classList.toggle('is-unavailable', !available);
        label?.setAttribute('aria-disabled', String(!available));
        if (label) {
          if (available) {
            label.removeAttribute('title');
          } else {
            const constructionLabel = selectedConstruction === 'closed'
              ? 'geschlossene Gitterroste'
              : selectedConstruction === 'open'
                ? 'offene Gitterroste'
                : 'die aktuelle Auswahl';
            label.title = `Für ${constructionLabel} derzeit nicht verfügbar`;
          }
        }
      });

      let replacement = null;
      if (!allHeights && !heightInputs.some((input) => input.checked)) {
        const availableInputs = heightInputs.filter((input) => !input.disabled);
        const nearestReference = selectedBefore.length ? selectedBefore : [Number(availableInputs[0]?.value || 0)];
        replacement = availableInputs.sort((left, right) => {
          const leftValue = Number(left.value);
          const rightValue = Number(right.value);
          const leftDistance = Math.min(...nearestReference.map((height) => Math.abs(leftValue - height)));
          const rightDistance = Math.min(...nearestReference.map((height) => Math.abs(rightValue - height)));
          return leftDistance - rightDistance || leftValue - rightValue;
        })[0] || null;
        if (replacement) replacement.checked = true;
      }

      const selectedHeights = heightInputs
        .filter((input) => input.checked)
        .map((input) => String(input.value));
      const matchesHeight = (product) => (
        allHeights
        || !selectedHeights.length
        || selectedHeights.includes(String(product.height))
      );

      if (this.constructionSelect) {
        Array.from(this.constructionSelect.options).forEach((option) => {
          const baseLabel = option.dataset.baseLabel || option.textContent.trim();
          option.dataset.baseLabel = baseLabel;
          option.disabled = false;
          if (option.value === 'any' || allHeights || !selectedHeights.length) {
            option.textContent = baseLabel;
            return;
          }
          const availableAtSelectedHeight = products.some((product) => (
            matchesHeight(product) && matchesConstruction(product, option.value)
          ));
          const availableAtAnyHeight = products.some((product) => matchesConstruction(product, option.value));
          option.textContent = availableAtSelectedHeight
            ? baseLabel
            : availableAtAnyHeight
              ? `${baseLabel} – andere Höhe nötig`
              : `${baseLabel} – derzeit nicht verfügbar`;
        });
      }

      if (this.heightAvailabilityNote) {
        const showAdjustment = source === 'construction' && !allHeights && removedHeights.length > 0;
        this.heightAvailabilityNote.hidden = !showAdjustment;
        if (showAdjustment) {
          const constructionLabel = selectedConstruction === 'closed' ? 'geschlossene' : 'offene';
          const removedLabel = removedHeights.map((height) => `${height} mm`).join(', ');
          this.heightAvailabilityNote.textContent = replacement
            ? `${removedLabel} ist für ${constructionLabel} Gitterroste derzeit nicht verfügbar. Deshalb wurde die nächstliegende verfügbare Gesamthöhe ${replacement.value} mm ausgewählt.`
            : `${removedLabel} ist für ${constructionLabel} Gitterroste derzeit nicht verfügbar und wurde aus Ihrer Auswahl entfernt.`;
        }
      }
    }

    applyPreferenceMode(control) {
      if (control?.matches('[data-surface-mode]')) {
        const mode = String(control.value || 'auto');
        if (this.surfaceAny) this.surfaceAny.checked = mode === 'auto' || mode === 'mix';
        if (this.surfaceMix) this.surfaceMix.checked = mode === 'mix';
        if (this.surfaceSelect && (mode === 'konkav' || mode === 'besandet')) this.surfaceSelect.value = mode;
        return;
      }
      if (control?.matches('[data-mesh-mode]')) {
        const mode = String(control.value || 'auto');
        if (this.meshAny) this.meshAny.checked = mode !== 'specific';
        if (this.meshMix) this.meshMix.checked = mode === 'mix';
      }
    }

    setWizardStep(step) {
      const safeStep = ['project', 'requirements', 'results'].includes(step) ? step : 'project';
      this.wizardStep = safeStep;
      this.dataset.wizardStep = safeStep;
      this.wizardPanels.forEach((panel) => {
        panel.hidden = panel.dataset.wizardPanel !== safeStep;
      });
      if (this.wizardSummary) this.wizardSummary.hidden = safeStep !== 'results';
      if (this.wizardNext) this.wizardNext.hidden = safeStep !== 'project';
      if (this.wizardSubmit) this.wizardSubmit.hidden = safeStep !== 'requirements';
      if (this.wizardBack) this.wizardBack.hidden = safeStep === 'project';

      this.wizardButtons.forEach((button) => {
        const active = button.dataset.wizardGo === safeStep;
        button.classList.toggle('is-active', active);
        if (active) button.setAttribute('aria-current', 'step');
        else button.removeAttribute('aria-current');
        if (button.dataset.wizardGo === 'results') button.disabled = this.resultsSection.hidden;
      });
    }

    validateProjectStep() {
      const activeRows = this.getPieceRows().filter((row) => !row.hidden);
      const valid = activeRows.length > 0 && activeRows.every((row) =>
        Array.from(row.querySelectorAll('input:not(:disabled)')).every((input) => input.checkValidity())
      );
      const totalQuantity = activeRows.reduce((sum, row) => sum + Math.max(1, Number(row.querySelector('[data-piece-quantity]')?.value || 1)), 0);
      if (valid && totalQuantity <= 100) {
        const criteria = this.readCriteria();
        if (criteria.mode === 'area' && criteria.maxPieceLength && criteria.maxPieces) {
          const minimumPieces = Math.ceil(criteria.length / criteria.maxPieceLength) * Math.ceil(criteria.width / criteria.maxPieceLength);
          if (criteria.maxPieces < minimumPieces) {
            const maxPieces = this.form.elements.namedItem('max_pieces');
            maxPieces?.setCustomValidity(`Mit maximal ${criteria.maxPieceLength} mm je Teilstück benötigen Sie für diese Fläche mindestens ${minimumPieces} Teilstücke.`);
            maxPieces?.reportValidity();
            window.setTimeout(() => maxPieces?.setCustomValidity(''), 0);
            return false;
          }
        }
        return true;
      }
      if (totalQuantity > 100) {
        const quantity = activeRows.find((row) => row.querySelector('[data-piece-quantity]'))?.querySelector('[data-piece-quantity]');
        quantity?.setCustomValidity('Bitte planen Sie höchstens 100 Roste in einem Auftrag.');
        quantity?.reportValidity();
        window.setTimeout(() => quantity?.setCustomValidity(''), 0);
        return false;
      }
      this.form.reportValidity();
      return false;
    }

    updateWizardSummary(criteria) {
      if (!this.wizardSummaryText) return;
      const constructionPrefix = criteria.construction === 'open' ? 'Offen' : 'Offen oder geschlossen';
      const surface = criteria.construction === 'closed'
        ? 'Geschlossen · automatisch besandet'
        : criteria.surfaceAny
          ? criteria.surfaceMix ? `${constructionPrefix} · offene Oberflächen dürfen sich unterscheiden` : `${constructionPrefix} · Oberfläche einheitlich`
          : `${constructionPrefix} · ${this.surfaceLabel(criteria.surface)}`;
      const mesh = criteria.meshAny
        ? criteria.meshMix ? 'Maschenweite flexibel · Unterschiede erlaubt' : 'Maschenweite flexibel · einheitlich'
        : this.meshLabel(criteria.mesh);
      const cuts = Array.isArray(criteria.cuts) && criteria.cuts.length
        ? criteria.cuts
        : [{ length: criteria.length, width: criteria.width, quantity: 1 }];
      const sizeSummary = criteria.mode === 'piece'
        ? cuts.map((cut) => `${cut.quantity}× ${cut.length}×${cut.width} mm`).join(' · ')
        : `${criteria.length} × ${criteria.width} mm`;
      const parts = [
        sizeSummary,
        this.criteriaHeightLabel(criteria),
        mesh,
        criteria.color === 'any' ? 'alle Farben' : this.capitalize(criteria.color),
        surface,
      ];
      if (criteria.maxPieces) parts.push(`max. ${criteria.maxPieces} ${criteria.maxPieces === 1 ? 'Teilstück' : 'Teilstücke'}`);
      if (criteria.maxPieceLength) parts.push(`max. ${criteria.maxPieceLength} mm je Teilstück`);
      if (this.splitDirectionLabel(criteria)) parts.push(this.splitDirectionLabel(criteria));
      this.wizardSummaryText.innerHTML = parts.map((part) => `<span>${this.escapeHtml(part)}</span>`).join('');
    }

    async loadCatalog() {
      const cutPricePromise = this.loadCutServicePrices();
      const fallbackProducts = Array.isArray(this.config.fallbackProducts) ? this.config.fallbackProducts : [];
      let catalog = fallbackProducts;

      try {
        catalog = await this.loadInventoryCatalog();
      } catch (error) {
        console.warn('Restposten-Finder: Bestandsdaten konnten nicht vollständig geladen werden; öffentlicher Katalog wird verwendet.', error);
        try {
          const endpoint = `${String(this.config.collectionUrl || '').replace(/\/$/, '')}/products.json?limit=250`;
          const response = await fetch(endpoint, {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
          });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const payload = await response.json();
          if (Array.isArray(payload.products) && payload.products.length) catalog = this.mergeCatalog(payload.products, fallbackProducts);
        } catch (fallbackError) {
          console.warn('Restposten-Finder: Theme-Daten werden als Rückfall verwendet.', fallbackError);
        }
      }

      await cutPricePromise;
      this.inventoryProducts = catalog.map((product) => this.normalizeProduct(product)).filter(Boolean);
      this.products = this.dedupeProducts(this.inventoryProducts);
      this.populateFilters();
      const restored = this.restoreCriteria();
      this.updateMeshOptions();

      if (this.catalogStatus) {
        const known = this.inventoryProducts.filter((product) => product.inventoryKnown).length;
        this.catalogStatus.textContent = `${this.products.length} Restposten · ${known} mit Bestandsprüfung`;
      }

      if (restored) this.runSearch();
    }

    async loadCutServicePrices() {
      const loadVariantPrice = async (variantId) => {
        const safeId = String(variantId || '').replace(/\D/g, '');
        if (!safeId) return null;
        const response = await fetch(`/variants/${safeId}.js`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const variant = await response.json();
        const price = Number(variant.price);
        return Number.isFinite(price) ? price : null;
      };

      try {
        const [first, additional] = await Promise.all([
          loadVariantPrice(this.config.firstCutVariantId),
          loadVariantPrice(this.config.additionalCutVariantId),
        ]);
        this.cutServicePrices = { first, additional };
      } catch (error) {
        console.warn('Restposten-Finder: Zuschnittspreise konnten nicht geladen werden.', error);
        this.cutServicePrices = { first: null, additional: null };
      }
    }

    async loadInventoryCatalog() {
      const firstUrl = new URL(this.config.inventoryUrl || `${this.config.collectionUrl}?view=sonderposten_finder_data`, window.location.origin);
      firstUrl.searchParams.set('page', '1');
      const fetchPage = async (page) => {
        const url = new URL(firstUrl.toString());
        url.searchParams.set('page', String(page));
        const response = await fetch(`${url.pathname}${url.search}`, {
          credentials: 'same-origin',
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return JSON.parse(await response.text());
      };

      const first = await fetchPage(1);
      if (!Array.isArray(first.products)) throw new Error('Ungültige Bestandsantwort');
      const pages = Math.max(1, Number(first.pages || 1));
      const rest = pages > 1 ? await Promise.all(Array.from({ length: pages - 1 }, (_, index) => fetchPage(index + 2))) : [];
      return [first, ...rest].flatMap((payload) => (Array.isArray(payload.products) ? payload.products : []));
    }

    dedupeProducts(products) {
      const unique = new Map();

      products.forEach((product) => {
        const normalizedTitle = String(product.title || '')
          .toLocaleLowerCase('de-DE')
          .replace(/\s+/g, ' ')
          .trim();
        const key = `${normalizedTitle}|${product.length}x${product.width}`;
        const existing = unique.get(key);

        if (!existing) {
          unique.set(key, product);
          return;
        }

        const shouldReplace =
          (product.inventoryKnown && !existing.inventoryKnown) ||
          (product.inventoryKnown && existing.inventoryKnown && product.inventoryQuantity > existing.inventoryQuantity);
        if (shouldReplace) unique.set(key, product);
      });

      return Array.from(unique.values());
    }

    mergeCatalog(catalog, fallbackProducts) {
      const fallbackByHandle = new Map(fallbackProducts.map((product) => [String(product.handle), product]));

      return catalog.map((product) => {
        const fallback = fallbackByHandle.get(String(product.handle));
        if (!fallback) return product;

        const fallbackVariants = new Map((fallback.variants || []).map((variant) => [String(variant.id), variant]));
        return {
          ...product,
          url: fallback.url || product.url,
          image: fallback.image || product.image,
          variants: (product.variants || []).map((variant) => ({
            ...variant,
            ...(fallbackVariants.get(String(variant.id)) || {}),
          })),
        };
      });
    }

    normalizeProduct(product) {
      const title = String(product.title || '');
      const variants = Array.isArray(product.variants) ? product.variants : [];
      const availableVariants = variants.filter((variant) => variant.available !== false);
      if (!availableVariants.length) return null;

      const variant = availableVariants[0];
      const variantDimensions = variant.dimensions_mm;
      const productDimensions = variants.length === 1 ? product.dimensions_mm : null;
      const storedDimensions = variantDimensions && [variantDimensions.length, variantDimensions.width].every(value => Number(value) > 0)
        ? variantDimensions : productDimensions;
      const dimensions = storedDimensions && [storedDimensions.length, storedDimensions.width].every(value => Number(value) > 0)
        ? { length: Number(storedDimensions.length), width: Number(storedDimensions.width) } : this.parseDimensions(title);
      if (!dimensions) return null;
      const inventoryQuantity = Number(variant.inventory_quantity);
      const inventoryKnown = !!variant.inventory_management && Number.isFinite(inventoryQuantity);
      const family = this.parseFamily(title);
      const tags = this.normalizeTags(product.tags);
      const description = String(product.description || product.body_html || '');
      const surface = this.parseSurface(`${title} ${tags.join(' ')} ${description}`, family);
      const mesh = this.parseMesh({ title, tags, description, family });
      const height = Number(storedDimensions?.height) > 0 ? Number(storedDimensions.height) : this.parseHeight(family, title);
      const grams = Math.max(0, Number(variant.grams || 0));

      return {
        id: product.id,
        title,
        handle: String(product.handle || ''),
        url: product.url || `/products/${String(product.handle || '')}`,
        image: this.getImage(product),
        tags,
        description,
        type: String(product.product_type || product.type || ''),
        length: dimensions.length,
        width: dimensions.width,
        area: dimensions.length * dimensions.width,
        family,
        height,
        color: this.parseColor(`${title} ${tags.join(' ')}`),
        surface,
        mesh,
        variantId: variant.id,
        price: this.toCents(variant.price),
        grams,
        estimatedGrams: grams || this.estimateProductWeightGrams({
          length: dimensions.length,
          width: dimensions.width,
          height,
          family,
        }),
        inventoryKnown,
        inventoryQuantity: inventoryKnown ? Math.max(0, inventoryQuantity) : null,
        inventoryPolicy: String(variant.inventory_policy || 'deny'),
      };
    }

    parseDimensions(value) {
      const matches = Array.from(String(value || '').matchAll(/(\d{2,5})\s*[x×]\s*(\d{2,5})(?:\s*mm\b)?/gi));
      if (!matches.length) return null;
      // Restposten titles can end in the mesh size (e.g. 995×995 ... 38×38).
      // The enclosing plate is the largest pair, never the trailing mesh opening.
      const match = matches.reduce((largest, candidate) => Number(candidate[1]) * Number(candidate[2]) > Number(largest[1]) * Number(largest[2]) ? candidate : largest);
      const length = Number.parseInt(match[1], 10);
      const width = Number.parseInt(match[2], 10);
      if (!length || !width) return null;
      return { length, width };
    }

    parseFamily(value) {
      const source = String(value || '').replace(/[_-]+/g, ' ');
      const match = source.match(/\b(ISO\s*\d+\s*(?:SWL)?|P\s*\d+\s*(?:SANDWICH|SWL)?)\b/i);
      if (!match) return 'Sonstige';
      return match[1]
        .replace(/\s+/g, ' ')
        .replace(/^ISO\s*/i, 'ISO ')
        .replace(/^P\s*/i, 'P')
        .trim()
        .toUpperCase()
        .replace('SANDWICH', 'Sandwich');
    }

    parseHeight(family, title) {
      const familyMatch = String(family || '').match(/\d+/);
      if (familyMatch) {
        const nominalHeight = Number.parseInt(familyMatch[0], 10);
        return nominalHeight + this.laminatedTopHeight(family);
      }
      const heightMatch = String(title || '').match(/\b(\d{2})\s*mm\b/i);
      return heightMatch ? Number.parseInt(heightMatch[1], 10) : null;
    }

    laminatedTopHeight(family) {
      return /^P\d+/i.test(String(family || '').trim()) ? 3 : 0;
    }

    estimateProductWeightGrams({ length, width, height, family }) {
      const safeLength = Math.max(0, Number(length || 0));
      const safeWidth = Math.max(0, Number(width || 0));
      const safeHeight = Math.max(0, Number(height || 0));
      if (!safeLength || !safeWidth || !safeHeight) return 0;

      const topHeight = this.laminatedTopHeight(family);
      const openGridHeight = Math.max(0, safeHeight - topHeight);
      const areaSquareMetres = (safeLength * safeWidth) / 1000000;
      // Konservative Versandsschätzung, falls beim Shopify-Artikel kein Gewicht gepflegt ist.
      const kilogramsPerSquareMetre = Math.max(12, openGridHeight * 0.55) + (topHeight ? 6 : 0);
      return Math.ceil(areaSquareMetres * kilogramsPerSquareMetre * 1000);
    }

    formatProductHeight(height, family, { detailed = false } = {}) {
      if (!height) return '–';
      const topHeight = this.laminatedTopHeight(family);
      if (!topHeight) return `${height} mm hoch`;
      return detailed
        ? `${height} mm Gesamthöhe (inkl. ${topHeight} mm Deckplatte)`
        : `${height} mm gesamt`;
    }

    parseColor(value) {
      const source = String(value || '').toLocaleLowerCase('de-DE');
      const colors = [
        ['grau', /\bgrau(?:e|er|es|en)?\b/],
        ['weiß', /(?:^|[^a-zäöüß])(?:weiß|weiss)(?:e|er|es|en)?(?=$|[^a-zäöüß])/],
        ['gelb', /\bgelb(?:e|er|es|en)?\b/],
        ['grün', /\b(?:grün|gruen)(?:e|er|es|en)?\b/],
        ['schwarz', /\bschwarz(?:e|er|es|en)?\b/],
        ['rot', /\brot(?:e|er|es|en)?\b/],
        ['blau', /\bblau(?:e|er|es|en)?\b/],
        ['beige', /\bbeige\b/],
      ];
      return colors.find((entry) => entry[1].test(source))?.[0] || 'sonstige';
    }

    parseSurface(value, family) {
      const source = String(value || '').toLocaleLowerCase('de-DE');
      if (/^p\s*\d+/i.test(String(family || '')) || /geschlossen(?:e|er|es|en)?\s+(?:gfk-?)?gitterrost|auflaminiert/i.test(source)) {
        return 'geschlossen-besandet';
      }
      if (/\bkonkav(?:e|er|es|en)?\b/i.test(source)) return 'konkav';
      if (/oberfl[aä]che\s*:?\s*(?:sand|besandet)|\bbesandet(?:e|er|es|en)?\b/i.test(source)) return 'besandet';
      return 'unbekannt';
    }

    parseMesh({ title = '', tags = [], description = '', family = '' } = {}) {
      const values = Array.isArray(tags) ? tags.map(String) : [];
      const explicitTag = values.find((tag) => /^\s*\d{1,3}\s*[x×]\s*\d{1,3}(?:\s*mm)?\s*$/i.test(tag));
      const explicitDescription = String(description).match(/Maschenweite\s*:?\s*(\d{1,3}\s*[x×]\s*\d{1,3})/i)?.[1];
      const explicit = explicitTag || explicitDescription;
      if (explicit) return this.normalizeMeshValue(explicit);

      const descriptorSource = `${values.join(' ')} ${description} ${title}`;
      const descriptor = String(description).match(/Maschenweite\s*:?\s*(normal|micro|swl|sandwich|-)/i)?.[1]
        || (/(?:^|\s)micro(?:\s|$)/i.test(descriptorSource) ? 'micro' : '')
        || (/(?:schwerlast|\bswl\b)/i.test(descriptorSource) ? 'swl' : '')
        || (/(?:sandwich)/i.test(descriptorSource) ? 'sandwich' : '');
      return this.defaultMeshForFamily(family, descriptor);
    }

    normalizeMeshValue(value) {
      const match = String(value || '').match(/(\d{1,3})\s*[x×]\s*(\d{1,3})/i);
      if (!match) return 'unbekannt';
      return `${Number(match[1])}x${Number(match[2])}`;
    }

    defaultMeshForFamily(family, descriptor = '') {
      const nominal = Number(String(family || '').match(/\d+/)?.[0] || 0);
      const hint = String(descriptor || '').toLocaleLowerCase('de-DE');
      if (hint === 'micro') {
        if (nominal === 30) return '8x8';
        if (nominal === 14) return '14x13';
      }
      if (hint === 'swl') {
        if (nominal === 35) return '28x28';
        if (nominal === 50 || nominal === 60) return '26x26';
      }
      const defaults = {
        14: '14x13',
        15: '44x44',
        23: '38x38',
        25: '38x38',
        30: '38x38',
        35: '28x28',
        38: '38x38',
        40: '76x76',
        50: '42x42',
        60: '26x26',
      };
      return defaults[nominal] || 'unbekannt';
    }

    meshLabel(value) {
      if (value === 'gemischt') return 'Mehrere Maschenweiten';
      if (value === 'unbekannt' || !value) return 'Maschenweite nicht angegeben';
      const match = String(value).match(/(\d+)x(\d+)/i);
      return match ? `${Number(match[1])} × ${Number(match[2])} mm` : String(value);
    }

    criteriaHeightLabel(criteria) {
      const heights = Array.isArray(criteria?.heights) ? criteria.heights : [];
      if (criteria?.heightAny !== false || !heights.length) return 'alle verfügbaren Höhen';
      return heights.map((height) => `${height} mm`).join(' oder ');
    }

    surfaceLabel(value) {
      const labels = {
        konkav: 'Konkav',
        besandet: 'Besandet',
        'geschlossen-besandet': 'Geschlossen besandet',
        'offen-gemischt': 'Offen: Konkav + Besandet',
        unbekannt: 'Oberfläche offen',
      };
      return labels[value] || this.capitalize(value);
    }

    constructionLabel(value) {
      if (value === 'closed') return 'Geschlossener Gitterrost';
      if (value === 'open') return 'Offener Gitterrost';
      return 'Offen oder geschlossen';
    }

    planConstruction(plan) {
      return plan?.surface === 'geschlossen-besandet' ? 'closed' : 'open';
    }

    planSurfaceExplanation(surface) {
      if (surface === 'offen-gemischt') {
        return 'Sie haben unterschiedliche offene Oberflächen zugelassen. Diese Lösung nutzt konkave und besandete Roste.';
      }
      if (surface === 'geschlossen-besandet') {
        return 'Diese Lösung verwendet ausschließlich geschlossene Roste; deren Oberfläche ist automatisch besandet.';
      }
      if (surface === 'konkav' || surface === 'besandet') {
        return `Die Oberfläche bleibt in dieser Lösung durchgehend ${this.surfaceLabel(surface).toLocaleLowerCase('de-DE')}.`;
      }
      return 'Die Oberflächen der verwendeten Restposten sind im Plan einzeln aufgeführt.';
    }

    normalizeTags(tags) {
      if (Array.isArray(tags)) return tags.map(String);
      if (typeof tags === 'string') return tags.split(',').map((tag) => tag.trim()).filter(Boolean);
      return [];
    }

    getImage(product) {
      if (typeof product.image === 'string') return product.image;
      if (product.image?.src) return product.image.src;
      if (typeof product.featured_image === 'string') return product.featured_image;
      if (product.featured_image?.src) return product.featured_image.src;
      const first = Array.isArray(product.images) ? product.images[0] : null;
      if (typeof first === 'string') return first;
      return first?.src || null;
    }

    toCents(price) {
      if (typeof price === 'string') {
        const normalized = Number.parseFloat(price.replace(',', '.'));
        return Number.isFinite(normalized) ? Math.round(normalized * 100) : 0;
      }
      const numeric = Number(price || 0);
      return Number.isFinite(numeric) ? Math.round(numeric) : 0;
    }

    populateFilters() {
      const heights = Array.from(new Set(this.products.map((product) => product.height).filter(Boolean))).sort((a, b) => a - b);
      const colors = Array.from(new Set(this.products.map((product) => product.color).filter(Boolean))).sort(this.naturalSort);

      this.renderHeightOptions(heights);
      this.updateMeshOptions();
      this.populateSelect('[data-color-select]', colors, (value) => this.capitalize(value));
    }

    renderHeightOptions(heights) {
      if (!this.heightOptions) return;
      this.heightOptions.innerHTML = heights.map((height) => `
        <label>
          <input type="checkbox" name="heights" value="${this.escapeHtml(height)}" data-height-option checked>
          <span>${this.escapeHtml(height)} mm</span>
        </label>`).join('');
      this.syncPreferenceControls();
    }

    selectedHeightValues() {
      return Array.from(this.heightOptions?.querySelectorAll('[data-height-option]:checked') || []).map((input) => String(input.value));
    }

    updateMeshOptions() {
      if (!this.meshSelect || !this.products.length) return;
      const selected = this.meshSelect.value;
      const heights = this.selectedHeightValues();
      const useAllHeights = this.heightAny?.checked !== false || heights.length === 0;
      const color = String(this.form?.elements.namedItem('color')?.value || 'any');
      const construction = String(this.constructionSelect?.value || 'any');
      const surfaceAny = this.surfaceAny?.checked !== false;
      const surface = String(this.surfaceSelect?.value || 'konkav');
      const meshes = Array.from(new Set(this.products
        .filter((product) => !product.inventoryKnown || product.inventoryQuantity > 0)
        .filter((product) => useAllHeights || heights.includes(String(product.height)))
        .filter((product) => color === 'any' || product.color === color)
        .filter((product) => construction === 'any'
          || (construction === 'closed' ? product.surface === 'geschlossen-besandet' : ['konkav', 'besandet'].includes(product.surface)))
        .filter((product) => construction === 'closed' || surfaceAny || product.surface === surface)
        .map((product) => product.mesh)
        .filter((mesh) => mesh && mesh !== 'unbekannt'))).sort(this.naturalSort);
      this.populateSelect('[data-mesh-select]', meshes, (value) => this.meshLabel(value), false);
      if (meshes.includes(selected)) this.meshSelect.value = selected;
      if (!this.meshSelect.options.length) {
        const option = document.createElement('option');
        option.value = 'unbekannt';
        option.textContent = 'Keine Maschenweite verfügbar';
        this.meshSelect.append(option);
      }
    }

    populateSelect(selector, values, labeler, preserveFirst = true) {
      const select = this.querySelector(selector);
      if (!select) return;
      const preservedOptions = preserveFirst ? 1 : 0;
      while (select.options.length > preservedOptions) select.remove(preservedOptions);
      values.forEach((value) => {
        const option = document.createElement('option');
        option.value = String(value);
        option.textContent = labeler(value);
        select.append(option);
      });
    }

    naturalSort(a, b) {
      return String(a).localeCompare(String(b), 'de', { numeric: true, sensitivity: 'base' });
    }

    readCriteria() {
      const formData = new FormData(this.form);
      const cuts = this.getPieceRows()
        .filter((row) => !row.hidden)
        .map((row) => ({
          length: Math.round(Number(row.querySelector('[data-piece-length]')?.value || 0)),
          width: Math.round(Number(row.querySelector('[data-piece-width]')?.value || 0)),
          quantity: Math.max(1, Math.round(Number(row.querySelector('[data-piece-quantity]')?.value || 1))),
        }))
        .filter((cut) => cut.length > 0 && cut.width > 0);
      const firstCut = cuts[0] || { length: 0, width: 0, quantity: 1 };
      const construction = String(this.constructionSelect?.value || 'any');
      const heights = this.selectedHeightValues();
      const heightAny = this.heightAny?.checked !== false || heights.length === 0;
      const surfaceAny = construction !== 'closed' && this.surfaceAny?.checked === true;
      return {
        mode: formData.get('mode') === 'area' ? 'area' : 'piece',
        length: firstCut.length,
        width: firstCut.width,
        cuts,
        totalQuantity: cuts.reduce((sum, cut) => sum + cut.quantity, 0),
        use: String(formData.get('use') || 'any'),
        height: heightAny ? 'any' : heights[0],
        heights: heightAny ? [] : heights,
        heightAny,
        family: 'any',
        color: String(formData.get('color') || 'any'),
        construction,
        surface: construction === 'closed' ? 'geschlossen-besandet' : String(this.surfaceSelect?.value || 'konkav'),
        surfaceAny,
        surfaceMix: surfaceAny && this.surfaceMix?.checked === true,
        mesh: String(this.meshSelect?.value || 'unbekannt'),
        meshAny: formData.get('mesh_any') === '1',
        meshMix: formData.get('mesh_mix') === '1',
        maxPieces: Math.max(0, Math.round(Number(formData.get('max_pieces') || 0))),
        maxPieceLength: Math.max(0, Math.round(Number(formData.get('max_piece_length') || 0))),
        splitDirection: formData.get('mode') === 'area' && ['length', 'width'].includes(formData.get('split_direction')) ? formData.get('split_direction') : 'free',
      };
    }

    persistCriteria(criteria) {
      const keys = ['mode', 'length', 'width', 'use', 'height', 'heights', 'heightAny', 'color', 'construction', 'surface', 'surfaceAny', 'surfaceMix', 'mesh', 'meshAny', 'meshMix', 'maxPieces', 'maxPieceLength', 'splitDirection'];
      const url = new URL(window.location.href);
      keys.forEach((key) => url.searchParams.set(`rf_${key}`, String(criteria[key])));
      if (criteria.mode === 'piece' && Array.isArray(criteria.cuts) && criteria.cuts.length) {
        url.searchParams.set('rf_cuts', criteria.cuts.map((cut) => `${cut.length}x${cut.width}x${cut.quantity}`).join('|'));
      } else {
        url.searchParams.delete('rf_cuts');
      }
      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
      try {
        window.sessionStorage.setItem(this.storageKey, JSON.stringify(criteria));
      } catch (error) {
        console.warn('Restposten-Finder: Zustand konnte nicht gespeichert werden.', error);
      }
    }

    restoreCriteria() {
      const url = new URL(window.location.href);
      const fromUrl = url.searchParams.has('rf_length')
        ? {
            mode: url.searchParams.get('rf_mode'),
            length: url.searchParams.get('rf_length'),
            width: url.searchParams.get('rf_width'),
            use: url.searchParams.get('rf_use'),
            height: url.searchParams.get('rf_height'),
            heights: String(url.searchParams.get('rf_heights') || '').split(',').filter(Boolean),
            heightAny: url.searchParams.has('rf_heightAny') ? url.searchParams.get('rf_heightAny') === 'true' : null,
            color: url.searchParams.get('rf_color'),
            construction: url.searchParams.get('rf_construction') || 'any',
            surface: url.searchParams.get('rf_surface'),
            surfaceAny: url.searchParams.has('rf_surfaceAny')
              ? url.searchParams.get('rf_surfaceAny') === 'true'
              : url.searchParams.get('rf_surface') === 'any',
            surfaceMix: url.searchParams.get('rf_surfaceMix') === 'true',
            mesh: url.searchParams.get('rf_mesh'),
            meshAny: url.searchParams.has('rf_meshAny') ? url.searchParams.get('rf_meshAny') === 'true' : true,
            meshMix: url.searchParams.get('rf_meshMix') === 'true',
            maxPieces: url.searchParams.get('rf_maxPieces'),
            maxPieceLength: url.searchParams.get('rf_maxPieceLength'),
            splitDirection: url.searchParams.get('rf_splitDirection') || 'free',
            cuts: String(url.searchParams.get('rf_cuts') || '')
              .split('|')
              .map((entry) => entry.split('x').map(Number))
              .filter(([length, width]) => length > 0 && width > 0)
              .map(([length, width, quantity]) => ({ length, width, quantity: Math.max(1, quantity || 1) })),
          }
        : null;
      let saved = fromUrl;

      if (!saved) {
        try {
          saved = JSON.parse(window.sessionStorage.getItem(this.storageKey) || 'null');
        } catch (error) {
          saved = null;
        }
      }
      if (!saved || !Number(saved.length) || !Number(saved.width)) return false;
      if (saved.surfaceAny == null) saved.surfaceAny = saved.surface === 'any';
      if (saved.surfaceMix == null) saved.surfaceMix = false;
      if (saved.meshAny == null) saved.meshAny = true;
      if (saved.meshMix == null) saved.meshMix = false;
      if (!Array.isArray(saved.heights)) saved.heights = saved.height && saved.height !== 'any' ? [String(saved.height)] : [];
      if (saved.heightAny == null) saved.heightAny = saved.heights.length === 0;
      if (!saved.construction) saved.construction = 'any';
      if (saved.surface === 'geschlossen-besandet') {
        saved.construction = 'closed';
        saved.surface = 'konkav';
        saved.surfaceAny = false;
        saved.surfaceMix = false;
      }

      this.setMode(saved.mode);
      const restoredCuts = Array.isArray(saved.cuts) && saved.cuts.length
        ? saved.cuts
        : [{ length: Number(saved.length), width: Number(saved.width), quantity: 1 }];
      this.resetPieceRows();
      const firstRow = this.getPieceRows()[0];
      restoredCuts.forEach((cut, index) => {
        const row = index === 0 ? firstRow : this.addPieceRow(cut);
        if (!row) return;
        const length = row.querySelector('[data-piece-length]');
        const width = row.querySelector('[data-piece-width]');
        const quantity = row.querySelector('[data-piece-quantity]');
        if (length) length.value = String(cut.length || '');
        if (width) width.value = String(cut.width || '');
        if (quantity) quantity.value = String(Math.max(1, Number(cut.quantity || 1)));
      });
      if (this.heightAny) this.heightAny.checked = saved.heightAny === true || saved.heightAny === 'true';
      this.heightOptions?.querySelectorAll('[data-height-option]').forEach((input) => {
        input.checked = saved.heights.includes(String(input.value));
      });
      ['length', 'width', 'use', 'color', 'construction', 'surface', 'surfaceAny', 'surfaceMix', 'mesh', 'meshAny', 'meshMix', 'maxPieces', 'maxPieceLength'].forEach((name) => {
        if (['surfaceAny', 'surfaceMix', 'meshAny', 'meshMix'].includes(name)) {
          const checkbox = {
            surfaceAny: this.surfaceAny,
            surfaceMix: this.surfaceMix,
            meshAny: this.meshAny,
            meshMix: this.meshMix,
          }[name];
          if (checkbox) checkbox.checked = saved[name] === true || saved[name] === 'true';
          return;
        }
        if (name === 'length' || name === 'width') return;
        const controlName = name === 'maxPieces' ? 'max_pieces' : name === 'maxPieceLength' ? 'max_piece_length' : name;
        const control = this.form.elements.namedItem(controlName);
        if (!control || saved[name] == null) return;
        if (control.tagName === 'SELECT' && !Array.from(control.options).some((option) => option.value === String(saved[name]))) return;
        control.value = Number(saved[name]) === 0 && ['maxPieces', 'maxPieceLength'].includes(name) ? '' : String(saved[name]);
      });
      const directionControl = this.form.elements.namedItem('split_direction');
      if (directionControl) directionControl.value = ['length', 'width'].includes(saved.splitDirection) ? saved.splitDirection : 'free';
      this.syncPreferenceControls();
      this.syncPieceRows();
      this.updateDrawingLabels();
      return true;
    }

    clearStoredCriteria() {
      const url = new URL(window.location.href);
      ['mode', 'length', 'width', 'cuts', 'use', 'height', 'heights', 'heightAny', 'family', 'color', 'construction', 'surface', 'surfaceAny', 'surfaceMix', 'mesh', 'meshAny', 'meshMix', 'maxPieces', 'maxPieceLength', 'splitDirection'].forEach((key) => url.searchParams.delete(`rf_${key}`));
      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
      try {
        window.sessionStorage.removeItem(this.storageKey);
      } catch (error) {
        // Storage can be disabled; the URL state has already been cleared.
      }
    }

    setLoading(isLoading) {
      this.toggleAttribute('aria-busy', isLoading);
      this.classList.toggle('is-loading', isLoading);
      if (this.loadingOverlay) this.loadingOverlay.hidden = !isLoading;
      if (this.wizardSubmit) {
        this.wizardSubmit.disabled = isLoading;
        this.wizardSubmit.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      }
    }

    runSearch(options = {}) {
      if (this.searchPending) return;
      this.searchPending = true;
      this.setLoading(true);

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          window.setTimeout(() => {
            try {
              this.search(options);
            } catch (error) {
              console.error('Restposten-Finder: Die Lösung konnte nicht berechnet werden.', error);
              if (this.resultsSection) this.resultsSection.hidden = false;
              if (this.emptyState) {
                this.emptyState.hidden = false;
                this.emptyState.textContent = 'Die Lösung konnte gerade nicht berechnet werden. Bitte versuchen Sie es erneut oder ändern Sie eine Angabe.';
              }
            } finally {
              this.searchPending = false;
              this.setLoading(false);
            }
          }, 0);
        });
      });
    }

    search({ moveToResults = false } = {}) {
      const criteria = this.readCriteria();
      this.lastCriteria = criteria;
      this.persistCriteria(criteria);

      const multiPieceOrder = this.isMultiPieceCriteria(criteria);
      const matches = multiPieceOrder ? [] : this.findMatches(criteria);
      const hasAvailableSinglePiece = criteria.mode === 'piece' && matches.some((match) =>
        match.fit.stockSufficient && (match.fit.cuttable || match.fit.nearFit)
      );
      const plan = criteria.mode === 'area'
        ? this.findAreaPlan(criteria, matches)
        : multiPieceOrder
          ? this.buildMultiPiecePlan(criteria)
          : hasAvailableSinglePiece
            ? null
            : this.findSplitPiecePlan(criteria);
      let alternatives = [];
      if (criteria.mode === 'area' && !plan) {
        const splitFallback = criteria.maxPieces === 1 ? this.buildSplitAreaSuggestion(criteria) : null;
        const layoutAlternatives = this.buildAreaLayoutFallbackSuggestions(criteria);
        const propertyAlternatives = this.buildAlternativeSuggestions(criteria);
        alternatives = [splitFallback, ...layoutAlternatives, ...propertyAlternatives]
          .filter(Boolean)
          .filter((alternative, index, all) => {
            const signature = alternative.signature || this.planSignature(alternative.plan);
            return all.findIndex((item) => (item.signature || this.planSignature(item.plan)) === signature) === index;
          })
          .slice(0, 3);
      }
      if (multiPieceOrder && !plan) alternatives = this.buildMultiPieceAlternatives(criteria);

      this.renderResults(matches, criteria, plan, alternatives);
      if (plan) this.updatePlanVisual(plan);
      else if (alternatives[0]) this.updatePlanVisual(alternatives[0].plan);
      else if (matches[0]) this.updateVisual(matches[0], criteria);
      this.updateWizardSummary(criteria);
      this.setWizardStep('results');

      if (moveToResults) {
        this.resultsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    findMatches(criteria) {
      const matches = this.products
        .filter((product) => this.matchesFilters(product, criteria))
        .map((product) => {
          const fit = criteria.mode === 'area' ? this.analyzeArea(product, criteria) : this.analyzePiece(product, criteria);
          if (!fit) return null;
          const match = { product, fit };
          if (criteria.mode === 'area') match.previewPlan = this.buildPlanFromSingleMatch(match, criteria);
          return match;
        })
        .filter(Boolean)
        .filter((match) => criteria.mode !== 'area' || (match.fit.cuttable && match.previewPlan));
      if (!matches.length) return matches;

      const minimumWaste = Math.min(...matches.map((match) => criteria.mode === 'area' ? this.matchAreaMetrics(match).wastePercent : match.fit.wastePercent));
      const minimumQuantity = Math.min(...matches.map((match) => criteria.mode === 'area' ? this.matchAreaMetrics(match).rawPlateCount : Number(match.fit.quantity || 1)));
      const minimumPrice = Math.min(...matches.map((match) => this.matchTotalPrice(match, criteria)));
      const score = (match) => {
        const areaMetrics = criteria.mode === 'area' ? this.matchAreaMetrics(match) : null;
        const wasteDifference = (areaMetrics?.wastePercent ?? match.fit.wastePercent) - minimumWaste;
        const pieceDifference = (areaMetrics?.rawPlateCount ?? Number(match.fit.quantity || 1)) - minimumQuantity;
        const priceDifference = (this.matchTotalPrice(match, criteria) - minimumPrice) / Math.max(100, minimumPrice);
        return wasteDifference + pieceDifference * 4.5 + priceDifference * 18 + ((areaMetrics?.stockSufficient ?? match.fit.stockSufficient) ? 0 : 45);
      };
      return matches.sort((a, b) =>
        score(a) - score(b) ||
        (criteria.mode === 'area' ? this.matchAreaMetrics(a).rawPlateCount - this.matchAreaMetrics(b).rawPlateCount : Number(a.fit.quantity || 1) - Number(b.fit.quantity || 1)) ||
        (criteria.mode === 'area' ? this.matchAreaMetrics(a).wastePercent - this.matchAreaMetrics(b).wastePercent : a.fit.wastePercent - b.fit.wastePercent) ||
        this.matchTotalPrice(a, criteria) - this.matchTotalPrice(b, criteria)
      );
    }

    findSplitPiecePlan(criteria) {
      const splitCriteria = { ...criteria, splitDirection: this.splitDirection(criteria), mode: 'area', maxPieces: 0 };
      const plan = this.buildMixedAreaPlan(splitCriteria);
      return plan?.placements?.length > 1 ? plan : null;
    }

    findAreaPlan(criteria, matches = this.findMatches(criteria)) {
      let plan = this.buildMixedAreaPlan(criteria);
      if (!plan) {
        const singleProductFallback = matches.find(
          (match) =>
            match.fit.cuttable &&
            this.matchAreaMetrics(match).stockSufficient &&
            (!criteria.maxPieces || match.fit.quantity <= criteria.maxPieces)
        );
        if (singleProductFallback) plan = this.buildPlanFromSingleMatch(singleProductFallback, criteria);
      }
      return plan;
    }

    planSignature(plan) {
      return (plan?.products || [])
        .map((group) => String(group.product.variantId))
        .sort()
        .join('|');
    }

    findMinimumSplitAreaPlan(criteria) {
      const unrestrictedCriteria = { ...criteria, maxPieces: 0 };
      const unrestrictedMatches = this.findMatches(unrestrictedCriteria);
      const unrestrictedPlan = this.findAreaPlan(unrestrictedCriteria, unrestrictedMatches);
      if (!unrestrictedPlan || unrestrictedPlan.placements.length <= 1) return null;

      const upperLimit = Math.max(2, unrestrictedPlan.placements.length);
      for (let maxPieces = 2; maxPieces <= upperLimit; maxPieces += 1) {
        const splitCriteria = {
          ...criteria,
          maxPieces,
          fallbackFromMaxPieces: Number(criteria.maxPieces || 1),
        };
        const matches = this.findMatches(splitCriteria);
        const plan = this.findAreaPlan(splitCriteria, matches);
        if (plan && plan.placements.length > 1 && plan.placements.length <= maxPieces) {
          return { plan, criteria: splitCriteria, matches };
        }
      }

      return {
        plan: unrestrictedPlan,
        criteria: {
          ...unrestrictedCriteria,
          maxPieces: unrestrictedPlan.placements.length,
          fallbackFromMaxPieces: Number(criteria.maxPieces || 1),
        },
        matches: unrestrictedMatches,
      };
    }

    buildSplitAreaSuggestion(criteria) {
      const result = this.findMinimumSplitAreaPlan(criteria);
      if (!result) return null;
      const count = result.plan.placements.length;
      return {
        ...result,
        requestedCriteria: criteria,
        kind: 'split',
        relaxesMaxPieces: true,
        changes: [`Teilung: 1 Teilstück → ${count} Teilstücke`],
        label: `Statt einer Platte: passende Lösung mit ${count} Teilstücken`,
        explanation: `Eine einteilige Belegung ist aktuell nicht verfügbar. Mit ${count} Teilstücken kann Ihre Fläche vollständig belegt werden.`,
      };
    }

    intervalCovered(segments, total) {
      const sorted = segments
        .map(([start, end]) => [Math.max(0, Number(start)), Math.min(Number(total), Number(end))])
        .filter(([start, end]) => end > start)
        .sort((a, b) => a[0] - b[0]);
      if (!sorted.length || sorted[0][0] > 0) return false;
      let coveredUntil = sorted[0][1];
      for (let index = 1; index < sorted.length && coveredUntil < total; index += 1) {
        const [start, end] = sorted[index];
        if (start > coveredUntil) return false;
        coveredUntil = Math.max(coveredUntil, end);
      }
      return coveredUntil >= total;
    }

    narrowAreaPieceSizes(plan, minimumPiece = this.areaMinimumPiece()) {
      return Array.from(new Set((plan?.placements || [])
        .flatMap((placement) => [Number(placement.width), Number(placement.height)])
        .filter((size) => size > 0 && size < minimumPiece)))
        .sort((a, b) => a - b);
    }

    areaReductionCandidates(plan, minimumPiece = this.areaMinimumPiece()) {
      if (!plan?.placements?.length) return [];
      const candidates = [];
      const targetLength = Number(plan.targetLength);
      const targetWidth = Number(plan.targetWidth);
      const tolerance = 0.01;

      const rightStarts = Array.from(new Set(plan.placements
        .filter((placement) => Math.abs(placement.x + placement.width - targetLength) <= tolerance && placement.width < minimumPiece)
        .map((placement) => Number(placement.x))));
      rightStarts.forEach((start) => {
        const reduction = targetLength - start;
        const hasCrossingPiece = plan.placements.some((placement) => placement.x < start && placement.x + placement.width > start + tolerance);
        const edgePieces = plan.placements.filter((placement) => placement.x >= start - tolerance);
        if (
          reduction > 0 &&
          reduction < minimumPiece &&
          !hasCrossingPiece &&
          this.intervalCovered(edgePieces.map((placement) => [placement.y, placement.y + placement.height]), targetWidth)
        ) {
          candidates.push({ length: start, width: targetWidth, lengthReduction: reduction, widthReduction: 0, reduction });
        }
      });

      const bottomStarts = Array.from(new Set(plan.placements
        .filter((placement) => Math.abs(placement.y + placement.height - targetWidth) <= tolerance && placement.height < minimumPiece)
        .map((placement) => Number(placement.y))));
      bottomStarts.forEach((start) => {
        const reduction = targetWidth - start;
        const hasCrossingPiece = plan.placements.some((placement) => placement.y < start && placement.y + placement.height > start + tolerance);
        const edgePieces = plan.placements.filter((placement) => placement.y >= start - tolerance);
        if (
          reduction > 0 &&
          reduction < minimumPiece &&
          !hasCrossingPiece &&
          this.intervalCovered(edgePieces.map((placement) => [placement.x, placement.x + placement.width]), targetLength)
        ) {
          candidates.push({ length: targetLength, width: start, lengthReduction: 0, widthReduction: reduction, reduction });
        }
      });

      const lengthReductions = candidates.filter((candidate) => candidate.lengthReduction > 0 && candidate.widthReduction === 0);
      const widthReductions = candidates.filter((candidate) => candidate.widthReduction > 0 && candidate.lengthReduction === 0);
      lengthReductions.forEach((lengthCandidate) => {
        widthReductions.forEach((widthCandidate) => {
          candidates.push({
            length: lengthCandidate.length,
            width: widthCandidate.width,
            lengthReduction: lengthCandidate.lengthReduction,
            widthReduction: widthCandidate.widthReduction,
            reduction: lengthCandidate.lengthReduction + widthCandidate.widthReduction,
          });
        });
      });

      return candidates.filter((candidate, index, all) =>
        candidate.length >= minimumPiece &&
        candidate.width >= minimumPiece &&
        all.findIndex((item) => item.length === candidate.length && item.width === candidate.width) === index
      );
    }

    areaReductionText(reduction, separator = ' · ') {
      const parts = [];
      if (Number(reduction?.lengthReduction || 0) > 0) parts.push(`Länge: ${Number(reduction.lengthReduction)} mm kleiner`);
      if (Number(reduction?.widthReduction || 0) > 0) parts.push(`Breite: ${Number(reduction.widthReduction)} mm kleiner`);
      return parts.join(separator);
    }

    areaReductionSentence(reduction) {
      const parts = [];
      if (Number(reduction?.lengthReduction || 0) > 0) parts.push(`in der Länge ${Number(reduction.lengthReduction)} mm`);
      if (Number(reduction?.widthReduction || 0) > 0) parts.push(`in der Breite ${Number(reduction.widthReduction)} mm`);
      return parts.join(' und ');
    }

    buildAreaLayoutFallbackSuggestions(criteria) {
      // A direction choice also fixes the full cross-span; do not shrink or split it in a fallback.
      if (this.splitDirection(criteria) !== 'free') return [];
      const minimumPiece = this.areaMinimumPiece();
      const technicalMinimum = Number(this.config.minCut || 50);
      if (technicalMinimum >= minimumPiece) return [];

      const narrowPlan = this.buildMixedAreaPlan(criteria, { minPiece: technicalMinimum });
      const narrowSizes = this.narrowAreaPieceSizes(narrowPlan, minimumPiece);
      if (!narrowPlan || !narrowSizes.length) return [];

      const reducedOptions = this.areaReductionCandidates(narrowPlan, minimumPiece)
        .map((reduction) => {
          const reducedCriteria = {
            ...criteria,
            length: reduction.length,
            width: reduction.width,
            areaFallbackKind: 'reduced-area',
            requestedLength: criteria.length,
            requestedWidth: criteria.width,
            areaReductionLengthMm: reduction.lengthReduction,
            areaReductionWidthMm: reduction.widthReduction,
          };
          const plan = this.buildMixedAreaPlan(reducedCriteria);
          if (!plan || this.narrowAreaPieceSizes(plan, minimumPiece).length) return null;
          return { plan, criteria: reducedCriteria, reduction };
        })
        .filter(Boolean)
        .sort((a, b) =>
          a.reduction.reduction - b.reduction.reduction ||
          a.plan.placements.length - b.plan.placements.length ||
          this.planTotalPriceForSort(a.plan) - this.planTotalPriceForSort(b.plan) ||
          a.plan.wastePercent - b.plan.wastePercent
        );

      const suggestions = [];
      const reduced = reducedOptions[0];
      if (reduced) {
        const { plan, criteria: reducedCriteria, reduction } = reduced;
        suggestions.push({
          plan,
          criteria: reducedCriteria,
          requestedCriteria: criteria,
          kind: 'area-reduced',
          signature: `area-reduced:${plan.targetLength}x${plan.targetWidth}:${this.planSignature(plan)}`,
          changes: [this.areaReductionText(reduction)],
          label: `Ohne schmalen Randstreifen: ${plan.targetLength} × ${plan.targetWidth} mm`,
          explanation: `Diese Lösung ist ${this.areaReductionSentence(reduction)} kleiner und vermeidet Teilstücke unter ${minimumPiece} mm.`,
        });
      }

      const narrowLabel = narrowSizes.map((size) => `${size} mm`).join(' / ');
      const narrowCriteria = {
        ...criteria,
        areaFallbackKind: 'narrow-strip',
        requestedLength: criteria.length,
        requestedWidth: criteria.width,
        narrowPieceSizes: narrowSizes,
      };
      suggestions.push({
        plan: narrowPlan,
        criteria: narrowCriteria,
        requestedCriteria: criteria,
        kind: 'area-narrow-strip',
        signature: `area-narrow-strip:${narrowPlan.targetLength}x${narrowPlan.targetWidth}:${this.planSignature(narrowPlan)}:${narrowSizes.join('-')}`,
        changes: [`schmaler Randstreifen: ${narrowLabel}`],
        label: `Wunschmaß vollständig – mit ${narrowLabel} Randstreifen`,
        explanation: `Das Wunschmaß bleibt vollständig erhalten. Dafür enthält der Verlegeplan einen klar markierten Randstreifen unter ${minimumPiece} mm.`,
      });

      return suggestions;
    }

    buildRelaxedCriteriaCandidates(criteria) {
      const surfaces = ['konkav', 'besandet'];
      const propertyCandidates = [];

      if (criteria.construction !== 'closed' && !criteria.surfaceAny) {
        surfaces.filter((surface) => surface !== criteria.surface).forEach((surface) => {
          propertyCandidates.push({ criteria: { ...criteria, surface, surfaceAny: false, surfaceMix: false }, kind: 'surface' });
        });
      } else if (criteria.construction !== 'closed' && !criteria.surfaceMix) {
        propertyCandidates.push({ criteria: { ...criteria, surfaceMix: true }, kind: 'surface-mix' });
      }

      if (criteria.construction === 'open') {
        propertyCandidates.push({
          criteria: { ...criteria, construction: 'closed', surface: 'geschlossen-besandet', surfaceAny: false, surfaceMix: false },
          kind: 'construction',
        });
      } else if (criteria.construction === 'closed') {
        propertyCandidates.push({
          criteria: { ...criteria, construction: 'open', surface: 'konkav', surfaceAny: true, surfaceMix: false },
          kind: 'construction',
        });
      }

      if (!criteria.meshAny) {
        propertyCandidates.push({ criteria: { ...criteria, meshAny: true, meshMix: false }, kind: 'mesh' });
      } else if (!criteria.meshMix) {
        propertyCandidates.push({ criteria: { ...criteria, meshMix: true }, kind: 'mesh-mix' });
      }

      if (criteria.construction !== 'closed' && criteria.surfaceAny && !criteria.surfaceMix && criteria.meshAny && !criteria.meshMix) {
        propertyCandidates.push({ criteria: { ...criteria, surfaceMix: true, meshMix: true }, kind: 'surface-mesh-mix' });
      }

      const candidates = [...propertyCandidates];
      if (criteria.heightAny === false) {
        candidates.push({ criteria: { ...criteria, height: 'any', heights: [], heightAny: true }, kind: 'height' });
        propertyCandidates.forEach((candidate) => {
          candidates.push({
            criteria: { ...candidate.criteria, height: 'any', heights: [], heightAny: true },
            kind: `${candidate.kind}-height`,
          });
        });
      }
      return candidates;
    }

    buildAlternativeSuggestions(criteria) {
      const suggestions = [];
      const seen = new Set();
      const candidates = this.buildRelaxedCriteriaCandidates(criteria);

      candidates.some((candidate) => {
        const matches = this.findMatches(candidate.criteria);
        let planCriteria = candidate.criteria;
        let planMatches = matches;
        let plan = this.findAreaPlan(planCriteria, planMatches);
        let relaxesMaxPieces = false;
        if (!plan && criteria.maxPieces === 1) {
          const splitResult = this.findMinimumSplitAreaPlan(candidate.criteria);
          if (splitResult) {
            plan = splitResult.plan;
            planCriteria = splitResult.criteria;
            planMatches = splitResult.matches;
            relaxesMaxPieces = true;
          }
        }
        if (!plan) return false;
        const requestedHeights = Array.isArray(criteria.heights) ? criteria.heights.map(String) : [];
        if (candidate.kind.includes('height') && requestedHeights.includes(String(plan.height))) return false;
        const signature = plan.products.map((group) => group.product.variantId).sort().join('|');
        if (seen.has(signature)) return false;
        seen.add(signature);

        const surfaceChanged = !criteria.surfaceAny && plan.surface !== criteria.surface;
        const constructionChanged = criteria.construction !== 'any' && this.planConstruction(plan) !== criteria.construction;
        const surfaceMixed = !criteria.surfaceMix && plan.surface === 'offen-gemischt';
        const meshChanged = !criteria.meshAny && plan.mesh !== criteria.mesh;
        const meshMixed = !criteria.meshMix && plan.mesh === 'gemischt';
        const heightChanged = criteria.heightAny === false && !requestedHeights.includes(String(plan.height));
        const changes = [];
        if (constructionChanged) changes.push(this.constructionLabel(this.planConstruction(plan)));
        if (surfaceChanged) changes.push(this.surfaceLabel(plan.surface));
        if (surfaceMixed) changes.push('unterschiedlichen offenen Oberflächen');
        if (meshChanged) changes.push(this.meshLabel(plan.mesh));
        if (meshMixed) changes.push('unterschiedlichen Maschenweiten');
        if (heightChanged) changes.push(`${this.formatProductHeight(plan.height, plan.family)} statt ${this.criteriaHeightLabel(criteria)}`);
        if (relaxesMaxPieces) changes.push(`${plan.placements.length} Teilstücke`);
        const label = `Passende Alternative mit ${changes.join(' · ') || `${this.surfaceLabel(plan.surface)} · ${this.meshLabel(plan.mesh)}`}`;
        suggestions.push({
          plan,
          criteria: planCriteria,
          requestedCriteria: criteria,
          matches: planMatches,
          changes,
          label,
          kind: relaxesMaxPieces ? `${candidate.kind}-split` : candidate.kind,
          relaxesMaxPieces,
          explanation: `Diese Lösung passt zu Ihren Maßen und ist mit ${this.surfaceLabel(plan.surface)}, ${this.meshLabel(plan.mesh)}${heightChanged ? ` sowie ${this.formatProductHeight(plan.height, plan.family, { detailed: true })}` : ''} verfügbar.`,
        });
        return suggestions.length >= 3;
      });
      return suggestions;
    }

    matchesFilters(product, criteria) {
      const allowedHeights = Array.isArray(criteria.heights) ? criteria.heights.map(String) : [];
      if (criteria.heightAny === false && allowedHeights.length && !allowedHeights.includes(String(product.height))) return false;
      if (criteria.family && criteria.family !== 'any' && product.family !== criteria.family) return false;
      if (criteria.color !== 'any' && product.color !== criteria.color) return false;
      if (!criteria.meshAny && product.mesh !== criteria.mesh) return false;
      if (criteria.construction === 'open' && !['konkav', 'besandet'].includes(product.surface)) return false;
      if (criteria.construction === 'closed' && product.surface !== 'geschlossen-besandet') return false;
      if (criteria.construction === 'closed') return true;
      if (criteria.surfaceAny) {
        if (!['konkav', 'besandet', 'geschlossen-besandet'].includes(product.surface)) return false;
      } else if (product.surface !== criteria.surface) {
        return false;
      }
      return true;
    }

    analyzePiece(product, criteria) {
      const tolerance = Math.max(0, Number(this.config.nearFitTolerance ?? 5));
      const minimumCut = Number(this.config.minCut || 50);
      const orientations = this.orientations(product);
      const candidates = orientations
        .filter((orientation) =>
          criteria.length <= orientation.length + tolerance && criteria.width <= orientation.width + tolerance
        )
        .map((orientation) => {
          const deltaLength = orientation.length - criteria.length;
          const deltaWidth = orientation.width - criteria.width;
          const sameLength = deltaLength === 0;
          const sameWidth = deltaWidth === 0;
          const canCutExact = deltaLength >= 0 && deltaWidth >= 0;
          const cuts = (sameLength ? 0 : 1) + (sameWidth ? 0 : 1);
          const nearFit = (deltaLength !== 0 || deltaWidth !== 0) && Math.abs(deltaLength) <= tolerance && Math.abs(deltaWidth) <= tolerance;
          const wasteArea = canCutExact ? product.area - criteria.length * criteria.width : 0;
          const wastePercent = canCutExact && product.area ? (wasteArea / product.area) * 100 : 0;
          const dimensionDifference = Math.abs(deltaLength) + Math.abs(deltaWidth);
          return {
            ...orientation,
            cuts: canCutExact ? cuts : 0,
            exactCuts: canCutExact ? cuts : 0,
            canCutExact,
            nearFit,
            deltaLength,
            deltaWidth,
            tolerance,
            quantity: 1,
            wasteArea,
            wastePercent,
            score: nearFit ? dimensionDifference * 0.35 + (orientation.rotated ? 0.25 : 0) : wastePercent + cuts * 4 + (orientation.rotated ? 0.25 : 0),
            cutRows: [{ length: canCutExact ? criteria.length : orientation.length, width: canCutExact ? criteria.width : orientation.width, quantity: 1 }],
            cuttable: canCutExact && criteria.length >= minimumCut && criteria.width >= minimumCut,
            stockSufficient: !product.inventoryKnown || product.inventoryQuantity >= 1,
          };
        });

      return candidates.sort((a, b) => a.score - b.score)[0] || null;
    }

    isMultiPieceCriteria(criteria) {
      return criteria?.mode === 'piece' && (Number(criteria.totalQuantity || 0) > 1 || Number(criteria.cuts?.length || 0) > 1);
    }

    expandRequestedPieces(criteria) {
      return (criteria.cuts || []).flatMap((cut, requestIndex) =>
        Array.from({ length: Math.max(1, Number(cut.quantity || 1)) }, (_, quantityIndex) => ({
          id: `M${requestIndex + 1}-${quantityIndex + 1}`,
          requestIndex,
          quantityIndex,
          length: Number(cut.length),
          width: Number(cut.width),
          baseLength: Number(cut.length),
          baseWidth: Number(cut.width),
        }))
      ).slice(0, 100);
    }

    buildCompatibilityGroups(products, criteria) {
      const modes = [{ surfaceMix: false, meshMix: false }];
      if (criteria.surfaceAny && criteria.surfaceMix) modes.push({ surfaceMix: true, meshMix: false });
      if (criteria.meshAny && criteria.meshMix) modes.push({ surfaceMix: false, meshMix: true });
      if (criteria.surfaceAny && criteria.surfaceMix && criteria.meshAny && criteria.meshMix) {
        modes.push({ surfaceMix: true, meshMix: true });
      }

      const groups = new Map();
      modes.forEach((mode) => {
        products.forEach((product) => {
          const openSurface = ['konkav', 'besandet'].includes(product.surface);
          const surfaceKey = mode.surfaceMix && openSurface ? 'offen-gemischt' : product.surface;
          const knownMesh = product.mesh && product.mesh !== 'unbekannt';
          const meshKey = mode.meshMix && knownMesh
            ? 'gemischt'
            : knownMesh ? product.mesh : `unbekannt:${product.family}`;
          const key = `${mode.surfaceMix ? 1 : 0}|${mode.meshMix ? 1 : 0}|${product.height || 'ohne'}|${product.color}|${surfaceKey}|${meshKey}`;
          if (!groups.has(key)) groups.set(key, []);
          groups.get(key).push(product);
        });
      });

      const unique = new Map();
      groups.forEach((group) => {
        const signature = group.map((product) => String(product.variantId)).sort().join('|');
        if (!unique.has(signature)) unique.set(signature, group);
      });
      return Array.from(unique.values());
    }

    buildMultiPiecePlan(criteria) {
      const pieces = this.expandRequestedPieces(criteria);
      if (!pieces.length) return null;
      const compatibleProducts = this.inventoryProducts
        .filter((product) => this.matchesFilters(product, criteria))
        .filter((product) => !product.inventoryKnown || product.inventoryQuantity > 0);
      const groups = this.buildCompatibilityGroups(compatibleProducts, criteria);

      const plans = [];
      groups.forEach((products) => {
        const plan = this.planMultiPieceCompatibleGroup(products, pieces, criteria);
        if (plan) plans.push(plan);
      });
      return this.rankPlans(plans)[0] || null;
    }

    planMultiPieceCompatibleGroup(products, pieces, criteria) {
      const units = [];
      products.forEach((product) => {
        const available = product.inventoryKnown ? product.inventoryQuantity : 1;
        const limit = Math.min(Math.max(0, available), pieces.length, 100);
        for (let index = 0; index < limit; index += 1) {
          units.push({ id: `${product.variantId}:multi:${index}`, product });
        }
      });
      if (!units.length) return null;
      if (!pieces.every((piece) => units.some((unit) => this.pieceFitsProduct(piece, unit.product)))) return null;

      const strategies = [
        { order: 'area', unit: 'waste', split: 'wide' },
        { order: 'max-side', unit: 'waste', split: 'tall' },
        { order: 'width', unit: 'cheap', split: 'wide' },
        { order: 'length', unit: 'cheap', split: 'tall' },
        { order: 'area', unit: 'large', split: 'tall' },
      ];
      const plans = strategies
        .map((strategy) => this.packMultiPieceUnits(units, pieces, criteria, strategy))
        .filter(Boolean);
      return this.rankPlans(plans)[0] || null;
    }

    pieceFitsProduct(piece, product) {
      return (piece.length <= product.length && piece.width <= product.width)
        || (piece.width <= product.length && piece.length <= product.width);
    }

    packMultiPieceUnits(sourceUnits, sourcePieces, criteria, strategy) {
      const pieceArea = (piece) =>
        Number(piece.baseLength || piece.length || 0) * Number(piece.baseWidth || piece.width || 0);
      const pieces = [...sourcePieces].sort((a, b) => {
        if (strategy.order === 'width') return b.width - a.width || b.length - a.length;
        if (strategy.order === 'length') return b.length - a.length || b.width - a.width;
        if (strategy.order === 'max-side') return Math.max(b.length, b.width) - Math.max(a.length, a.width) || pieceArea(b) - pieceArea(a);
        return pieceArea(b) - pieceArea(a) || Math.max(b.length, b.width) - Math.max(a.length, a.width);
      });
      const unused = new Map(sourceUnits.map((unit) => [unit.id, unit]));
      let bins = [];

      const placementOptions = (bin, piece) => {
        const options = [];
        const requestedLength = Number(piece.baseLength || piece.length || 0);
        const requestedWidth = Number(piece.baseWidth || piece.width || 0);
        bin.freeRects.forEach((rect, rectIndex) => {
          const orientations = [
            { width: requestedLength, height: requestedWidth, rotated: false },
            ...(requestedLength === requestedWidth ? [] : [{ width: requestedWidth, height: requestedLength, rotated: true }]),
          ];
          orientations.forEach((orientation) => {
            if (orientation.width > rect.width || orientation.height > rect.height) return;
            const remainingArea = rect.width * rect.height - orientation.width * orientation.height;
            const shortSide = Math.min(rect.width - orientation.width, rect.height - orientation.height);
            options.push({ rect, rectIndex, ...orientation, score: remainingArea * 10 + shortSide });
          });
        });
        return options.sort((a, b) => a.score - b.score || Number(a.rotated) - Number(b.rotated));
      };

      const place = (bin, piece, option) => {
        const rect = option.rect;
        bin.freeRects.splice(option.rectIndex, 1);
        const rightWidth = rect.width - option.width;
        const bottomHeight = rect.height - option.height;
        const splitWide = strategy.split === 'wide' ? rightWidth >= bottomHeight : rightWidth < bottomHeight;
        const additions = splitWide
          ? [
              { x: rect.x + option.width, y: rect.y, width: rightWidth, height: option.height },
              { x: rect.x, y: rect.y + option.height, width: rect.width, height: bottomHeight },
            ]
          : [
              { x: rect.x + option.width, y: rect.y, width: rightWidth, height: rect.height },
              { x: rect.x, y: rect.y + option.height, width: option.width, height: bottomHeight },
            ];
        additions.filter((free) => free.width > 0 && free.height > 0).forEach((free) => bin.freeRects.push(free));
        const cuts = (option.width < rect.width ? 1 : 0) + (option.height < rect.height ? 1 : 0);
        bin.pieces.push({
          ...piece,
          x: rect.x,
          y: rect.y,
          width: option.width,
          height: option.height,
          rotated: option.rotated,
          cuts,
        });
        bin.cutCount += cuts;
      };

      for (const piece of pieces) {
        const existing = bins
          .flatMap((bin, binIndex) => placementOptions(bin, piece).slice(0, 1).map((option) => ({ bin, binIndex, option })))
          .sort((a, b) => a.option.score - b.option.score)[0];
        if (existing) {
          place(existing.bin, piece, existing.option);
          continue;
        }

        const unitCandidates = Array.from(unused.values())
          .filter((unit) => this.pieceFitsProduct(piece, unit.product))
          .map((unit) => {
            const waste = unit.product.area - pieceArea(piece);
            const price = Number(unit.product.price || 0);
            let score = waste + price * 1000;
            if (strategy.unit === 'cheap') score = price * 100000 + waste;
            if (strategy.unit === 'large') score = -unit.product.area + price * 500;
            return { unit, score };
          })
          .sort((a, b) => a.score - b.score || a.unit.product.area - b.unit.product.area);
        const selected = unitCandidates[0]?.unit;
        if (!selected) return null;
        unused.delete(selected.id);
        const bin = {
          unitId: selected.id,
          product: selected.product,
          rawLength: selected.product.length,
          rawWidth: selected.product.width,
          pieces: [],
          cutCount: 0,
          freeRects: [{ x: 0, y: 0, width: selected.product.length, height: selected.product.width }],
        };
        bins.push(bin);
        const firstOption = placementOptions(bin, piece)[0];
        if (!firstOption) return null;
        place(bin, piece, firstOption);
      }

      // A greedy first choice can open a small plate even though the same piece
      // later fits into the remainder of a larger plate. Repack complete source
      // bins into the free rectangles of the other bins before scoring the plan.
      // This prevents redundant plates such as 1× R910 plus 6× R893 when the
      // twelve requested pieces already fit into 6× R893.
      const cloneBin = (bin) => ({
        ...bin,
        pieces: bin.pieces.map((piece) => ({ ...piece })),
        freeRects: bin.freeRects.map((rect) => ({ ...rect })),
      });
      const removeOneRedundantBin = () => {
        const sources = bins
          .map((bin, index) => ({ bin, index }))
          .sort((a, b) =>
            a.bin.pieces.length - b.bin.pieces.length ||
            a.bin.product.area - b.bin.product.area ||
            Number(b.bin.product.price || 0) - Number(a.bin.product.price || 0)
          );

        for (const source of sources) {
          const trialBins = bins.filter((_, index) => index !== source.index).map(cloneBin);
          const sourcePieces = [...source.bin.pieces].sort((a, b) => pieceArea(b) - pieceArea(a));
          let allPlaced = true;

          for (const piece of sourcePieces) {
            const target = trialBins
              .flatMap((bin, binIndex) => placementOptions(bin, piece).slice(0, 1).map((option) => ({ bin, binIndex, option })))
              .sort((a, b) => a.option.score - b.option.score)[0];
            if (!target) {
              allPlaced = false;
              break;
            }
            place(target.bin, piece, target.option);
          }

          if (allPlaced) {
            bins = trialBins;
            return true;
          }
        }
        return false;
      };

      let compactionPasses = 0;
      while (bins.length > 1 && compactionPasses < sourcePieces.length && removeOneRedundantBin()) {
        compactionPasses += 1;
      }

      const requestedArea = sourcePieces.reduce((sum, piece) => sum + pieceArea(piece), 0);
      const totalMaterialArea = bins.reduce((sum, bin) => sum + bin.product.area, 0);
      const products = this.aggregateMultiPlanProducts(bins);
      return {
        isMultiPieceOrder: true,
        targetLength: criteria.length,
        targetWidth: criteria.width,
        requestedCuts: criteria.cuts,
        requestedPieceCount: sourcePieces.length,
        placements: bins,
        products,
        distinctProducts: products.length,
        totalMaterialArea,
        totalWasteArea: Math.max(0, totalMaterialArea - requestedArea),
        wastePercent: totalMaterialArea ? ((totalMaterialArea - requestedArea) / totalMaterialArea) * 100 : 100,
        totalPrice: bins.reduce((sum, bin) => sum + Number(bin.product.price || 0), 0),
        cutCount: bins.reduce((sum, bin) => sum + bin.cutCount, 0),
        stockVerified: bins.every((bin) => bin.product.inventoryKnown),
        hasUntrackedStock: bins.some((bin) => !bin.product.inventoryKnown),
        family: bins[0].product.family,
        height: bins[0].product.height,
        color: bins[0].product.color,
        surface: this.summarizePlanSurface(bins),
        mesh: this.summarizePlanMesh(bins),
      };
    }

    aggregateMultiPlanProducts(bins) {
      const groups = new Map();
      bins.forEach((bin) => {
        const key = String(bin.product.variantId);
        if (!groups.has(key)) groups.set(key, { product: bin.product, quantity: 0, placements: [], cuts: new Map() });
        const group = groups.get(key);
        group.quantity += 1;
        group.placements.push(bin);
        bin.pieces.forEach((piece) => {
          const cutKey = `${piece.baseLength}×${piece.baseWidth}`;
          group.cuts.set(cutKey, (group.cuts.get(cutKey) || 0) + 1);
        });
      });
      return Array.from(groups.values());
    }

    buildMultiPieceAlternatives(criteria) {
      const candidates = this.buildRelaxedCriteriaCandidates(criteria);
      const suggestions = [];
      const seen = new Set();
      candidates.some((candidateEntry) => {
        const candidate = candidateEntry.criteria;
        const plan = this.buildMultiPiecePlan(candidate);
        if (!plan) return false;
        const signature = this.planSignature(plan);
        if (seen.has(signature)) return false;
        seen.add(signature);
        const surfaceChanged = !criteria.surfaceAny && plan.surface !== criteria.surface;
        const constructionChanged = criteria.construction !== 'any' && this.planConstruction(plan) !== criteria.construction;
        const surfaceMixed = !criteria.surfaceMix && plan.surface === 'offen-gemischt';
        const meshChanged = !criteria.meshAny && plan.mesh !== criteria.mesh;
        const meshMixed = !criteria.meshMix && plan.mesh === 'gemischt';
        const requestedHeights = Array.isArray(criteria.heights) ? criteria.heights.map(String) : [];
        const heightChanged = criteria.heightAny === false && !requestedHeights.includes(String(plan.height));
        const changes = [];
        if (constructionChanged) changes.push(this.constructionLabel(this.planConstruction(plan)));
        if (surfaceChanged) changes.push(this.surfaceLabel(plan.surface));
        if (surfaceMixed) changes.push('unterschiedlichen offenen Oberflächen');
        if (meshChanged) changes.push(this.meshLabel(plan.mesh));
        if (meshMixed) changes.push('unterschiedlichen Maschenweiten');
        if (heightChanged) changes.push(this.formatProductHeight(plan.height, plan.family));
        suggestions.push({
          plan,
          criteria: candidate,
          requestedCriteria: criteria,
          kind: `multi-piece-${candidateEntry.kind}`,
          changes,
          label: `Bedarfsliste verfügbar mit ${changes.join(' · ') || `${this.surfaceLabel(plan.surface)} · ${this.meshLabel(plan.mesh)}`}`,
        });
        return suggestions.length >= 3;
      });
      return suggestions;
    }

    areaMinimumPiece() {
      return Math.max(
        Number(this.config.minCut || 50),
        Number(this.config.minAreaPiece || 150)
      );
    }

    analyzeArea(product, criteria, minimumPiece = this.areaMinimumPiece()) {
      const candidates = this.orientations(product).map((orientation) => {
        const maximumLength = Number(criteria.maxPieceLength || 0);
        const installedLength = maximumLength ? Math.min(orientation.length, maximumLength) : orientation.length;
        const installedWidth = maximumLength ? Math.min(orientation.width, maximumLength) : orientation.width;
        const columns = Math.ceil(criteria.length / installedLength);
        const rows = Math.ceil(criteria.width / installedWidth);
        const quantity = columns * rows;
        const targetArea = criteria.length * criteria.width;
        const rawQuantityEstimate = Math.max(1, Math.ceil(targetArea / product.area));
        const coveredArea = rawQuantityEstimate * product.area;
        const wasteArea = Math.max(0, coveredArea - targetArea);
        const wastePercent = coveredArea ? (wasteArea / coveredArea) * 100 : 100;
        const fittingOrientation = { ...orientation, length: installedLength, width: installedWidth };
        const cutRows = this.buildAreaCuts(criteria, fittingOrientation);
        const cuttable = cutRows.every(
          (cut) => cut.length >= minimumPiece && cut.width >= minimumPiece
        );
        const stockSufficient = !product.inventoryKnown || product.inventoryQuantity >= rawQuantityEstimate;
        const stockPenalty = stockSufficient ? 0 : 45;
        return {
          ...orientation,
          length: installedLength,
          width: installedWidth,
          rawLength: orientation.length,
          rawWidth: orientation.width,
          columns,
          rows,
          quantity,
          rawQuantityEstimate,
          wasteArea,
          wastePercent,
          cutRows,
          cuttable,
          stockSufficient,
          cuts: cutRows.filter((cut) => cut.length !== orientation.length || cut.width !== orientation.width).length,
          score: wastePercent + quantity * 0.2 + rawQuantityEstimate * 0.35 + stockPenalty + (cuttable ? 0 : 25) + (orientation.rotated ? 0.25 : 0),
        };
      });

      return candidates
        .filter((candidate) => this.splitDirection(criteria) === 'free'
          || (this.splitDirection(criteria) === 'length' ? candidate.rows === 1 : candidate.columns === 1))
        .filter((candidate) => !criteria.maxPieces || candidate.quantity <= criteria.maxPieces)
        .sort((a, b) => a.score - b.score)[0] || null;
    }

    orientations(product) {
      const values = [{ length: product.length, width: product.width, rotated: false }];
      if (product.length !== product.width) values.push({ length: product.width, width: product.length, rotated: true });
      return values;
    }

    buildAreaCuts(criteria, orientation) {
      const fullColumns = Math.floor(criteria.length / orientation.length);
      const fullRows = Math.floor(criteria.width / orientation.width);
      const remainingLength = criteria.length - fullColumns * orientation.length;
      const remainingWidth = criteria.width - fullRows * orientation.width;
      const cuts = [];

      const add = (length, width, quantity) => {
        if (length > 0 && width > 0 && quantity > 0) cuts.push({ length, width, quantity });
      };

      add(orientation.length, orientation.width, fullColumns * fullRows);
      add(remainingLength, orientation.width, fullRows);
      add(orientation.length, remainingWidth, fullColumns);
      add(remainingLength, remainingWidth, remainingLength && remainingWidth ? 1 : 0);

      if (!cuts.length) add(criteria.length, criteria.width, 1);
      return cuts;
    }

    buildMixedAreaPlan(criteria, { minPiece = this.areaMinimumPiece() } = {}) {
      const targetArea = criteria.length * criteria.width;
      const compatibleProducts = this.inventoryProducts
        .filter((product) => this.matchesFilters(product, criteria))
        .filter((product) => !product.inventoryKnown || product.inventoryQuantity > 0);
      const groups = this.buildCompatibilityGroups(compatibleProducts, criteria);

      const plans = [];
      groups.forEach((products) => {
        const availableArea = products.reduce((sum, product) => {
          const quantity = product.inventoryKnown ? product.inventoryQuantity : 1;
          return sum + product.area * quantity;
        }, 0);
        if (availableArea < targetArea) return;
        if (criteria.maxPieces) {
          const largestAreas = products
            .flatMap((product) => {
              const maxLength = Number(criteria.maxPieceLength || 0);
              const usableArea = maxLength
                ? Math.min(product.length, maxLength) * Math.min(product.width, maxLength)
                : product.area;
              return Array.from({ length: Math.min(product.inventoryKnown ? product.inventoryQuantity : 1, criteria.maxPieces) }, () => usableArea);
            })
            .sort((a, b) => b - a)
            .slice(0, criteria.maxPieces);
          if (largestAreas.reduce((sum, area) => sum + area, 0) < targetArea) return;
        }
        const plan = this.planCompatibleGroup(products, criteria, minPiece);
        if (plan) plans.push(plan);
      });

      return this.rankPlans(plans)[0] || null;
    }

    planCompatibleGroup(products, criteria, minCut = this.areaMinimumPiece()) {
      if (this.splitDirection(criteria) !== 'free') return this.planDirectionalGroup(products, criteria, minCut);
      const minArea = Math.max(1, Math.min(...products.map((product) => product.area)));
      const usefulUnitLimit = Math.min(220, Math.ceil((criteria.length * criteria.width) / minArea) + 30);
      const units = [];

      products.forEach((product) => {
        const quantity = product.inventoryKnown ? product.inventoryQuantity : 1;
        const cappedQuantity = Math.min(Math.max(0, quantity), 100, usefulUnitLimit);
        for (let index = 0; index < cappedQuantity && units.length < 220; index += 1) {
          units.push({ id: `${product.variantId}:${index}`, product });
        }
      });

      if (!units.length) return null;
      const strategies = ['packing', 'efficient', 'tall', 'count', 'cheap'];
      const candidates = strategies
        .map((strategy) => this.planWithStrategy(units, criteria, minCut, strategy))
        .filter(Boolean)
        .filter((plan) => !criteria.maxPieces || plan.placements.length <= criteria.maxPieces);
      return this.rankPlans(candidates)[0] || null;
    }

    planMatchesDirection(plan, criteria) {
      const direction = this.splitDirection(criteria);
      if (!plan || direction === 'free') return Boolean(plan);
      return plan.targetLength === criteria.length && plan.targetWidth === criteria.width
        && plan.placements.every((piece) => direction === 'length'
          ? piece.y === 0 && piece.height === criteria.width
          : piece.x === 0 && piece.width === criteria.length);
    }

    planDirectionalGroup(products, criteria, minCut) {
      // Solve one continuous row, then transpose it for a continuous project length.
      const transpose = this.splitDirection(criteria) === 'width';
      const span = transpose ? criteria.length : criteria.width;
      const run = transpose ? criteria.width : criteria.length;
      if (span < minCut || run < minCut || (criteria.maxPieceLength && span > criteria.maxPieceLength)) return null;
      const usable = products.filter((product) => this.orientations(product).some((orientation) =>
        orientation.width >= span && orientation.length >= minCut
      ));
      if (!usable.length) return null;
      const candidates = [];
      // Also try each lot alone so a cheap small lot cannot prevent a complete solution.
      const groups = [usable, ...usable.map((product) => [product])];
      for (const group of groups) {
        for (const reuse of [false, true]) {
          const units = [];
          for (const product of group) {
            const stock = product.inventoryKnown ? product.inventoryQuantity : 1;
            const capacity = Math.max(...this.orientations(product).map((orientation) =>
              Math.floor(orientation.width / span) * Math.floor(orientation.length / minCut)
            ));
            const count = Math.min(stock * (reuse ? capacity : 1), criteria.maxPieces || 100, Math.ceil(run / minCut));
            for (let index = 0; index < count; index += 1) units.push({ id: `${product.variantId}:direction:${index}`, product });
          }
          for (const strategy of ['count', 'packing', 'efficient', 'cheap']) {
            const row = this.buildRow(units, run, span, minCut, strategy, criteria.maxPieces, criteria.maxPieceLength);
            if (!row) continue;
            for (const rowPieces of [row.placements, this.balanceDirectionalRow(row.placements, run, criteria.maxPieceLength)]) {
              const placements = rowPieces.map((piece) => transpose
                ? { ...piece, x: 0, y: piece.x, width: piece.height, height: piece.width,
                    rawLength: piece.rawWidth, rawWidth: piece.rawLength, rotated: !piece.rotated }
                : { ...piece, y: 0 });
              const averageLength = run / rowPieces.length;
              const directionBalancePenalty = rowPieces.reduce((sum, piece) => sum + Math.max(0, 0.5 - piece.width / averageLength) * 40, 0);
              const plan = this.finalizeAreaPlan({ targetLength: criteria.length, targetWidth: criteria.width, placements, directionBalancePenalty });
              // Virtual segments may share a raw plate; charge and check the final packed stock.
              if (!this.planMatchesDirection(plan, criteria)) continue;
              if (plan.products.some(({ product, quantity }) => quantity > (product.inventoryKnown ? product.inventoryQuantity : 1))) continue;
              candidates.push(plan);
            }
          }
        }
      }
      return this.rankPlans(candidates)[0] || null;
    }

    balanceDirectionalRow(placements, run, maxPieceLength) {
      const capacities = placements.map((piece, index) => ({ index, limit: Math.min(piece.rawLength, maxPieceLength || Number.POSITIVE_INFINITY) }))
        .sort((a, b) => a.limit - b.limit);
      const lengths = [];
      let remaining = run;
      capacities.forEach(({ index, limit }, position) => {
        const length = Math.min(limit, Math.floor(remaining / (capacities.length - position)));
        lengths[index] = length;
        remaining -= length;
      });
      let x = 0;
      return placements.map((piece, index) => {
        const width = lengths[index];
        const balanced = { ...piece, x, width,
          cuts: (width === piece.rawLength ? 0 : 1) + (piece.height === piece.rawWidth ? 0 : 1),
          wasteArea: piece.product.area - width * piece.height };
        x += width;
        return balanced;
      });
    }

    rankPlans(plans) {
      if (!plans.length) return [];
      const minimumWaste = Math.min(...plans.map((plan) => plan.wastePercent));
      const minimumPieces = Math.min(...plans.map((plan) => plan.placements.length));
      const minimumRawPlates = Math.min(...plans.map((plan) => this.planRawPlateCount(plan)));
      const minimumProducts = Math.min(...plans.map((plan) => plan.distinctProducts));
      const minimumPrice = Math.min(...plans.map((plan) => this.planTotalPriceForSort(plan)));
      const score = (plan) => {
        const wasteDifference = plan.wastePercent - minimumWaste;
        const pieceDifference = plan.placements.length - minimumPieces;
        const rawPlateDifference = this.planRawPlateCount(plan) - minimumRawPlates;
        const productDifference = plan.distinctProducts - minimumProducts;
        const priceDifference = (this.planTotalPriceForSort(plan) - minimumPrice) / Math.max(100, minimumPrice);
        const surfaceCount = new Set(plan.placements.map((placement) => placement.product.surface)).size;
        const meshCount = new Set(plan.placements.map((placement) => placement.product.mesh)).size;
        const mixPenalty = (surfaceCount > 1 ? 6 : 0) + (meshCount > 1 ? 7 : 0);
        return wasteDifference + pieceDifference * 4.5 + rawPlateDifference * 5 + productDifference * 1.5 + priceDifference * 18 + mixPenalty + Number(plan.directionBalancePenalty || 0);
      };
      return [...plans].sort((a, b) =>
        score(a) - score(b) ||
        a.placements.length - b.placements.length ||
        this.planRawPlateCount(a) - this.planRawPlateCount(b) ||
        a.wastePercent - b.wastePercent ||
        this.planTotalPriceForSort(a) - this.planTotalPriceForSort(b)
      );
    }

    planWithStrategy(sourceUnits, criteria, minCut, strategy) {
      const unused = new Map(sourceUnits.map((unit) => [unit.id, unit]));
      const placements = [];
      let y = 0;
      let remainingWidth = criteria.width;
      let guard = 0;

      while (remainingWidth > 0 && guard < 120) {
        guard += 1;
        const heights = this.candidateRowHeights(Array.from(unused.values()), remainingWidth, minCut, criteria.maxPieceLength);
        const rows = heights
          .map((height) =>
            this.buildRow(
              Array.from(unused.values()),
              criteria.length,
              height,
              minCut,
              strategy,
              criteria.maxPieces ? criteria.maxPieces - placements.length : 0,
              criteria.maxPieceLength
            )
          )
          .filter(Boolean)
          .filter((row) => !criteria.maxPieces || placements.length + row.placements.length <= criteria.maxPieces)
          .map((row) => ({ ...row, score: this.scoreRow(row, remainingWidth, strategy, minCut) }))
          .sort((a, b) => a.score - b.score);
        const row = rows[0];
        if (!row) return null;

        row.placements.forEach((placement) => {
          placements.push({ ...placement, y });
          unused.delete(placement.unitId);
        });
        y += row.height;
        remainingWidth -= row.height;
      }

      if (remainingWidth !== 0 || !placements.length) return null;
      return this.finalizeAreaPlan({
        targetLength: criteria.length,
        targetWidth: criteria.width,
        placements,
      });
    }

    candidateRowHeights(units, remainingWidth, minCut, maxPieceLength = 0) {
      const values = new Set();
      const supportedWidths = [];
      const lengthLimit = Number(maxPieceLength || 0);
      units.forEach((unit) => {
        this.orientations(unit.product).forEach((orientation) => {
          const usableWidth = lengthLimit ? Math.min(orientation.width, lengthLimit) : orientation.width;
          if (usableWidth >= minCut) supportedWidths.push(usableWidth);
        });
      });
      if (!supportedWidths.length) return [];

      const maxWidth = Math.max(...supportedWidths);
      supportedWidths.forEach((width) => values.add(Math.min(width, remainingWidth)));
      if (remainingWidth <= maxWidth) values.add(remainingWidth);

      const minimumRows = Math.max(1, Math.ceil(remainingWidth / maxWidth));
      for (let rows = minimumRows; rows <= Math.min(minimumRows + 6, 16); rows += 1) {
        values.add(Math.floor(remainingWidth / rows));
        values.add(Math.ceil(remainingWidth / rows));
      }

      Array.from(values).forEach((height) => {
        const tail = remainingWidth - height;
        if (tail > 0 && tail < minCut && remainingWidth - minCut >= minCut) values.add(remainingWidth - minCut);
      });

      return Array.from(values)
        .map(Number)
        .filter((height) => height >= minCut && height <= remainingWidth)
        .filter((height) => supportedWidths.some((width) => width >= height))
        .sort((a, b) => b - a);
    }

    buildRow(sourceUnits, targetLength, height, minCut, strategy, maxRowPieces = 0, maxPieceLength = 0) {
      const unused = new Map(sourceUnits.map((unit) => [unit.id, unit]));
      const placements = [];
      let x = 0;
      let remainingLength = targetLength;
      let guard = 0;

      while (remainingLength > 0 && guard < 120) {
        guard += 1;
        if (maxRowPieces && placements.length >= maxRowPieces) return null;
        const candidates = [];
        unused.forEach((unit) => {
          this.orientations(unit.product).forEach((orientation) => {
            if (orientation.width < height) return;
            const limit = Number(maxPieceLength || 0)
              ? Math.min(orientation.length, Number(maxPieceLength))
              : orientation.length;
            if (height > Number(maxPieceLength || Number.POSITIVE_INFINITY) || limit < minCut) return;

            const pieceLengths = new Set([Math.min(limit, remainingLength)]);
            const rawParts = Math.max(1, Math.ceil(orientation.length / limit));
            pieceLengths.add(Math.floor(orientation.length / rawParts));
            const remainingParts = Math.max(1, Math.ceil(remainingLength / limit));
            pieceLengths.add(Math.ceil(remainingLength / remainingParts));
            if (orientation.length > limit) pieceLengths.add(orientation.length - limit);

            pieceLengths.forEach((candidateLength) => {
              let pieceLength = Math.min(limit, remainingLength, Number(candidateLength));
              const tail = remainingLength - pieceLength;
              if (tail > 0 && tail < minCut) {
                const adjusted = remainingLength - minCut;
                if (adjusted >= minCut && adjusted <= limit) pieceLength = adjusted;
                else return;
              }
              if (pieceLength < minCut || pieceLength > orientation.length || pieceLength > limit) return;
              const slotsAfterThisPiece = maxRowPieces ? maxRowPieces - placements.length - 1 : Number.POSITIVE_INFINITY;
              if (pieceLength < remainingLength && slotsAfterThisPiece < 1) return;

              const installedArea = pieceLength * height;
              const rawWaste = unit.product.area - installedArea;
              const wasteRatio = rawWaste / Math.max(1, installedArea);
              const priceDensity = unit.product.price / Math.max(1, unit.product.area);
              const finishes = pieceLength === remainingLength;
              const piecesPerRaw = Math.max(1, Math.floor(orientation.length / pieceLength));
              const packingWaste = (orientation.length - Math.min(orientation.length, piecesPerRaw * pieceLength)) / orientation.length;
              let score = wasteRatio * 100 + priceDensity * 50000 + (finishes ? -5 : 0);
              if (strategy === 'packing') score = packingWaste * 120 + priceDensity * 35000 + wasteRatio * 8 + (finishes ? -2 : 0);
              if (strategy === 'count') score = -pieceLength / 80 + wasteRatio * 35 + (finishes ? -8 : 0);
              if (strategy === 'cheap') score = priceDensity * 100000 + wasteRatio * 28 + (finishes ? -3 : 0);
              if (strategy === 'tall') score = wasteRatio * 70 + priceDensity * 30000 - orientation.width / 1000 + (finishes ? -4 : 0);

              candidates.push({ unit, orientation, pieceLength, rawWaste, score });
            });
          });
        });

        candidates.sort((a, b) => a.score - b.score || b.pieceLength - a.pieceLength);
        const selected = candidates[0];
        if (!selected) return null;

        const { unit, orientation, pieceLength, rawWaste } = selected;
        const cuts = (pieceLength === orientation.length ? 0 : 1) + (height === orientation.width ? 0 : 1);
        placements.push({
          unitId: unit.id,
          product: unit.product,
          x,
          width: pieceLength,
          height,
          rawLength: orientation.length,
          rawWidth: orientation.width,
          rotated: orientation.rotated,
          cuts,
          wasteArea: rawWaste,
        });
        unused.delete(unit.id);
        x += pieceLength;
        remainingLength -= pieceLength;
      }

      if (remainingLength !== 0) return null;
      return {
        height,
        placements,
        wasteArea: placements.reduce((sum, placement) => sum + placement.wasteArea, 0),
        totalPrice: placements.reduce((sum, placement) => sum + placement.product.price, 0),
      };
    }

    packAreaProductPlacements(entries, strategy) {
      const pieces = entries
        .map(({ placement, placementIndex }) => ({
          placementIndex,
          product: placement.product,
          installedLength: Number(placement.width),
          installedWidth: Number(placement.height),
          width: Number(placement.rotated ? placement.height : placement.width),
          height: Number(placement.rotated ? placement.width : placement.height),
          rotated: Boolean(placement.rotated),
        }))
        .sort((a, b) => {
          if (strategy.order === 'length') return b.width - a.width || b.height - a.height;
          if (strategy.order === 'width') return b.height - a.height || b.width - a.width;
          if (strategy.order === 'max-side') return Math.max(b.width, b.height) - Math.max(a.width, a.height) || b.width * b.height - a.width * a.height;
          return b.width * b.height - a.width * a.height || Math.max(b.width, b.height) - Math.max(a.width, a.height);
        });
      if (!pieces.length) return [];

      const product = pieces[0].product;
      if (pieces.some((piece) => piece.width > Number(product.length) || piece.height > Number(product.width))) return [];
      let bins = [];
      const placementOptions = (bin, piece) => bin.freeRects
        .map((rect, rectIndex) => {
          if (piece.width > rect.width || piece.height > rect.height) return null;
          const remainingArea = rect.width * rect.height - piece.width * piece.height;
          const shortSide = Math.min(rect.width - piece.width, rect.height - piece.height);
          return { rect, rectIndex, score: remainingArea * 10 + shortSide };
        })
        .filter(Boolean)
        .sort((a, b) => a.score - b.score);

      const place = (bin, piece, option) => {
        const rect = option.rect;
        bin.freeRects.splice(option.rectIndex, 1);
        const rightWidth = rect.width - piece.width;
        const bottomHeight = rect.height - piece.height;
        const splitWide = strategy.split === 'wide' ? rightWidth >= bottomHeight : rightWidth < bottomHeight;
        const additions = splitWide
          ? [
              { x: rect.x + piece.width, y: rect.y, width: rightWidth, height: piece.height },
              { x: rect.x, y: rect.y + piece.height, width: rect.width, height: bottomHeight },
            ]
          : [
              { x: rect.x + piece.width, y: rect.y, width: rightWidth, height: rect.height },
              { x: rect.x, y: rect.y + piece.height, width: piece.width, height: bottomHeight },
            ];
        additions.filter((free) => free.width > 0 && free.height > 0).forEach((free) => bin.freeRects.push(free));
        const cuts = (piece.width < rect.width ? 1 : 0) + (piece.height < rect.height ? 1 : 0);
        bin.pieces.push({
          ...piece,
          baseLength: piece.installedLength,
          baseWidth: piece.installedWidth,
          x: rect.x,
          y: rect.y,
          cuts,
        });
        bin.cutCount += cuts;
      };

      for (const piece of pieces) {
        const target = bins
          .flatMap((bin) => placementOptions(bin, piece).slice(0, 1).map((option) => ({ bin, option })))
          .sort((a, b) => a.option.score - b.option.score)[0];
        if (target) {
          place(target.bin, piece, target.option);
          continue;
        }
        const bin = {
          unitId: `${product.variantId}:area-packed:${bins.length}`,
          product,
          rawLength: Number(product.length),
          rawWidth: Number(product.width),
          pieces: [],
          cutCount: 0,
          freeRects: [{ x: 0, y: 0, width: Number(product.length), height: Number(product.width) }],
        };
        const firstOption = placementOptions(bin, piece)[0];
        if (!firstOption) return [];
        bins.push(bin);
        place(bin, piece, firstOption);
      }

      const cloneBin = (bin) => ({
        ...bin,
        pieces: bin.pieces.map((piece) => ({ ...piece })),
        freeRects: bin.freeRects.map((rect) => ({ ...rect })),
      });
      const removeOneRedundantBin = () => {
        const sources = bins
          .map((bin, index) => ({ bin, index }))
          .sort((a, b) => a.bin.pieces.length - b.bin.pieces.length || a.bin.cutCount - b.bin.cutCount);
        for (const source of sources) {
          const trialBins = bins.filter((_, index) => index !== source.index).map(cloneBin);
          const sourcePieces = [...source.bin.pieces].sort((a, b) => b.width * b.height - a.width * a.height);
          let complete = true;
          for (const piece of sourcePieces) {
            const target = trialBins
              .flatMap((bin) => placementOptions(bin, piece).slice(0, 1).map((option) => ({ bin, option })))
              .sort((a, b) => a.option.score - b.option.score)[0];
            if (!target) {
              complete = false;
              break;
            }
            place(target.bin, piece, target.option);
          }
          if (complete) {
            bins = trialBins;
            return true;
          }
        }
        return false;
      };
      let passes = 0;
      while (bins.length > 1 && passes < pieces.length && removeOneRedundantBin()) passes += 1;
      return bins;
    }

    packAreaPlacements(placements) {
      const groups = new Map();
      placements.forEach((placement, placementIndex) => {
        const key = String(placement.product.variantId);
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push({ placement, placementIndex });
      });
      const strategies = [
        { order: 'area', split: 'wide' },
        { order: 'area', split: 'tall' },
        { order: 'max-side', split: 'wide' },
        { order: 'length', split: 'tall' },
        { order: 'width', split: 'wide' },
      ];
      const rawPlates = [];
      groups.forEach((entries) => {
        const candidates = strategies
          .map((strategy) => this.packAreaProductPlacements(entries, strategy))
          .filter((bins) => bins.length)
          .sort((a, b) => a.length - b.length || a.reduce((sum, bin) => sum + bin.cutCount, 0) - b.reduce((sum, bin) => sum + bin.cutCount, 0));
        rawPlates.push(...(candidates[0] || []));
      });
      rawPlates.forEach((bin, rawPlateIndex) => {
        bin.pieces.forEach((piece) => {
          const placement = placements[piece.placementIndex];
          if (placement) placement.rawPlateIndex = rawPlateIndex;
        });
      });
      return rawPlates;
    }

    aggregateAreaPlanProducts(rawPlates) {
      const groups = new Map();
      rawPlates.forEach((bin) => {
        const key = String(bin.product.variantId);
        if (!groups.has(key)) groups.set(key, { product: bin.product, quantity: 0, placements: [], cuts: new Map() });
        const group = groups.get(key);
        group.quantity += 1;
        group.placements.push(bin);
        bin.pieces.forEach((piece) => {
          const cutKey = `${piece.installedLength}×${piece.installedWidth}`;
          group.cuts.set(cutKey, (group.cuts.get(cutKey) || 0) + 1);
        });
      });
      return Array.from(groups.values());
    }

    finalizeAreaPlan(plan) {
      const rawPlates = this.packAreaPlacements(plan.placements);
      if (!rawPlates.length) return null;
      const products = this.aggregateAreaPlanProducts(rawPlates);
      const totalMaterialArea = rawPlates.reduce((sum, bin) => sum + Number(bin.product.area || bin.rawLength * bin.rawWidth), 0);
      const targetArea = Number(plan.targetLength) * Number(plan.targetWidth);
      const totalWasteArea = Math.max(0, totalMaterialArea - targetArea);
      return {
        ...plan,
        rawPlates,
        products,
        distinctProducts: products.length,
        totalMaterialArea,
        totalWasteArea,
        wastePercent: totalMaterialArea ? (totalWasteArea / totalMaterialArea) * 100 : 100,
        totalPrice: rawPlates.reduce((sum, bin) => sum + Number(bin.product.price || 0), 0),
        cutCount: rawPlates.reduce((sum, bin) => sum + Number(bin.cutCount || 0), 0),
        stockVerified: rawPlates.every((bin) => bin.product.inventoryKnown),
        hasUntrackedStock: rawPlates.some((bin) => !bin.product.inventoryKnown),
        family: rawPlates[0].product.family,
        height: rawPlates[0].product.height,
        color: rawPlates[0].product.color,
        surface: this.summarizePlanSurface(rawPlates),
        mesh: this.summarizePlanMesh(rawPlates),
      };
    }

    rawPlatesForPlan(plan) {
      if (plan?.isMultiPieceOrder) return plan.placements || [];
      if (Array.isArray(plan?.rawPlates) && plan.rawPlates.length) return plan.rawPlates;
      return (plan?.placements || []).map((placement, placementIndex) => ({
        unitId: placement.unitId,
        product: placement.product,
        rawLength: Number(placement.product.length),
        rawWidth: Number(placement.product.width),
        cutCount: Number(placement.cuts || 0),
        pieces: [{
          placementIndex,
          installedLength: Number(placement.width),
          installedWidth: Number(placement.height),
          baseLength: Number(placement.width),
          baseWidth: Number(placement.height),
          width: Number(placement.rotated ? placement.height : placement.width),
          height: Number(placement.rotated ? placement.width : placement.height),
          x: 0,
          y: 0,
          rotated: Boolean(placement.rotated),
          cuts: Number(placement.cuts || 0),
        }],
      }));
    }

    planRawPlateCount(plan) {
      return this.rawPlatesForPlan(plan).length;
    }

    unloadingDelivery(plan) {
      const pieces = [];
      let weight = 0, weightKnown = true;
      this.rawPlatesForPlan(plan).forEach(plate => {
        const product = plate.product || {};
        const area = Number(plate.rawLength || product.length) * Number(plate.rawWidth || product.width);
        const grams = Number(product.grams);
        if (!(area > 0 && grams > 0)) weightKnown = false;
        (plate.pieces || []).forEach(piece => {
          const a = Number(piece.baseLength || piece.installedLength || piece.width);
          const b = Number(piece.baseWidth || piece.installedWidth || piece.height);
          pieces.push([Math.max(a, b), Math.min(a, b)]);
          if (area > 0 && grams > 0) weight += grams * a * b / area;
        });
      });
      if (!pieces.length || pieces.some(piece => piece.some(n => !(n > 0)))) return [0, 0, 0];
      return [Math.max(...pieces.map(p => p[0])), Math.max(...pieces.map(p => p[1])), weightKnown ? Math.ceil(weight) : 0];
    }

    parcelShippingProfile(plan) {
      const parcelVariantId = Number(this.config.parcelCutVariantId || 54667718000986);
      const rawPlates = this.rawPlatesForPlan(plan);
      if (!parcelVariantId || !plan || Number(plan.cutCount || 0) < 1 || !rawPlates.length) {
        return { eligible: false };
      }

      const pieces = [];
      let weightGrams = 0;
      let weightKnown = true;
      let weightEstimated = false;

      rawPlates.forEach((rawPlate) => {
        const product = rawPlate.product || {};
        const rawLength = Math.max(0, Number(rawPlate.rawLength || product.length || 0));
        const rawWidth = Math.max(0, Number(rawPlate.rawWidth || product.width || 0));
        const rawArea = rawLength * rawWidth;
        const storedWeight = Math.max(0, Number(product.grams || 0));
        const rawWeight = storedWeight || Math.max(0, Number(product.estimatedGrams || 0));
        if (!storedWeight && rawWeight) weightEstimated = true;
        if (!rawArea || !rawWeight) weightKnown = false;

        (rawPlate.pieces || []).forEach((piece) => {
          const firstSide = Math.max(0, Number(piece.baseLength || piece.installedLength || piece.width || 0));
          const secondSide = Math.max(0, Number(piece.baseWidth || piece.installedWidth || piece.height || 0));
          if (!firstSide || !secondSide) return;
          pieces.push({
            length: Math.max(firstSide, secondSide),
            width: Math.min(firstSide, secondSide),
            height: Math.max(0, Number(product.height || plan.height || 0)),
          });
          if (rawArea && rawWeight) weightGrams += rawWeight * ((firstSide * secondSide) / rawArea);
        });
      });

      if (!pieces.length || !weightKnown) return { eligible: false, weightKnown };

      const length = Math.max(...pieces.map((piece) => piece.length));
      const width = Math.max(...pieces.map((piece) => piece.width));
      const height = pieces.reduce((sum, piece) => sum + piece.height, 0);
      const roundedWeightGrams = Math.ceil(weightGrams);
      const maxLength = Math.max(0, Number(this.config.parcelMaxLength || 1000));
      const maxWidth = Math.max(0, Number(this.config.parcelMaxWidth || 760));
      const maxHeight = Math.max(0, Number(this.config.parcelMaxHeight || 760));
      const maxWeightGrams = Math.max(0, Number(this.config.parcelMaxWeightGrams || 30000));
      const eligible = length <= maxLength
        && width <= maxWidth
        && height <= maxHeight
        && roundedWeightGrams <= maxWeightGrams;

      return {
        eligible,
        length,
        width,
        height,
        weightGrams: roundedWeightGrams,
        weightKnown,
        weightEstimated,
      };
    }

    buildPlanFromSingleMatch(match, criteria) {
      const { product, fit } = match;
      const optimizedPlan = this.planCompatibleGroup([product], criteria);
      if (optimizedPlan) return optimizedPlan;
      if (this.splitDirection(criteria) !== 'free') return null;
      if (!fit.cuttable) return null;
      const placements = [];
      let position = 0;

      for (let row = 0; row < fit.rows; row += 1) {
        const y = row * fit.width;
        const installedHeight = Math.min(fit.width, criteria.width - y);
        for (let column = 0; column < fit.columns; column += 1) {
          const x = column * fit.length;
          const installedWidth = Math.min(fit.length, criteria.length - x);
          const cuts = (installedWidth === fit.length ? 0 : 1) + (installedHeight === fit.width ? 0 : 1);
          placements.push({
            unitId: `${product.variantId}:fallback:${position}`,
            product,
            x,
            y,
            width: installedWidth,
            height: installedHeight,
            rawLength: Number(fit.rawLength || fit.length),
            rawWidth: Number(fit.rawWidth || fit.width),
            rotated: fit.rotated,
            cuts,
            wasteArea: product.area - installedWidth * installedHeight,
          });
          position += 1;
        }
      }

      return this.finalizeAreaPlan({
        targetLength: criteria.length,
        targetWidth: criteria.width,
        placements,
      });
    }

    buildSinglePiecePlan(match, criteria, finishMode = 'cut') {
      const { product, fit } = match;
      const acceptedNearFit = finishMode === 'as-is' && fit.nearFit;
      const targetLength = acceptedNearFit ? Number(fit.length) : criteria.length;
      const targetWidth = acceptedNearFit ? Number(fit.width) : criteria.width;
      const plan = this.finalizeAreaPlan({
        targetLength,
        targetWidth,
        placements: [{
          unitId: `${product.variantId}:single-piece`,
          product,
          x: 0,
          y: 0,
          width: targetLength,
          height: targetWidth,
          rawLength: Number(fit.length),
          rawWidth: Number(fit.width),
          rotated: Boolean(fit.rotated),
          cuts: acceptedNearFit ? 0 : Number(fit.exactCuts ?? fit.cuts ?? 0),
          wasteArea: acceptedNearFit ? 0 : Math.max(0, Number(product.area || 0) - criteria.length * criteria.width),
        }],
      });
      if (!plan) return null;
      return {
        ...plan,
        acceptedNearFit,
        requestedLength: criteria.length,
        requestedWidth: criteria.width,
        deliveredLength: targetLength,
        deliveredWidth: targetWidth,
        deltaLength: targetLength - criteria.length,
        deltaWidth: targetWidth - criteria.width,
      };
    }

    scoreRow(row, remainingWidth, strategy, minPiece = this.areaMinimumPiece()) {
      const installedArea = row.placements.reduce((sum, placement) => sum + placement.width * placement.height, 0);
      const wasteRatio = row.wasteArea / Math.max(1, installedArea);
      const priceDensity = row.totalPrice / Math.max(1, installedArea);
      const tail = remainingWidth - row.height;
      const tailPenalty = tail > 0 && tail < minPiece ? 10000 : 0;
      if (strategy === 'tall') return wasteRatio * 50 - row.height / 80 + row.placements.length + tailPenalty;
      if (strategy === 'count') return row.placements.length * 12 + wasteRatio * 40 + tailPenalty;
      if (strategy === 'cheap') return priceDensity * 100000 + wasteRatio * 35 + row.placements.length * 0.5 + tailPenalty;
      return wasteRatio * 100 + row.placements.length * 0.8 + priceDensity * 30000 + tailPenalty;
    }

    aggregatePlanProducts(placements) {
      const groups = new Map();
      placements.forEach((placement) => {
        const key = String(placement.product.variantId);
        if (!groups.has(key)) groups.set(key, { product: placement.product, quantity: 0, placements: [], cuts: new Map() });
        const group = groups.get(key);
        group.quantity += 1;
        group.placements.push(placement);
        const cutKey = `${placement.width}×${placement.height}`;
        group.cuts.set(cutKey, (group.cuts.get(cutKey) || 0) + 1);
      });
      return Array.from(groups.values());
    }

    summarizePlanSurface(placements) {
      const surfaces = new Set(placements.map((placement) => placement.product.surface));
      if (surfaces.has('geschlossen-besandet')) return 'geschlossen-besandet';
      if (surfaces.has('konkav') && surfaces.has('besandet')) return 'offen-gemischt';
      return Array.from(surfaces)[0] || 'unbekannt';
    }

    summarizePlanMesh(placements) {
      const meshes = new Set(placements.map((placement) => placement.product.mesh || 'unbekannt'));
      return meshes.size > 1 ? 'gemischt' : Array.from(meshes)[0] || 'unbekannt';
    }

    planMeshExplanation(plan) {
      const meshes = Array.from(new Set((plan.placements || []).map((placement) => placement.product.mesh || 'unbekannt')));
      if (meshes.length > 1) {
        return `Dieser Vorschlag verwendet ${meshes.map((mesh) => this.meshLabel(mesh)).join(' und ')}. Unterschiedliche Maschenweiten wurden von Ihnen zugelassen.`;
      }
      return `Die Maschenweite bleibt in der gesamten Lösung einheitlich: ${this.meshLabel(meshes[0] || plan.mesh)}.`;
    }

    renderResults(matches, criteria, plan = null, alternatives = []) {
      if (this.planResult.parentElement === this.resultGrid) this.resultGrid.after(this.planResult);
      const limit = Math.max(1, Number(this.config.resultLimit || 6));
      const visible = matches.slice(0, limit);
      this.alternativePlans = alternatives;
      this.recommendedPlan = plan;
      this.visibleMatches = alternatives.length ? [] : visible;
      this.resultsSection.hidden = false;
      this.emptyState.hidden = visible.length > 0 || !!plan || alternatives.length > 0;
      const recommendedCard = plan && (plan.isMultiPieceOrder || plan.distinctProducts > 1 || plan.placements.length > 1)
        ? this.recommendedPlanCardMarkup(plan, criteria)
        : '';
      this.resultGrid.innerHTML = alternatives.length
        ? alternatives.map((alternative, index) => this.alternativeCardMarkup(alternative, index)).join('')
        : recommendedCard + visible.map((match, index) => this.cardMarkup(match, criteria, index)).join('');
      this.setupResultSlider();
      this.activePlan = null;

      if (criteria.mode === 'area') {
        this.resultsTitle.textContent = alternatives.length ? 'Ihre Wunschlösung ist aktuell nicht verfügbar' : 'Wählen Sie Ihre passende Lösung';
        if (alternatives.length) {
          const splitFallback = alternatives.find((alternative) => alternative.relaxesMaxPieces);
          const layoutFallback = alternatives.find((alternative) => String(alternative.kind || '').startsWith('area-'));
          if (layoutFallback) {
            const hasReducedArea = alternatives.some((alternative) => alternative.kind === 'area-reduced');
            const hasNarrowStrip = alternatives.some((alternative) => alternative.kind === 'area-narrow-strip');
            const message = hasReducedArea && hasNarrowStrip
              ? 'Sie können die Fläche geringfügig verkleinern oder das Wunschmaß mit einem klar markierten Randstreifen belegen.'
              : hasReducedArea
                ? 'Wir zeigen Ihnen deshalb eine geringfügig kleinere Fläche ohne schmalen Randstreifen.'
                : 'Sie können das Wunschmaß mit einem klar markierten schmalen Randstreifen vollständig belegen.';
            this.setResultsSummary(
              `Ohne Teilstücke unter ${this.areaMinimumPiece()} mm ist das Wunschmaß mit den verfügbaren Restposten aktuell nicht möglich. ${message}`,
              { alternative: true, title: hasReducedArea && hasNarrowStrip ? 'Zwei Möglichkeiten für Ihre Fläche' : 'Eine passende Alternative für Ihre Fläche' }
            );
          } else if (criteria.maxPieces === 1 && splitFallback) {
            const minimumPieces = Math.min(...alternatives
              .filter((alternative) => alternative.relaxesMaxPieces)
              .map((alternative) => alternative.plan.placements.length));
            this.setResultsSummary(
              `Eine einteilige Lösung ist aktuell nicht verfügbar. Mit mindestens ${minimumPieces} Teilstücken können wir Ihre Fläche vollständig belegen. Wählen Sie eine Alternative, um den Verlege- und Zuschnittsplan zu sehen.`,
              { alternative: true, title: `Alternative verfügbar: Lösung mit ${minimumPieces} Teilstücken` }
            );
          } else {
            this.setResultsSummary(
              'Die folgenden Lösungen passen zu Ihren Maßen. Jede Karte zeigt deutlich, welche Eigenschaft gegenüber Ihrer Auswahl geändert wird.',
              { alternative: true, title: 'Wir haben passende Alternativen gefunden' }
            );
          }
        } else {
          const lengthNote = criteria.maxPieceLength
            ? ` Kein Teilstück ist länger als ${criteria.maxPieceLength} mm.`
            : '';
          const summary = plan
            ? `Wählen Sie eine Lösung aus. Anschließend sehen Sie den passenden Verlege- und Zuschnittsplan.${criteria.maxPieces ? ` Ihre Vorgabe von maximal ${criteria.maxPieces} ${criteria.maxPieces === 1 ? 'Teilstück' : 'Teilstücken'} wird berücksichtigt.` : ''}${lengthNote}`
            : criteria.maxPieces
              ? `Mit maximal ${criteria.maxPieces} ${criteria.maxPieces === 1 ? 'Teilstück' : 'Teilstücken'} können wir Ihre Fläche aus den verfügbaren Restposten aktuell nicht vollständig belegen.${lengthNote}`
              : `Mit den ausgewählten Eigenschaften können wir Ihre Fläche aktuell nicht vollständig belegen. Unterhalb des Finders können Sie weitere Restposten ansehen oder Ihr Projekt anfragen.${lengthNote}`;
          this.setResultsSummary(summary);
        }
        this.renderAreaPlan(null, criteria);
        this.markSelectedPlan(null);
        this.markSelectedRecommended(false);
        this.placePlanBelowOptions();
      } else {
        if (this.isMultiPieceCriteria(criteria)) {
          if (plan) {
            this.resultsTitle.textContent = 'Passende Lösung für Ihre Bedarfsliste';
            this.setResultsSummary(`${plan.requestedPieceCount} Roste werden gemeinsam aus ${plan.placements.length} ${plan.placements.length === 1 ? 'Rohplatte' : 'Rohplatten'} geplant. Plattenzahl, Preis und Verschnitt bestimmen gemeinsam die Empfehlung.`);
          } else if (alternatives.length) {
            this.resultsTitle.textContent = 'Ihre Wunschkombination ist aktuell nicht verfügbar';
            this.setResultsSummary(
              'Alle angegebenen Maße können gefertigt werden. Jede Karte zeigt deutlich, welche Eigenschaft gegenüber Ihrer Auswahl geändert wird.',
              { alternative: true, title: 'Ihre komplette Bedarfsliste ist als Alternative verfügbar' }
            );
          } else {
            this.resultsTitle.textContent = 'Bedarfsliste aktuell nicht vollständig verfügbar';
            this.setResultsSummary('Für die angegebenen Maße und Mengen ist aktuell keine vollständige Restposten-Lösung verfügbar. Ändern Sie eine Eigenschaft oder senden Sie uns Ihre Anfrage.');
          }
          this.renderAreaPlan(null, criteria);
          this.markSelectedRecommended(false);
          this.placePlanBelowOptions();
        } else if (plan) {
          this.resultsTitle.textContent = 'Geteilte Lösung für Ihr Wunschmaß';
          this.setResultsSummary(`Eine einzelne Restplatte ist in diesem Maß aktuell nicht verfügbar. Diese Lösung verwendet ${plan.placements.length} Teilstücke und berücksichtigt Teilstückzahl, Preis und Verschnitt gemeinsam.`);
          this.renderAreaPlan(null, criteria);
          this.markSelectedRecommended(false);
          this.placePlanBelowOptions();
        } else {
          this.resultsTitle.textContent = 'Passende Restposten für Ihr Maß';
          this.setResultsSummary(matches.length
            ? `Wir haben ${matches.length === 1 ? 'einen passenden Restposten' : `${matches.length} passende Restposten`} gefunden. Gesamtpreis und Verschnitt bestimmen gemeinsam die Reihenfolge.`
            : 'Für Ihr Wunschmaß und die gewählten Eigenschaften ist aktuell keine passende Lösung verfügbar. Ändern Sie eine Eigenschaft oder senden Sie uns Ihre Anfrage.');
          this.planResult.hidden = true;
          this.planResult.innerHTML = '';
        }
      }

      this.resultGrid.querySelectorAll('[data-result-index]').forEach((card) => {
        const show = () => {
          const match = visible[Number(card.dataset.resultIndex)];
          if (match) this.updateVisual(match, criteria);
        };
        card.addEventListener('mouseenter', show);
        card.addEventListener('focusin', show);
      });
      const directionLabel = this.splitDirectionLabel(criteria);
      if (directionLabel) {
        if (!plan && !matches.length && !alternatives.length) {
          this.resultsTitle.textContent = 'Keine Lösung mit dieser Teilungsrichtung';
          this.setResultsSummary(`${directionLabel}. Mit den gewählten Eigenschaften ist das aktuell nicht möglich. Ändern Sie die Eigenschaften oder wählen Sie „Frei aufteilen“.`);
          this.emptyState.hidden = true;
        } else {
          const note = document.createElement('span');
          note.className = 'restposten-finder__direction-result';
          note.textContent = `${directionLabel}. Diese Vorgabe gilt für alle gezeigten Lösungen.`;
          this.resultsSummary.append(note);
        }
      }
    }

    setResultsSummary(message, { alternative = false, title = '' } = {}) {
      this.resultsSummary.classList.toggle('is-alternative-notice', alternative);
      if (!alternative) {
        this.resultsSummary.textContent = message;
        return;
      }
      this.resultsSummary.innerHTML = `
        <strong><span aria-hidden="true">!</span>${this.escapeHtml(title)}</strong>
        <span>${this.escapeHtml(message)}</span>`;
    }

    alternativeRequestLabel(alternative) {
      const requested = alternative.requestedCriteria || this.lastCriteria || {};
      if (String(alternative.kind || '').startsWith('area-')) {
        return `${requested.length} × ${requested.width} mm ohne Teilstücke unter ${this.areaMinimumPiece()} mm`;
      }
      const parts = [];
      if (alternative.relaxesMaxPieces && requested.maxPieces === 1) parts.push('maximal 1 Teilstück');
      if (!requested.surfaceAny && requested.surface) parts.push(this.surfaceLabel(requested.surface));
      else if (requested.surfaceAny) parts.push(requested.surfaceMix ? 'Oberflächen dürfen sich unterscheiden' : 'eine einheitliche Oberfläche');
      if (!requested.meshAny && requested.mesh) parts.push(this.meshLabel(requested.mesh));
      else if (requested.meshAny) parts.push(requested.meshMix ? 'Maschenweiten dürfen sich unterscheiden' : 'eine einheitliche Maschenweite');
      if (requested.heightAny === false) parts.push(this.criteriaHeightLabel(requested));
      return parts.join(' und ') || 'die ursprünglichen Eigenschaften';
    }

    alternativePlanLabel(alternative) {
      if (alternative.kind === 'area-reduced') {
        return `${alternative.plan.targetLength} × ${alternative.plan.targetWidth} mm ohne schmalen Randstreifen`;
      }
      if (alternative.kind === 'area-narrow-strip') {
        const sizes = alternative.criteria?.narrowPieceSizes || this.narrowAreaPieceSizes(alternative.plan);
        return `${alternative.plan.targetLength} × ${alternative.plan.targetWidth} mm mit ${sizes.map((size) => `${size} mm`).join(' / ')} Randstreifen`;
      }
      const parts = [];
      if (alternative.relaxesMaxPieces) parts.push(`${alternative.plan.placements.length} Teilstücke`);
      parts.push(this.surfaceLabel(alternative.plan.surface));
      parts.push(this.meshLabel(alternative.plan.mesh));
      if (alternative.plan.height) parts.push(this.formatProductHeight(alternative.plan.height, alternative.plan.family));
      return parts.join(' und ');
    }

    recommendedPlanCardMarkup(plan, criteria = {}) {
      const primary = plan.products[0]?.product;
      const codes = plan.products.map((group, index) => this.productCode(group.product, index)).join(' + ');
      const image = primary?.image
        ? `<img src="${this.escapeHtml(primary.image)}" alt="Kombinierte Restposten-Lösung" loading="lazy" width="720" height="405">`
        : '<span class="restposten-finder__card-placeholder" aria-hidden="true">▦</span>';
      const pricing = this.planPricing(plan);
      const splitPiece = criteria.mode === 'piece';
      const multiPieceOrder = plan.isMultiPieceOrder === true;
      const badge = multiPieceOrder ? 'Bedarfsliste optimiert' : splitPiece ? 'Geteilte Maßlösung' : 'Beste Kombination';
      const heading = multiPieceOrder
        ? `${plan.requestedPieceCount} Roste aus ${plan.placements.length} ${plan.placements.length === 1 ? 'Rohplatte' : 'Rohplatten'}`
        : splitPiece
          ? `${plan.placements.length} Teilstücke für Ihr Wunschmaß`
        : `${plan.distinctProducts} Restposten effizient kombiniert`;
      const description = multiPieceOrder
        ? `Alle gewünschten Maße und Mengen werden gemeinsam geplant. Mehrere Roste werden, wenn möglich, aus derselben Rohplatte gefertigt. Verwendet werden ${this.escapeHtml(codes)}.`
        : splitPiece
          ? `Eine einzelne Restplatte ist in dieser Größe nicht verfügbar. Diese Aufteilung bietet eine ausgewogene Lösung aus wenigen Teilstücken, Preis und Verschnitt. Verwendet werden ${this.escapeHtml(codes)}.`
        : `Mit diesen verfügbaren Restposten wird Ihre Fläche vollständig belegt. Vorgesehen sind ${this.escapeHtml(codes)}.`;
      const firstFactLabel = multiPieceOrder ? 'Fertigteile' : 'Teilstücke';
      const firstFactValue = multiPieceOrder ? plan.requestedPieceCount : plan.placements.length;
      const rawPlateCount = this.planRawPlateCount(plan);
      return `
        <article class="restposten-finder__card restposten-finder__card--recommended" data-recommended-plan-card>
          <div class="restposten-finder__card-media">
            ${image}
            <span class="restposten-finder__card-badge">${badge}</span>
            <span class="restposten-finder__card-rank" aria-label="Beste Empfehlung">★</span>
          </div>
          <div class="restposten-finder__card-body">
            <div class="restposten-finder__card-meta">
              <span>${this.escapeHtml(this.surfaceLabel(plan.surface))}</span>
              <span>${this.escapeHtml(this.meshLabel(plan.mesh))}</span>
              <span>${this.escapeHtml(this.formatProductHeight(plan.height, plan.family))}</span>
              <span>${plan.distinctProducts} Posten</span>
            </div>
            <h4>${heading}</h4>
            <p class="restposten-finder__card-fit">${description}</p>
            <dl class="restposten-finder__card-facts">
              <div><dt>${firstFactLabel}</dt><dd>${firstFactValue}</dd></div>
              <div><dt>Verschnitt</dt><dd>${this.formatNumber(plan.wastePercent, 1)} %</dd></div>
              <div><dt>Rohplatten</dt><dd>${rawPlateCount}</dd></div>
            </dl>
            ${this.priceBreakdownMarkup(pricing, { compact: true })}
            <div class="restposten-finder__card-actions restposten-finder__card-actions--single">
              <button type="button" data-show-recommended-plan>Lösung auswählen</button>
            </div>
          </div>
        </article>`;
    }

    alternativeCardMarkup(alternative, index) {
      const { plan } = alternative;
      const primary = plan.products[0]?.product;
      const codes = plan.products.map((group, productIndex) => this.productCode(group.product, productIndex)).join(' + ');
      const image = primary?.image
        ? `<img src="${this.escapeHtml(primary.image)}" alt="${this.escapeHtml(alternative.label)}" loading="lazy" width="720" height="405">`
        : '<span class="restposten-finder__card-placeholder" aria-hidden="true">▦</span>';
      const requestedLabel = this.alternativeRequestLabel(alternative);
      const planLabel = this.alternativePlanLabel(alternative);
      const pricing = this.planPricing(plan);
      const splitFallback = alternative.relaxesMaxPieces === true;
      const reducedArea = alternative.kind === 'area-reduced';
      const narrowStrip = alternative.kind === 'area-narrow-strip';
      const multiPieceOrder = plan.isMultiPieceOrder === true;
      const rawPlateCount = this.planRawPlateCount(plan);
      const badge = reducedArea ? 'Etwas kleinere Fläche' : narrowStrip ? 'Wunschmaß mit Randstreifen' : 'Alternative';
      const actionLabel = reducedArea
        ? 'Kleinere Fläche auswählen'
        : narrowStrip
          ? 'Randstreifen akzeptieren'
          : splitFallback
            ? 'Geteilte Lösung auswählen'
            : multiPieceOrder
              ? 'Bedarfsliste auswählen'
              : 'Alternative auswählen';
      const changes = Array.isArray(alternative.changes) && alternative.changes.length
        ? alternative.changes.join(' · ')
        : planLabel;
      return `
        <article class="restposten-finder__card restposten-finder__card--alternative" data-alternative-index="${index}">
          <div class="restposten-finder__card-media">
            ${image}
            <span class="restposten-finder__card-badge">${badge}</span>
            <span class="restposten-finder__card-rank">${index + 1}</span>
          </div>
          <div class="restposten-finder__card-body">
            <div class="restposten-finder__alternative-warning">
              <strong>${reducedArea ? 'Ohne schmalen Randstreifen' : narrowStrip ? 'Ausnahme: schmaler Randstreifen' : 'Nicht exakt Ihre Auswahl'}</strong>
              <span>Geändert wird: ${this.escapeHtml(changes)}</span>
            </div>
            <div class="restposten-finder__card-meta">
              <span>${this.escapeHtml(this.surfaceLabel(plan.surface))}</span>
              <span>${this.escapeHtml(this.meshLabel(plan.mesh))}</span>
              <span>${this.escapeHtml(this.formatProductHeight(plan.height, plan.family))}</span>
            </div>
            <h4>${this.escapeHtml(alternative.label)}</h4>
            <div class="restposten-finder__alternative-compare" aria-label="Vergleich mit Ihrer Auswahl">
              <div><span>Ihre Auswahl</span><strong>${this.escapeHtml(requestedLabel)}</strong></div>
              <span class="restposten-finder__alternative-arrow" aria-hidden="true">→</span>
              <div><span>Verfügbare Lösung</span><strong>${this.escapeHtml(planLabel)}</strong></div>
            </div>
            <p class="restposten-finder__card-stock">Verwendete Posten: ${this.escapeHtml(codes)}</p>
            <dl class="restposten-finder__card-facts">
              <div><dt>${multiPieceOrder ? 'Fertigteile' : 'Teilstücke'}</dt><dd>${multiPieceOrder ? plan.requestedPieceCount : plan.placements.length}</dd></div>
              <div><dt>Verschnitt</dt><dd>${this.formatNumber(plan.wastePercent, 1)} %</dd></div>
              <div><dt>Rohplatten</dt><dd>${rawPlateCount}</dd></div>
            </dl>
            ${this.priceBreakdownMarkup(pricing, { compact: true })}
            <div class="restposten-finder__card-actions restposten-finder__card-actions--single">
              <button type="button" data-show-alternative-plan="${index}" data-default-label="${actionLabel}">${actionLabel}</button>
            </div>
          </div>
        </article>`;
    }

    placePlanNearCard() {
      this.placePlanBelowOptions();
    }

    placePlanBelowOptions() {
      (this.resultSlider || this.resultGrid).after(this.planResult);
      this.planResult.classList.remove('is-inline');
    }

    sliderCards() {
      return Array.from(this.resultGrid?.querySelectorAll('[data-recommended-plan-card], [data-result-index], [data-alternative-index]') || []);
    }

    sliderVisibleCount() {
      if (window.matchMedia('(min-width: 1120px)').matches) return 3;
      if (window.matchMedia('(min-width: 680px)').matches) return 2;
      return 1;
    }

    setupResultSlider() {
      const cards = this.sliderCards();
      cards.forEach((card, index) => {
        card.setAttribute('role', 'group');
        card.setAttribute('aria-roledescription', 'Vorschlag');
        card.setAttribute('aria-label', `${index + 1} von ${cards.length}`);
      });
      if (this.resultGrid) this.resultGrid.scrollLeft = 0;
      this.sliderIndex = 0;
      this.updateResultSlider();
      window.requestAnimationFrame(() => this.updateResultSlider());
    }

    scheduleSliderUpdate() {
      if (this.sliderUpdateFrame) window.cancelAnimationFrame(this.sliderUpdateFrame);
      this.sliderUpdateFrame = window.requestAnimationFrame(() => {
        this.sliderUpdateFrame = 0;
        this.updateResultSlider();
      });
    }

    updateResultSlider() {
      const cards = this.sliderCards();
      const total = cards.length;
      const visibleCount = this.sliderVisibleCount();
      if (this.sliderControls) this.sliderControls.hidden = total <= visibleCount;
      if (this.sliderTotal) this.sliderTotal.textContent = String(Math.max(1, total));
      if (!total || !this.resultGrid) {
        this.sliderIndex = 0;
        if (this.sliderCurrent) this.sliderCurrent.textContent = '1';
        return;
      }

      const gridRect = this.resultGrid.getBoundingClientRect();
      const paddingLeft = Number.parseFloat(window.getComputedStyle(this.resultGrid).paddingLeft) || 0;
      const targetLeft = gridRect.left + paddingLeft;
      this.sliderIndex = cards.reduce((bestIndex, card, index) => {
        const bestDistance = Math.abs(cards[bestIndex].getBoundingClientRect().left - targetLeft);
        const distance = Math.abs(card.getBoundingClientRect().left - targetLeft);
        return distance < bestDistance ? index : bestIndex;
      }, 0);

      if (this.sliderCurrent) this.sliderCurrent.textContent = String(this.sliderIndex + 1);
      const rangeEnd = Math.min(total, this.sliderIndex + visibleCount);
      if (this.sliderLabel) {
        this.sliderLabel.textContent = visibleCount === 1
          ? `Vorschlag ${this.sliderIndex + 1} von ${total}`
          : `Vorschläge ${this.sliderIndex + 1}–${rangeEnd} von ${total}`;
      }
      if (this.sliderPrev) this.sliderPrev.disabled = this.sliderIndex === 0;
      if (this.sliderNext) this.sliderNext.disabled = this.sliderIndex >= Math.max(0, total - visibleCount);
      this.sliderControls?.style.setProperty('--slider-progress', `${(rangeEnd / total) * 100}%`);
    }

    scrollResultSlider(direction) {
      const cards = this.sliderCards();
      if (!cards.length) return;
      this.updateResultSlider();
      const visibleCount = this.sliderVisibleCount();
      const maxIndex = Math.max(0, cards.length - visibleCount);
      const nextIndex = Math.max(0, Math.min(maxIndex, this.sliderIndex + direction * visibleCount));
      cards[nextIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      this.sliderIndex = nextIndex;
      window.setTimeout(() => this.updateResultSlider(), 280);
    }

    setupPlanSlider() {
      this.planSliderTrack = this.planResult?.querySelector('[data-plan-slider-track]');
      this.planSliderNav = this.planResult?.querySelector('[data-plan-slider-nav]');
      this.planSliderLabel = this.planResult?.querySelector('[data-plan-slider-label]');
      this.planSliderCurrent = this.planResult?.querySelector('[data-plan-slider-current]');
      this.planSliderPrev = this.planResult?.querySelector('[data-plan-slider-prev]');
      this.planSliderNext = this.planResult?.querySelector('[data-plan-slider-next]');
      this.planSliderIndex = 0;
      if (!this.planSliderTrack) return;

      this.planSliderTrack.scrollLeft = 0;
      this.planSliderTrack.addEventListener('scroll', () => this.schedulePlanSliderUpdate(), { passive: true });
      this.planSliderTrack.querySelectorAll('img').forEach((image) => {
        if (!image.complete) image.addEventListener('load', () => this.schedulePlanSliderUpdate(), { once: true });
      });
      this.updatePlanSlider();
      window.requestAnimationFrame(() => this.updatePlanSlider());
    }

    schedulePlanSliderUpdate() {
      if (!this.planSliderTrack) return;
      if (this.planSliderUpdateFrame) window.cancelAnimationFrame(this.planSliderUpdateFrame);
      this.planSliderUpdateFrame = window.requestAnimationFrame(() => {
        this.planSliderUpdateFrame = 0;
        this.updatePlanSlider();
      });
    }

    updatePlanSlider() {
      if (!this.planSliderTrack) return;
      const slides = Array.from(this.planSliderTrack.querySelectorAll('[data-plan-slide]'));
      if (!slides.length) return;
      const trackRect = this.planSliderTrack.getBoundingClientRect();
      this.planSliderIndex = slides.reduce((bestIndex, slide, index) => {
        const bestDistance = Math.abs(slides[bestIndex].getBoundingClientRect().left - trackRect.left);
        const distance = Math.abs(slide.getBoundingClientRect().left - trackRect.left);
        return distance < bestDistance ? index : bestIndex;
      }, 0);
      this.planSliderTrack.style.height = `${slides[this.planSliderIndex].scrollHeight}px`;

      const labels = slides.map((slide, index) => slide.getAttribute('aria-label') || `Plan ${index + 1}`);
      this.planResult?.querySelectorAll('[data-plan-slide-to]').forEach((button, index) => {
        const active = index === this.planSliderIndex;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      if (this.planSliderLabel) this.planSliderLabel.textContent = labels[this.planSliderIndex] || `Plan ${this.planSliderIndex + 1}`;
      if (this.planSliderCurrent) this.planSliderCurrent.textContent = String(this.planSliderIndex + 1);
      if (this.planSliderPrev) this.planSliderPrev.disabled = this.planSliderIndex === 0;
      if (this.planSliderNext) this.planSliderNext.disabled = this.planSliderIndex >= slides.length - 1;
    }

    scrollPlanSlider(index) {
      if (!this.planSliderTrack) return;
      const slides = Array.from(this.planSliderTrack.querySelectorAll('[data-plan-slide]'));
      const safeIndex = Math.max(0, Math.min(slides.length - 1, Number(index) || 0));
      const slide = slides[safeIndex];
      if (!slide) return;
      this.planSliderTrack.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      this.planSliderIndex = safeIndex;
      window.setTimeout(() => this.updatePlanSlider(), 280);
    }

    markSelectedAlternative(selectedIndex) {
      this.resultGrid.querySelectorAll('[data-alternative-index]').forEach((card) => {
        const selected = Number(card.dataset.alternativeIndex) === selectedIndex;
        card.classList.toggle('is-plan-selected', selected);
        const button = card.querySelector('[data-show-alternative-plan]');
        if (button) {
          button.setAttribute('aria-pressed', String(selected));
          button.textContent = selected ? '✓ Lösung ausgewählt' : (button.dataset.defaultLabel || 'Alternative auswählen');
        }
      });
    }

    markSelectedRecommended(selected) {
      const card = this.resultGrid.querySelector('[data-recommended-plan-card]');
      if (!card) return;
      card.classList.toggle('is-plan-selected', selected);
      const button = card.querySelector('[data-show-recommended-plan]');
      if (button) {
        button.setAttribute('aria-pressed', String(selected));
        button.textContent = selected ? '✓ Lösung ausgewählt' : 'Lösung auswählen';
      }
    }

    markSelectedPlan(variantId) {
      this.resultGrid.querySelectorAll('[data-result-index]').forEach((card) => {
        const match = this.visibleMatches?.[Number(card.dataset.resultIndex)];
        const selected = variantId != null && String(match?.product.variantId) === String(variantId);
        card.classList.toggle('is-plan-selected', selected);
        const button = card.querySelector('[data-show-area-plan]');
        if (button) {
          button.setAttribute('aria-pressed', String(selected));
          button.textContent = selected ? '✓ Lösung ausgewählt' : 'Lösung auswählen';
        }
      });
    }

    proportionalCanvas(length, width, maxWidth, maxHeight) {
      const safeLength = Math.max(1, Number(length) || 1);
      const safeWidth = Math.max(1, Number(width) || 1);
      const scale = Math.min(Number(maxWidth) / safeLength, Number(maxHeight) / safeWidth);
      return {
        width: safeLength * scale,
        height: safeWidth * scale,
        scale,
      };
    }

    renderMultiPiecePlan(plan, criteria) {
      const palette = ['#ef7d00', '#4c5860', '#7f9a4f', '#397c8e', '#9b6a8f', '#b29342', '#6376a3'];
      const pricing = this.planPricing(plan);
      const requestSummary = (criteria.cuts || [])
        .map((cut) => `${cut.quantity}× ${cut.length}×${cut.width} mm`)
        .join(' · ');
      const demandCards = (criteria.cuts || []).map((cut, index) => `
        <article class="restposten-finder__multi-demand">
          <span>Maß ${index + 1}</span>
          <strong>${cut.length} × ${cut.width} mm</strong>
          <small>${cut.quantity} ${cut.quantity === 1 ? 'Rost' : 'Roste'}</small>
        </article>`).join('');
      const plateCards = plan.placements.map((bin, plateIndex) => {
        const canvas = this.proportionalCanvas(bin.rawLength, bin.rawWidth, 420, 320);
        const canvasWidth = canvas.width;
        const canvasHeight = canvas.height;
        const scaleX = canvas.scale;
        const scaleY = canvas.scale;
        const pieces = bin.pieces.map((piece) => {
          const x = piece.x * scaleX;
          const y = piece.y * scaleY;
          const width = piece.width * scaleX;
          const height = piece.height * scaleY;
          const color = palette[piece.requestIndex % palette.length];
          const label = width > 66 && height > 34
            ? `<text x="${x + width / 2}" y="${y + height / 2 - 2}" text-anchor="middle">M${piece.requestIndex + 1}</text><text class="is-size" x="${x + width / 2}" y="${y + height / 2 + 15}" text-anchor="middle">${piece.baseLength}×${piece.baseWidth}</text>`
            : '';
          return `<g><rect x="${x}" y="${y}" width="${width}" height="${height}" fill="${color}"/><title>Maß ${piece.requestIndex + 1}: ${piece.baseLength}×${piece.baseWidth} mm</title>${label}</g>`;
        }).join('');
        const usedArea = bin.pieces.reduce((sum, piece) => sum + piece.baseLength * piece.baseWidth, 0);
        const restPercent = bin.product.area ? ((bin.product.area - usedArea) / bin.product.area) * 100 : 0;
        const productUrl = this.safeInternalUrl(bin.product.url);
        return `
          <article class="restposten-finder__multi-plate">
            <div class="restposten-finder__multi-plate-head">
              <span>Rohplatte ${plateIndex + 1}</span>
              <strong>${this.escapeHtml(this.productCode(bin.product))} · ${bin.rawLength}×${bin.rawWidth} mm</strong>
            </div>
            <svg viewBox="0 0 ${canvasWidth} ${canvasHeight}" role="img" aria-label="Zuschnittsplan für Rohplatte ${plateIndex + 1}">
              <rect class="is-raw" x="0" y="0" width="${canvasWidth}" height="${canvasHeight}"/>
              ${pieces}
            </svg>
            <dl>
              <div><dt>Fertigteile</dt><dd>${bin.pieces.length}</dd></div>
              <div><dt>Schnitte</dt><dd>${bin.cutCount}</dd></div>
              <div><dt>Rest</dt><dd>${this.formatNumber(restPercent, 1)} %</dd></div>
            </dl>
            <a href="${this.escapeHtml(productUrl)}" target="_blank" rel="noopener">${this.escapeHtml(bin.product.title)} ansehen ↗</a>
          </article>`;
      }).join('');
      const serviceReady = plan.cutCount === 0 || (String(this.config.firstCutVariantId || '') && String(this.config.additionalCutVariantId || ''));
      const orderHint = !serviceReady
        ? 'Diese Lösung kann derzeit nicht direkt bestellt werden. Bitte senden Sie uns Ihre Projektanfrage.'
        : plan.hasUntrackedStock
          ? 'Die verfügbare Menge wird beim Hinzufügen zum Warenkorb noch einmal geprüft.'
          : 'Alle Restposten und berechneten Zuschnitte werden zusammen in den Warenkorb gelegt.';

      this.planResult.hidden = false;
      this.planResult.innerHTML = `
        <div class="restposten-finder__plan-location">
          <div>
            <span>Ausgewählte Bedarfsliste</span>
            <strong>${plan.requestedPieceCount} Roste · ${plan.placements.length} ${plan.placements.length === 1 ? 'Rohplatte' : 'Rohplatten'} · ${plan.distinctProducts} ${plan.distinctProducts === 1 ? 'Posten' : 'Posten'}</strong>
            <small>Bedarfsliste und Zuschnittsplan werden mit einer gemeinsamen Plan-ID in Ihre Bestellung übernommen.</small>
          </div>
          <button type="button" data-back-to-options>↑ Andere Lösung wählen</button>
        </div>
        <article class="restposten-finder__plan">
          <div class="restposten-finder__plan-top">
            <div>
              <p class="restposten-finder__plan-kicker">${plan.stockVerified ? 'LAGERBESTAND GEPRÜFT' : 'AKTUELL VERFÜGBARE LÖSUNG'}</p>
              <h4>${plan.requestedPieceCount} Roste vollständig eingeplant</h4>
              <p>${this.escapeHtml(requestSummary)}. Die Maße werden gemeinsam auf verfügbare Restposten verteilt; mehrere Roste entstehen nach Möglichkeit aus derselben Rohplatte.</p>
            </div>
            <dl>
              <div><dt>Fertigteile</dt><dd>${plan.requestedPieceCount}</dd></div>
              <div><dt>Rohplatten</dt><dd>${plan.placements.length}</dd></div>
              <div><dt>Posten</dt><dd>${plan.distinctProducts}</dd></div>
              <div><dt>Schnitte</dt><dd>${plan.cutCount}</dd></div>
              <div><dt>Gesamtpreis</dt><dd>${pricing.totalPrice == null ? this.formatMoney(plan.totalPrice) : this.formatMoney(pricing.totalPrice)}</dd></div>
            </dl>
          </div>
          ${this.priceBreakdownMarkup(pricing)}
          <div class="restposten-finder__plan-slider" data-plan-slider>
            <div class="restposten-finder__plan-switch" data-plan-slider-switch aria-label="Planansicht auswählen">
              <button type="button" class="is-active" data-plan-slide-to="0" aria-pressed="true"><span>1</span> Bedarfsliste</button>
              <button type="button" data-plan-slide-to="1" aria-pressed="false"><span>2</span> Zuschnittsplan</button>
            </div>
            <div class="restposten-finder__plan-slider-nav" data-plan-slider-nav>
              <button type="button" data-plan-slider-prev disabled aria-label="Vorherigen Plan anzeigen">←</button>
              <p><strong data-plan-slider-label>Bedarfsliste</strong><small><span data-plan-slider-current>1</span> von 2 · wischen oder mit den Pfeilen wechseln</small></p>
              <button type="button" data-plan-slider-next aria-label="Nächsten Plan anzeigen">→</button>
            </div>
            <div class="restposten-finder__plan-slider-track" data-plan-slider-track>
              <section class="restposten-finder__plan-section" data-plan-slide="0" aria-label="Bedarfsliste">
                <div class="restposten-finder__plan-section-head"><span>1</span><div><small>Bedarfsliste</small><h5>Ihre gewünschten Maße und Mengen</h5></div></div>
                <div class="restposten-finder__multi-demand-grid">${demandCards}</div>
              </section>
              <section class="restposten-finder__cut-plan" data-plan-slide="1" aria-label="Zuschnittsplan">
                <div class="restposten-finder__plan-section-head"><span>2</span><div><small>Zuschnittsplan</small><h5>Verteilung auf die Rohplatten</h5></div></div>
                <p class="restposten-finder__cut-intro">Die farbigen Flächen sind Ihre Fertigteile. M1, M2 usw. beziehen sich auf die Nummern der Bedarfsliste.</p>
                <div class="restposten-finder__multi-plate-grid">${plateCards}</div>
              </section>
            </div>
          </div>
          <div class="restposten-finder__plan-actions">
            <button class="button" type="button" data-add-plan-to-cart aria-label="Restposten, Zuschnitt und Plan-PDF in den Warenkorb legen"${serviceReady ? '' : ' disabled'}>Auswahl in den Warenkorb</button>
            <button type="button" data-print-plan>Plan drucken</button>
          </div>
          <p class="restposten-finder__plan-order-hint" data-plan-order-status>${this.escapeHtml(orderHint)}</p>
        </article>`;
      this.setupPlanSlider();
    }

    renderAreaPlan(plan, criteria) {
      if (!plan) {
        this.activePlanCriteria = null;
        this.planResult.hidden = true;
        this.planResult.innerHTML = '';
        return;
      }
      this.activePlanCriteria = criteria;
      if (plan.isMultiPieceOrder) {
        this.renderMultiPiecePlan(plan, criteria);
        return;
      }

      const palette = ['#ef7d00', '#4c5860', '#7f9a4f', '#397c8e', '#9b6a8f', '#b29342', '#6376a3'];
      const splitPiece = criteria.mode === 'piece';
      const nearFitAccepted = splitPiece && plan.acceptedNearFit === true;
      const singlePiece = splitPiece && plan.placements.length === 1;
      const splitFallback = criteria.fallbackFromMaxPieces === 1;
      const reducedArea = criteria.areaFallbackKind === 'reduced-area';
      const narrowStrip = criteria.areaFallbackKind === 'narrow-strip';
      const narrowSizes = Array.isArray(criteria.narrowPieceSizes) ? criteria.narrowPieceSizes : [];
      const narrowSizeLabel = narrowSizes.map((size) => `${size} mm`).join(' / ');
      const areaReduction = {
        lengthReduction: Number(criteria.areaReductionLengthMm || 0),
        widthReduction: Number(criteria.areaReductionWidthMm || 0),
      };
      const directionNote = this.splitDirectionLabel(criteria);
      const areaFallbackNote = reducedArea
        ? `Diese Alternative ist ${this.areaReductionSentence(areaReduction)} kleiner als Ihr Wunschmaß ${criteria.requestedLength} × ${criteria.requestedWidth} mm. Dafür entstehen keine Teilstücke unter ${this.areaMinimumPiece()} mm.`
        : narrowStrip
          ? `Diese Ausnahme hält Ihr Wunschmaß vollständig ein. Der Plan enthält dafür einen ${narrowSizeLabel} breiten Randstreifen unter der empfohlenen Mindestbreite von ${this.areaMinimumPiece()} mm.`
          : '';
      const layoutPlanLabel = nearFitAccepted ? 'Maßvergleich' : singlePiece ? 'Maßplan' : splitPiece ? 'Teilungsplan' : 'Verlegeplan';
      const colorByVariant = new Map(plan.products.map((group, index) => [String(group.product.variantId), palette[index % palette.length]]));
      const canvas = this.proportionalCanvas(plan.targetLength, plan.targetWidth, 720, 440);
      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;
      const scaleX = canvas.scale;
      const scaleY = canvas.scale;

      const rectangles = plan.placements
        .map((placement, index) => {
          const x = placement.x * scaleX;
          const y = placement.y * scaleY;
          const width = placement.width * scaleX;
          const height = placement.height * scaleY;
          const color = colorByVariant.get(String(placement.product.variantId));
          const code = this.productCode(placement.product, plan.products.findIndex((group) => group.product.variantId === placement.product.variantId));
          const label = width > 55 && height > 34
            ? `<text x="${x + width / 2}" y="${y + height / 2 - 3}" text-anchor="middle">${this.escapeHtml(code)}</text><text class="is-size" x="${x + width / 2}" y="${y + height / 2 + 15}" text-anchor="middle">${placement.width}×${placement.height}</text>`
            : '';
          return `<g><title>Position ${index + 1}: ${this.escapeHtml(placement.product.title)}, Zuschnitt ${placement.width}×${placement.height} mm</title><rect x="${x}" y="${y}" width="${width}" height="${height}" fill="${color}"/><path d="M${x} ${y}h${width}v${height}h-${width}Z"/>${label}</g>`;
        })
        .join('');

      const legend = plan.products
        .map((group, index) => {
          const color = colorByVariant.get(String(group.product.variantId));
          const cuts = Array.from(group.cuts.entries()).map(([size, quantity]) => `${quantity}× ${size} mm`).join(', ');
          const productUrl = this.safeInternalUrl(group.product.url);
          const image = group.product.image
            ? `<img src="${this.escapeHtml(group.product.image)}" alt="" loading="lazy" width="96" height="72">`
            : '';
          return `
            <li>
              <a class="restposten-finder__plan-thumb" href="${this.escapeHtml(productUrl)}" target="_blank" rel="noopener" aria-label="${this.escapeHtml(group.product.title)} in neuem Tab öffnen">
                ${image}
                <span class="restposten-finder__plan-swatch" style="--swatch:${color}">${this.escapeHtml(this.productCode(group.product, index))}</span>
              </a>
              <div>
                <a class="restposten-finder__plan-product-title" href="${this.escapeHtml(productUrl)}" target="_blank" rel="noopener"><strong>${group.quantity}× ${this.escapeHtml(group.product.title)}</strong></a>
                <span>Rohmaß ${group.product.length}×${group.product.width} mm → ${this.escapeHtml(cuts)}</span>
                <a class="restposten-finder__plan-product-link" href="${this.escapeHtml(productUrl)}" target="_blank" rel="noopener">Posten ansehen ↗</a>
              </div>
            </li>`;
        })
        .join('');

      const rawPlates = this.rawPlatesForPlan(plan);
      const rawPlateCount = rawPlates.length;
      const cutPlanCards = rawPlates
        .map((plate, index) => {
          const cutCanvas = this.proportionalCanvas(plate.rawLength, plate.rawWidth, 720, 360);
          const svgWidth = cutCanvas.width;
          const svgHeight = cutCanvas.height;
          const scaleX = cutCanvas.scale;
          const scaleY = cutCanvas.scale;
          const rawArea = plate.rawLength * plate.rawWidth;
          const usedArea = plate.pieces.reduce((sum, piece) => sum + piece.width * piece.height, 0);
          const restPercent = rawArea ? Math.max(0, ((rawArea - usedArea) / rawArea) * 100) : 0;
          const code = this.productCode(plate.product, index);
          const color = colorByVariant.get(String(plate.product.variantId));
          const hatchId = `restposten-cut-hatch-${index}`;
          const pieceSummary = new Map();
          const pieceSvg = plate.pieces.map((piece) => {
            const x = piece.x * scaleX;
            const y = piece.y * scaleY;
            const width = piece.width * scaleX;
            const height = piece.height * scaleY;
            const key = `${piece.installedLength}×${piece.installedWidth}`;
            pieceSummary.set(key, (pieceSummary.get(key) || 0) + 1);
            const label = width > 100 && height > 54
              ? `<text x="${x + width / 2}" y="${y + height / 2 - 5}" text-anchor="middle">Teil ${piece.placementIndex + 1}</text><text class="is-size" x="${x + width / 2}" y="${y + height / 2 + 16}" text-anchor="middle">${piece.installedLength}×${piece.installedWidth} mm</text>`
              : '';
            return `<g><title>Teilstück ${piece.placementIndex + 1}: ${piece.installedLength}×${piece.installedWidth} mm</title><rect class="is-piece" x="${x}" y="${y}" width="${width}" height="${height}" fill="${color}"/>${label}</g>`;
          }).join('');
          const finishedSummary = Array.from(pieceSummary.entries()).map(([size, quantity]) => `${quantity}× ${size} mm`).join(', ');
          return `
            <article class="restposten-finder__cut-card">
              <header>
                <div>
                  <span>Rohplatte ${index + 1} von ${rawPlateCount}</span>
                  <strong>1× Restposten ${this.escapeHtml(code)}</strong>
                </div>
                <small>${plate.rawLength}×${plate.rawWidth} mm</small>
              </header>
              <div class="restposten-finder__cut-diagram">
                <div class="restposten-finder__cut-dimension is-length">${plate.rawLength} mm</div>
                <svg viewBox="0 0 ${svgWidth} ${svgHeight}" role="img" aria-label="Zuschnittsplan für Rohplatte ${index + 1} mit ${plate.pieces.length} Fertigteilen">
                  <defs>
                    <pattern id="${hatchId}" width="18" height="18" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                      <rect width="18" height="18" fill="#ece9e4"/>
                      <line x1="0" y1="0" x2="0" y2="18" stroke="#c5c0b8" stroke-width="5"/>
                    </pattern>
                  </defs>
                  <rect class="is-raw" x="0" y="0" width="${svgWidth}" height="${svgHeight}" fill="url(#${hatchId})"/>
                  ${pieceSvg}
                </svg>
                <div class="restposten-finder__cut-dimension is-width">${plate.rawWidth} mm</div>
                <div class="restposten-finder__cut-key"><span><i class="is-part" style="--part-color:${color}"></i>Fertigteil</span><span><i class="is-rest"></i>Rest</span></div>
              </div>
              <dl>
                <div><dt>Fertigteile</dt><dd>${finishedSummary}</dd></div>
                <div><dt>Schnitte</dt><dd>${plate.cutCount ? plate.cutCount : 'Kein Zuschnitt nötig'}</dd></div>
                <div><dt>Rest</dt><dd>${this.formatNumber(restPercent, 1)} %</dd></div>
              </dl>
            </article>`;
        })
        .join('');

      const serviceReady = plan.cutCount === 0 || (String(this.config.firstCutVariantId || '') && String(this.config.additionalCutVariantId || ''));
      const canOrder = serviceReady;
      const pricing = this.planPricing(plan);
      const nearFitComparison = nearFitAccepted
        ? `<div class="restposten-finder__near-plan-note">
            <div><span>Ihr Wunschmaß</span><strong>${plan.requestedLength}×${plan.requestedWidth} mm</strong></div>
            <span aria-hidden="true">→</span>
            <div><span>Liefermaß ohne Zuschnitt</span><strong>${plan.deliveredLength}×${plan.deliveredWidth} mm</strong></div>
            <small>Abweichung: Länge ${this.signedMillimetres(plan.deltaLength)}, Breite ${this.signedMillimetres(plan.deltaWidth)}. Mit Ihrer Auswahl bestätigen Sie dieses Liefermaß.</small>
          </div>`
        : '';
      const orderHint = !serviceReady
          ? 'Diese Lösung kann derzeit nicht direkt bestellt werden. Bitte senden Sie uns Ihre Projektanfrage.'
          : nearFitAccepted
            ? `Sie bestellen den Restposten im Liefermaß ${plan.deliveredLength}×${plan.deliveredWidth} mm ohne Zuschnitt. Die Abweichung zu Ihrem Wunschmaß steht in der Plan-PDF.`
          : plan.hasUntrackedStock
            ? 'Die verfügbare Menge wird beim Hinzufügen zum Warenkorb noch einmal geprüft.'
          : 'Alle Restposten und berechneten Zuschnitte werden zusammen in den Warenkorb gelegt.';

      this.planResult.hidden = false;
      this.planResult.innerHTML = `
        <div class="restposten-finder__plan-location">
          <div>
            <span>${reducedArea ? 'Ausgewählte kleinere Flächenlösung' : narrowStrip ? 'Ausgewählte Ausnahme mit Randstreifen' : nearFitAccepted ? 'Ausgewählte Maßalternative' : splitPiece ? singlePiece ? 'Ausgewählte Maßlösung' : 'Ausgewählte geteilte Maßlösung' : splitFallback ? 'Ausgewählte geteilte Flächenlösung' : 'Ausgewählte Flächenlösung'}</span>
            <strong>${this.escapeHtml(this.surfaceLabel(plan.surface))} · ${this.escapeHtml(this.meshLabel(plan.mesh))} · ${this.escapeHtml(this.formatProductHeight(plan.height, plan.family))} · ${plan.placements.length} ${plan.placements.length === 1 ? 'Teilstück' : 'Teilstücke'}${criteria.maxPieceLength ? ` · max. ${criteria.maxPieceLength} mm` : ''}</strong>
            <small>${directionNote ? `${this.escapeHtml(directionNote)}. ` : ''}${areaFallbackNote ? `${this.escapeHtml(areaFallbackNote)} ` : ''}${splitFallback ? 'Da keine passende Einzelplatte verfügbar ist, wird die kleinste verfügbare Teilung verwendet. ' : ''}${layoutPlanLabel} und Zuschnittsplan werden mit einer gemeinsamen Plan-ID in Ihre Bestellung übernommen.</small>
          </div>
          <button type="button" data-back-to-options>↑ Andere Lösung wählen</button>
        </div>
        <article class="restposten-finder__plan">
          <div class="restposten-finder__plan-top">
            <div>
              <p class="restposten-finder__plan-kicker">${narrowStrip ? 'KLAR GEKENNZEICHNETE AUSNAHME' : reducedArea ? 'ALTERNATIVE OHNE SCHMALEN RANDSTREIFEN' : plan.stockVerified ? 'LAGERBESTAND GEPRÜFT' : 'AKTUELL VERFÜGBARE LÖSUNG'}</p>
              <h4>${reducedArea ? `Ihre Alternative: ${plan.targetLength} × ${plan.targetWidth} mm` : narrowStrip ? `Ihr Wunschmaß mit ${narrowSizeLabel} Randstreifen` : nearFitAccepted ? `Liefermaß ${plan.targetLength} × ${plan.targetWidth} mm ohne Zuschnitt` : splitPiece ? singlePiece ? `Wunschmaß ${plan.targetLength} × ${plan.targetWidth} mm passend aus Restposten` : `${plan.targetLength} × ${plan.targetWidth} mm in ${plan.placements.length} Teilstücke aufgeteilt` : `Ihre Fläche: ${plan.targetLength} × ${plan.targetWidth} mm vollständig belegt`}</h4>
              <p>${areaFallbackNote ? `${this.escapeHtml(areaFallbackNote)} ` : ''}${this.escapeHtml(this.capitalize(plan.color))}, ${this.escapeHtml(this.surfaceLabel(plan.surface))}, ${this.escapeHtml(this.meshLabel(plan.mesh))}, ${this.escapeHtml(this.formatProductHeight(plan.height, plan.family, { detailed: true }))}. ${nearFitAccepted ? `Sie übernehmen bewusst das verfügbare Maß ohne kostenpflichtigen Zuschnitt. ` : ''}${this.escapeHtml(this.planSurfaceExplanation(plan.surface))} ${this.escapeHtml(this.planMeshExplanation(plan))}${splitFallback ? ` Eine einteilige Belegung ist aktuell nicht verfügbar; diese Lösung verwendet die kleinstmögliche verfügbare Teilung mit ${plan.placements.length} Teilstücken.` : criteria.maxPieces ? ` Ihre Vorgabe von maximal ${criteria.maxPieces} ${criteria.maxPieces === 1 ? 'Teilstück' : 'Teilstücken'} wird berücksichtigt.` : ''}${criteria.maxPieceLength ? ` Kein fertiger Rost überschreitet die gewünschte Länge von ${criteria.maxPieceLength} mm.` : ''}</p>
            </div>
            <dl>
              <div><dt>Rohplatten</dt><dd>${rawPlateCount}</dd></div>
              <div><dt>Posten</dt><dd>${plan.distinctProducts}</dd></div>
              <div><dt>Schnitte</dt><dd>${plan.cutCount}</dd></div>
              <div><dt>Verschnitt</dt><dd>${this.formatNumber(plan.wastePercent, 1)} %</dd></div>
              <div><dt>Gesamtpreis</dt><dd>${pricing.totalPrice == null ? this.formatMoney(plan.totalPrice) : this.formatMoney(pricing.totalPrice)}</dd></div>
            </dl>
          </div>
          ${this.priceBreakdownMarkup(pricing)}
          ${nearFitComparison}
          <div class="restposten-finder__plan-slider" data-plan-slider>
            <div class="restposten-finder__plan-switch" data-plan-slider-switch aria-label="Planansicht auswählen">
              <button type="button" class="is-active" data-plan-slide-to="0" aria-pressed="true"><span>1</span> ${layoutPlanLabel}</button>
              <button type="button" data-plan-slide-to="1" aria-pressed="false"><span>2</span> ${nearFitAccepted ? 'Produktmaß' : 'Zuschnittsplan'}</button>
            </div>
            <div class="restposten-finder__plan-slider-nav" data-plan-slider-nav>
              <button type="button" data-plan-slider-prev disabled aria-label="Vorherigen Plan anzeigen">←</button>
              <p><strong data-plan-slider-label>${layoutPlanLabel}</strong><small><span data-plan-slider-current>1</span> von 2 · wischen oder mit den Pfeilen wechseln</small></p>
              <button type="button" data-plan-slider-next aria-label="Nächsten Plan anzeigen">→</button>
            </div>
            <div class="restposten-finder__plan-slider-track" data-plan-slider-track>
          <section class="restposten-finder__plan-section" data-plan-slide="0" aria-label="${layoutPlanLabel}">
            <div class="restposten-finder__plan-section-head">
              <span>1</span>
              <div><small>${layoutPlanLabel}</small><h5>${nearFitAccepted ? 'Gewünschtes und verfügbares Maß' : splitPiece ? singlePiece ? 'Ihr ausgewähltes Fertigmaß' : 'So wird Ihr Wunschmaß aufgeteilt' : 'So wird Ihre Fläche aufgeteilt'}</h5></div>
            </div>
            <div class="restposten-finder__plan-layout">
              <div class="restposten-finder__plan-canvas" style="--plan-canvas-max-width:${Math.ceil(canvasWidth)}px">
                <div class="restposten-finder__plan-dimension is-length">${plan.targetLength} mm</div>
                <svg viewBox="0 0 ${canvasWidth} ${canvasHeight}" role="img" aria-label="Verlegeplan für ${plan.targetLength} mal ${plan.targetWidth} Millimeter">
                  ${rectangles}
                </svg>
                <div class="restposten-finder__plan-dimension is-width">${plan.targetWidth} mm</div>
              </div>
              <div class="restposten-finder__plan-legend">
                <h5>Verwendete Restposten und Zuschnitte</h5>
                <ol>${legend}</ol>
              </div>
            </div>
          </section>
          <section class="restposten-finder__cut-plan" data-plan-slide="1" aria-label="${nearFitAccepted ? 'Produktmaß' : 'Zuschnittsplan'}">
            <div class="restposten-finder__plan-section-head">
              <span>2</span>
              <div><small>${nearFitAccepted ? 'Produktmaß' : 'Zuschnittsplan'}</small><h5>${nearFitAccepted ? 'Verfügbarer Restposten ohne Zuschnitt' : 'Zuschnitte auf den Rohplatten'}</h5></div>
            </div>
            <p class="restposten-finder__cut-intro">${nearFitAccepted ? 'Die Karte zeigt das vollständige Liefermaß des Restpostens. Es wird kein kostenpflichtiger Zuschnitt ausgeführt.' : 'Jede Karte zeigt eine tatsächlich benötigte Rohplatte. Mehrere passende Fertigteile werden gemeinsam aus derselben Platte geschnitten; die schraffierte Fläche bleibt als Reststück erhalten.'}</p>
            <div class="restposten-finder__cut-grid">${cutPlanCards}</div>
          </section>
            </div>
          </div>
          <div class="restposten-finder__plan-actions">
            <button class="button" type="button" data-add-plan-to-cart aria-label="${nearFitAccepted ? 'Restposten im bestätigten Liefermaß mit Maß-PDF in den Warenkorb legen' : 'Restposten, Zuschnitt und Plan-PDF in den Warenkorb legen'}"${canOrder ? '' : ' disabled'}>Auswahl in den Warenkorb</button>
            <button type="button" data-print-plan>Plan drucken</button>
          </div>
          <p class="restposten-finder__plan-order-hint" data-plan-order-status>${this.escapeHtml(orderHint)}</p>
        </article>`;
      this.setupPlanSlider();
    }

    productCode(product, index = 0) {
      return String(product.title || '').match(/\bR\d+[A-Z-]*\b/i)?.[0]?.toUpperCase() || `P${index + 1}`;
    }

    updatePlanVisual(plan) {
      this.visualMetrics.hidden = false;
      if (plan.isMultiPieceOrder) {
        this.visualKicker.textContent = 'Optimierte Bedarfsliste';
        this.visualTitle.textContent = `${plan.requestedPieceCount} Roste aus ${plan.placements.length} ${plan.placements.length === 1 ? 'Rohplatte' : 'Rohplatten'}`;
        this.visualText.textContent = `${this.capitalize(plan.color)}, ${this.surfaceLabel(plan.surface)}, ${this.meshLabel(plan.mesh)}, ${this.formatProductHeight(plan.height, plan.family, { detailed: true })}. Bedarfsliste und Zuschnittsplan sehen Sie nach Auswahl der Lösung.`;
        this.metricRaw.textContent = `${plan.requestedPieceCount} Roste`;
        this.metricSecondaryLabel.textContent = 'Rohplatten';
        this.metricSecondary.textContent = `${plan.placements.length} / ${plan.distinctProducts} Posten`;
        this.metricWaste.textContent = `${this.formatNumber(plan.wastePercent, 1)} %`;
        const first = plan.requestedCuts?.[0] || { length: plan.targetLength, width: plan.targetWidth };
        this.drawingLength.textContent = `${first.length} mm`;
        this.drawingWidth.textContent = `${first.width} mm`;
        this.updateDrawingGeometry(first.length, first.width);
        return;
      }
      const splitPiece = this.lastCriteria?.mode === 'piece';
      const rawPlateCount = this.planRawPlateCount(plan);
      const nearFitAccepted = splitPiece && plan.acceptedNearFit === true;
      this.visualKicker.textContent = nearFitAccepted ? 'Nahe Maßlösung' : splitPiece ? 'Geteilte Maßlösung' : plan.distinctProducts > 1 ? 'Kombinierte Flächenlösung' : 'Passende Flächenlösung';
      this.visualTitle.textContent = nearFitAccepted
        ? `${plan.deliveredLength}×${plan.deliveredWidth} mm ohne Zuschnitt`
        : splitPiece
        ? `${plan.placements.length} Teilstücke für Ihr Wunschmaß`
        : `${rawPlateCount} ${rawPlateCount === 1 ? 'Rohplatte' : 'Rohplatten'} aus ${plan.distinctProducts} ${plan.distinctProducts === 1 ? 'Restposten' : 'Restposten'}`;
      this.visualText.textContent = nearFitAccepted
        ? `Ihr Wunschmaß ${plan.requestedLength}×${plan.requestedWidth} mm liegt innerhalb der Maßtoleranz. Sie übernehmen das verfügbare Liefermaß ohne Zuschnittskosten.`
        : `${this.capitalize(plan.color)}, ${this.surfaceLabel(plan.surface)}, ${this.meshLabel(plan.mesh)}, ${this.formatProductHeight(plan.height, plan.family, { detailed: true })}. ${splitPiece ? 'Den Teilungs- und Zuschnittsplan sehen Sie nach Auswahl der Lösung.' : 'Den Verlege- und Zuschnittsplan sehen Sie nach Auswahl der Lösung.'}`;
      this.metricRaw.textContent = `${plan.targetLength}×${plan.targetWidth} mm`;
      this.metricSecondaryLabel.textContent = 'Zuschnitte';
      this.metricSecondary.textContent = `${plan.cutCount} / ${rawPlateCount} Platten`;
      this.metricWaste.textContent = `${this.formatNumber(plan.wastePercent, 1)} %`;
      this.drawingLength.textContent = `${plan.targetLength} mm`;
      this.drawingWidth.textContent = `${plan.targetWidth} mm`;
      this.updateDrawingGeometry(plan.targetLength, plan.targetWidth);
    }

    pricingFor(materialPrice, cutCount = 0) {
      const material = Number(materialPrice || 0);
      const cuts = Math.max(0, Math.round(Number(cutCount || 0)));
      if (cuts === 0) return { materialPrice: material, cutPrice: 0, totalPrice: material, cutCount: 0 };

      const firstRaw = this.cutServicePrices?.first;
      const additionalRaw = this.cutServicePrices?.additional;
      const first = Number(firstRaw);
      const additional = Number(additionalRaw);
      const firstKnown = firstRaw != null && Number.isFinite(first) && first >= 0;
      const additionalKnown = cuts <= 1 || (additionalRaw != null && Number.isFinite(additional) && additional >= 0);
      if (!firstKnown || !additionalKnown) {
        return { materialPrice: material, cutPrice: null, totalPrice: null, cutCount: cuts };
      }

      const cutPrice = first + Math.max(0, cuts - 1) * additional;
      return { materialPrice: material, cutPrice, totalPrice: material + cutPrice, cutCount: cuts };
    }

    planPricing(plan) {
      return this.pricingFor(plan?.totalPrice, plan?.cutCount);
    }

    planTotalPriceForSort(plan) {
      const pricing = this.planPricing(plan);
      return pricing.totalPrice == null ? pricing.materialPrice : pricing.totalPrice;
    }

    matchAreaMetrics(match) {
      const plan = match?.previewPlan;
      const rawPlateCount = plan ? this.planRawPlateCount(plan) : Number(match?.fit?.quantity || 1);
      const stockSufficient = !match?.product?.inventoryKnown || Number(match.product.inventoryQuantity || 0) >= rawPlateCount;
      return {
        rawPlateCount,
        installedPieceCount: Number(plan?.placements?.length || match?.fit?.quantity || 1),
        cutCount: Number(plan?.cutCount ?? match?.fit?.cuts ?? 0),
        wastePercent: Number(plan?.wastePercent ?? match?.fit?.wastePercent ?? 100),
        stockSufficient,
      };
    }

    matchTotalPrice(match, criteria) {
      if (criteria.mode === 'area' && match.previewPlan) return this.planTotalPriceForSort(match.previewPlan);
      const materialPrice = match.product.price * (criteria.mode === 'area' ? match.fit.quantity : 1);
      const pricing = this.pricingFor(materialPrice, match.fit.nearFit ? 0 : match.fit.cuts);
      return pricing.totalPrice == null ? pricing.materialPrice : pricing.totalPrice;
    }

    priceBreakdownMarkup(pricing, { compact = false } = {}) {
      const cutLabel = pricing.cutCount === 0
        ? 'Kein Zuschnitt nötig'
        : `${pricing.cutCount} ${pricing.cutCount === 1 ? 'Schnitt' : 'Schnitte'}`;
      const cutValue = pricing.cutPrice == null ? 'wird berechnet' : this.formatMoney(pricing.cutPrice);
      const totalValue = pricing.totalPrice == null ? `${this.formatMoney(pricing.materialPrice)} + Zuschnitt` : this.formatMoney(pricing.totalPrice);
      const taxNote = this.config.taxesIncluded === false ? 'zzgl. MwSt.' : 'inkl. MwSt.';
      return `
        <div class="restposten-finder__price-breakdown${compact ? ' is-compact' : ''}">
          <div><span>Material</span><strong>${this.formatMoney(pricing.materialPrice)}</strong></div>
          <b aria-hidden="true">+</b>
          <div><span>Zuschnitt · ${this.escapeHtml(cutLabel)}</span><strong>${this.escapeHtml(cutValue)}</strong></div>
          <b aria-hidden="true">=</b>
          <div class="is-total"><span>Gesamt inkl. Zuschnitt</span><strong>${this.escapeHtml(totalValue)}</strong><small>${taxNote}</small></div>
        </div>`;
    }

    signedMillimetres(value) {
      const number = Number(value || 0);
      return `${number > 0 ? '+' : ''}${number} mm`;
    }

    nearFitPriceMarkup(match) {
      const { product, fit } = match;
      const asIsPricing = this.pricingFor(product.price, 0);
      const exactPricing = fit.canCutExact && fit.exactCuts > 0
        ? this.pricingFor(product.price, fit.exactCuts)
        : null;
      const exactTotal = exactPricing
        ? exactPricing.totalPrice == null ? `${this.formatMoney(product.price)} + Zuschnitt` : this.formatMoney(exactPricing.totalPrice)
        : '';
      return `
        <div class="restposten-finder__near-prices">
          <div class="is-recommended">
            <span>Ohne Zuschnitt</span>
            <strong>${this.formatMoney(asIsPricing.totalPrice)}</strong>
            <small>Liefermaß ${fit.length}×${fit.width} mm</small>
          </div>
          ${exactPricing ? `
            <div>
              <span>Exakt zugeschnitten</span>
              <strong>${this.escapeHtml(exactTotal)}</strong>
              <small>${fit.exactCuts} ${fit.exactCuts === 1 ? 'Schnitt' : 'Schnitte'} inklusive</small>
            </div>` : ''}
        </div>`;
    }

    cardMarkup(match, criteria, index) {
      const { product, fit } = match;
      const areaMetrics = criteria.mode === 'area' ? this.matchAreaMetrics(match) : null;
      const exact = criteria.mode === 'piece' && !fit.nearFit && fit.cuts === 0;
      const badge = criteria.mode === 'area' ? 'Flächenvorschlag' : fit.nearFit ? 'Nahe Maßlösung' : exact ? 'Passt ohne Schnitt' : 'Zuschnitt möglich';
      const secondaryLabel = criteria.mode === 'area' ? 'Raster' : fit.nearFit ? 'Abweichung L/B' : 'Schnitte';
      const secondaryValue = criteria.mode === 'area'
        ? `${fit.columns} × ${fit.rows}`
        : fit.nearFit
          ? `${this.signedMillimetres(fit.deltaLength)} / ${this.signedMillimetres(fit.deltaWidth)}`
          : String(fit.cuts);
      const totalPrice = product.price * (criteria.mode === 'area' ? fit.quantity : 1);
      const pricing = criteria.mode === 'area' && match.previewPlan
        ? this.planPricing(match.previewPlan)
        : this.pricingFor(totalPrice, fit.nearFit ? 0 : fit.cuts);
      const image = product.image
        ? `<img src="${this.escapeHtml(product.image)}" alt="${this.escapeHtml(product.title)}" loading="lazy" width="720" height="405">`
        : '<span class="restposten-finder__card-placeholder" aria-hidden="true">▦</span>';
      const productUrl = this.safeInternalUrl(product.url);
      const nextLabel = this.getNextLabel(match, criteria);
      const fitText = this.getFitText(match, criteria);
      const stockText = this.getStockText(match, criteria);
      const stockClass = (areaMetrics?.stockSufficient ?? fit.stockSufficient) ? '' : ' is-limited';
      const areaAction = fit.cuttable && (areaMetrics?.stockSufficient ?? fit.stockSufficient)
        ? `<button type="button" data-show-area-plan="${index}" aria-pressed="false">Lösung auswählen</button>`
        : `<a href="${this.escapeHtml(this.safeInternalUrl(this.config.contactUrl || '/pages/contact'))}">Verfügbarkeit anfragen</a>`;
      const pieceActions = !fit.stockSufficient
        ? `<a href="${this.escapeHtml(this.safeInternalUrl(this.config.contactUrl || '/pages/contact'))}">Verfügbarkeit anfragen</a>`
        : fit.nearFit
          ? `
            <button type="button" data-select-piece-plan="${index}" data-piece-finish="as-is" aria-pressed="false">Ohne Zuschnitt</button>
            ${fit.canCutExact && fit.exactCuts > 0 ? `<button type="button" data-select-piece-plan="${index}" data-piece-finish="cut" aria-pressed="false">Exakt zuschneiden</button>` : ''}`
          : `<button type="button" data-select-piece-plan="${index}" data-piece-finish="cut" aria-pressed="false">${this.escapeHtml(nextLabel)}</button>`;

      return `
        <article class="restposten-finder__card" data-result-index="${index}" data-variant-id="${this.escapeHtml(product.variantId)}">
          <div class="restposten-finder__card-media">
            ${image}
            <span class="restposten-finder__card-badge">${this.escapeHtml(badge)}</span>
            <span class="restposten-finder__card-rank" aria-label="Empfehlung ${index + 1}">${index + 1}</span>
          </div>
          <div class="restposten-finder__card-body">
            <div class="restposten-finder__card-meta">
              <span>${this.escapeHtml(this.capitalize(product.color))}</span>
              <span>${this.escapeHtml(this.surfaceLabel(product.surface))}</span>
              <span>${this.escapeHtml(this.meshLabel(product.mesh))}</span>
              ${product.height ? `<span>${this.escapeHtml(this.formatProductHeight(product.height, product.family))}</span>` : ''}
            </div>
            <h4>${this.escapeHtml(product.title)}</h4>
            <p class="restposten-finder__card-fit">${this.escapeHtml(fitText)}</p>
            <dl class="restposten-finder__card-facts">
              <div><dt>Rohmaß</dt><dd>${product.length}×${product.width}</dd></div>
              <div><dt>${secondaryLabel}</dt><dd>${secondaryValue}</dd></div>
              <div><dt>${criteria.mode === 'area' ? 'Rohplatten' : fit.nearFit ? 'Liefermaß' : 'Fertigmaß'}</dt><dd>${criteria.mode === 'area' ? `${areaMetrics.rawPlateCount} Stk.` : fit.nearFit ? `${fit.length}×${fit.width}` : `${criteria.length}×${criteria.width}`}</dd></div>
            </dl>
            ${fit.nearFit && criteria.mode === 'piece' ? this.nearFitPriceMarkup(match) : this.priceBreakdownMarkup(pricing, { compact: true })}
            <p class="restposten-finder__card-stock${stockClass}">${this.escapeHtml(stockText)}</p>
            <div class="restposten-finder__card-actions${fit.nearFit && criteria.mode === 'piece' ? ' is-near-fit' : ''}">
              <a href="${this.escapeHtml(productUrl)}" target="_blank" rel="noopener">Restposten ansehen ↗</a>
              ${criteria.mode === 'area' ? areaAction : pieceActions}
            </div>
          </div>
        </article>
      `;
    }

    getFitText(match, criteria) {
      const { product, fit } = match;
      if (criteria.mode === 'area') {
        const metrics = this.matchAreaMetrics(match);
        const rotation = fit.rotated ? 'gedreht ' : '';
        if (metrics.rawPlateCount < metrics.installedPieceCount) {
          return `Ihre Fläche wird in ${metrics.installedPieceCount} Teilstücke aufgeteilt. Dafür werden nur ${metrics.rawPlateCount} ${rotation}${product.length}×${product.width}-mm-Rohplatten benötigt, weil passende Randstücke gemeinsam aus einer Platte geschnitten werden.`;
        }
        return `Für Ihre Fläche werden ${metrics.rawPlateCount} ${rotation}${product.length}×${product.width}-mm-Rohplatten benötigt. Passende Randstücke werden zugeschnitten.`;
      }
      if (fit.nearFit) {
        const difference = `Länge ${this.signedMillimetres(fit.deltaLength)}, Breite ${this.signedMillimetres(fit.deltaWidth)}`;
        if (fit.canCutExact) {
          return `Das verfügbare Maß ${fit.length}×${fit.width} mm liegt nur geringfügig neben Ihrem Wunschmaß (${difference}). Sie können den Rost ohne Zuschnitt übernehmen oder exakt zuschneiden lassen.`;
        }
        return `Das verfügbare Maß ${fit.length}×${fit.width} mm ist nur geringfügig kleiner als Ihr Wunschmaß (${difference}). Wenn das für Ihr Projekt passt, können Sie den Rost ohne Zuschnitt übernehmen.`;
      }
      if (fit.cuts === 0) return 'Das Rohmaß entspricht Ihrem Wunschmaß. Ein Zuschnitt ist nicht erforderlich.';
      return `Aus dieser Rohplatte kann Ihr Wunschmaß ${criteria.length}×${criteria.width} mm mit ${fit.cuts} ${fit.cuts === 1 ? 'Schnitt' : 'Schnitten'} gefertigt werden.`;
    }

    getStockText(match, criteria) {
      const { product, fit } = match;
      if (product.inventoryKnown) {
        const metrics = criteria.mode === 'area' ? this.matchAreaMetrics(match) : null;
        if (criteria.mode === 'area' && !metrics.stockSufficient) {
          return `Benötigt: ${metrics.rawPlateCount} Stück. Aktuell verfügbar: ${product.inventoryQuantity} Stück. Bitte fragen Sie die fehlende Menge an.`;
        }
        return `Aktuell verfügbar: ${product.inventoryQuantity} Stück.`;
      }
      return criteria.mode === 'area'
        ? 'Die benötigte Stückzahl wird beim Auswählen noch einmal geprüft.'
        : 'Die Verfügbarkeit wird beim Auswählen noch einmal geprüft.';
    }

    getNextLabel(match, criteria) {
      if (!match.fit.cuttable || !match.fit.stockSufficient) return 'Verfügbarkeit anfragen';
      if (criteria.mode === 'area') return 'Flächenplan übernehmen';
      return match.fit.cuts ? 'Mit Zuschnitt auswählen' : 'Restposten auswählen';
    }

    async ensurePdfLibrary() {
      if (window.PDFLib?.PDFDocument) return window.PDFLib;
      if (this.pdfLibraryPromise) return this.pdfLibraryPromise;
      this.pdfLibraryPromise = new Promise((resolve, reject) => {
        const existing = document.querySelector('script[data-restposten-pdf-lib]');
        if (existing) {
          existing.addEventListener('load', () => resolve(window.PDFLib), { once: true });
          existing.addEventListener('error', () => reject(new Error('Die PDF-Funktion konnte nicht geladen werden.')), { once: true });
          return;
        }
        const script = document.createElement('script');
        script.src = this.config.pdfLibUrl;
        script.async = true;
        script.dataset.restpostenPdfLib = '';
        script.onload = () => window.PDFLib?.PDFDocument
          ? resolve(window.PDFLib)
          : reject(new Error('Die PDF-Funktion ist nicht verfügbar.'));
        script.onerror = () => reject(new Error('Die PDF-Funktion konnte nicht geladen werden.'));
        document.head.appendChild(script);
      });
      return this.pdfLibraryPromise;
    }

    async createPlanPdf(plan, orderId) {
      const { PDFDocument, StandardFonts, rgb } = await this.ensurePdfLibrary();
      const pdf = await PDFDocument.create();
      const regular = await pdf.embedFont(StandardFonts.Helvetica);
      const bold = await pdf.embedFont(StandardFonts.HelveticaBold);
      const criteria = this.activePlanCriteria || this.lastCriteria || {};
      const multiPieceOrder = plan.isMultiPieceOrder === true;
      const rawPlates = this.rawPlatesForPlan(plan);
      const rawPlateCount = rawPlates.length;
      const pricing = this.planPricing(plan);
      const pageWidth = 595.28;
      const pageHeight = 841.89;
      const margin = 42;
      const palette = ['#ef7d00', '#4c5860', '#7f9a4f', '#397c8e', '#9b6a8f', '#b29342', '#6376a3'];
      const colors = {
        orange: '#ef7d00',
        ink: '#303b42',
        muted: '#68747b',
        line: '#d7dadd',
        light: '#f3f1ed',
        white: '#ffffff',
      };

      const safe = (value) => String(value ?? '')
        .replace(/[\r\n]+/g, ' ')
        .replace(/\s+/g, ' ')
        .replace(/ẞ/g, 'SS')
        .replace(/[→←↑↓]/g, (character) => ({ '→': '->', '←': '<-', '↑': '^', '↓': 'v' }[character]))
        .replace(/[–—]/g, '-')
        .replace(/…/g, '...')
        .trim();
      const color = (hex) => {
        const source = String(hex || '#000000').replace('#', '');
        const full = source.length === 3 ? source.split('').map((part) => part + part).join('') : source;
        return rgb(
          parseInt(full.slice(0, 2), 16) / 255,
          parseInt(full.slice(2, 4), 16) / 255,
          parseInt(full.slice(4, 6), 16) / 255,
        );
      };
      const yFromTop = (top, height = 0) => pageHeight - top - height;
      const rect = (page, x, top, width, height, options = {}) => {
        page.drawRectangle({
          x,
          y: yFromTop(top, height),
          width,
          height,
          ...(options.fill ? { color: color(options.fill) } : {}),
          ...(options.stroke ? { borderColor: color(options.stroke), borderWidth: options.strokeWidth || 0.8 } : {}),
        });
      };
      const line = (page, x1, top1, x2, top2, stroke = colors.line, thickness = 0.8) => {
        page.drawLine({
          start: { x: x1, y: yFromTop(top1) },
          end: { x: x2, y: yFromTop(top2) },
          color: color(stroke),
          thickness,
        });
      };
      const text = (page, value, x, top, size = 10, options = {}) => {
        page.drawText(safe(value), {
          x,
          y: yFromTop(top, size),
          size,
          font: options.bold ? bold : regular,
          color: color(options.color || colors.ink),
        });
      };
      const wrap = (value, font, size, maxWidth) => {
        const words = safe(value).split(' ').filter(Boolean);
        const rows = [];
        let row = '';
        words.forEach((word) => {
          const next = row ? `${row} ${word}` : word;
          if (row && font.widthOfTextAtSize(next, size) > maxWidth) {
            rows.push(row);
            row = word;
          } else {
            row = next;
          }
        });
        if (row) rows.push(row);
        return rows.length ? rows : [''];
      };
      const wrappedText = (page, value, x, top, maxWidth, size = 10, options = {}) => {
        const font = options.bold ? bold : regular;
        const leading = options.leading || size * 1.35;
        const rows = wrap(value, font, size, maxWidth);
        rows.forEach((row, index) => text(page, row, x, top + index * leading, size, options));
        return top + rows.length * leading;
      };
      const header = (page, title, kicker) => {
        rect(page, 0, 0, pageWidth, 62, { fill: colors.ink });
        text(page, 'FIBROLUX', margin, 17, 20, { bold: true, color: colors.orange });
        text(page, 'RESTPOSTEN-FINDER', margin + 116, 24, 8, { bold: true, color: colors.white });
        text(page, kicker, margin, 82, 8, { bold: true, color: colors.orange });
        text(page, title, margin, 96, 22, { bold: true });
        text(page, `Plan-ID ${orderId}`, pageWidth - margin - 142, 91, 8, { bold: true, color: colors.muted });
      };
      const metric = (page, label, value, x, top, width) => {
        rect(page, x, top, width, 50, { fill: colors.white, stroke: colors.line });
        text(page, label.toUpperCase(), x + 10, top + 9, 7, { bold: true, color: colors.muted });
        text(page, value, x + 10, top + 25, 11.5, { bold: true });
      };

      const nearFitAccepted = plan.acceptedNearFit === true;
      const reducedArea = criteria.areaFallbackKind === 'reduced-area';
      const narrowStrip = criteria.areaFallbackKind === 'narrow-strip';
      const narrowSizeLabel = (criteria.narrowPieceSizes || []).map((size) => `${size} mm`).join(' / ');
      const requestSummary = multiPieceOrder
        ? (plan.requestedCuts || []).map((cut) => `${cut.quantity}× ${cut.length}×${cut.width} mm`).join(' · ')
        : nearFitAccepted
          ? `Wunschmaß ${plan.requestedLength}×${plan.requestedWidth} mm · Liefermaß ${plan.deliveredLength}×${plan.deliveredWidth} mm ohne Zuschnitt`
          : reducedArea
            ? `Wunschmaß ${criteria.requestedLength}×${criteria.requestedWidth} mm · ausgewählt ${plan.targetLength}×${plan.targetWidth} mm`
            : narrowStrip
              ? `Wunschmaß ${plan.targetLength}×${plan.targetWidth} mm · Ausnahme mit ${narrowSizeLabel} Randstreifen`
              : `${plan.targetLength}×${plan.targetWidth} mm`;
      const layoutLabel = multiPieceOrder ? 'Bedarfsliste' : nearFitAccepted ? 'Maßvergleich' : criteria.mode === 'piece' ? 'Teilungsplan' : 'Verlegeplan';
      const totalPrice = pricing.totalPrice == null ? this.formatMoney(plan.totalPrice) : this.formatMoney(pricing.totalPrice);

      pdf.setTitle(`FIBROLUX ${layoutLabel} und Zuschnittsplan ${orderId}`);
      pdf.setSubject('Restposten-Verlege- und Zuschnittsplan');
      pdf.setKeywords(['FIBROLUX', 'Restposten', 'Gitterrost', 'Verlegeplan', 'Zuschnittsplan', orderId]);
      pdf.setProducer('FIBROLUX Restposten-Finder');
      pdf.setCreator('FIBROLUX Onlineshop');
      pdf.setCreationDate(new Date());

      const overview = pdf.addPage([pageWidth, pageHeight]);
      rect(overview, 0, 0, pageWidth, 124, { fill: colors.ink });
      text(overview, 'FIBROLUX', margin, 22, 22, { bold: true, color: colors.orange });
      text(overview, 'RESTPOSTEN-FINDER', margin + 128, 29, 8, { bold: true, color: colors.white });
      text(overview, nearFitAccepted ? 'MASSVERGLEICH UND PRODUKTPLAN' : 'VERLEGE- UND ZUSCHNITTSPLAN', margin, 69, 8, { bold: true, color: colors.orange });
      text(overview, multiPieceOrder ? 'Ihre Bedarfsliste als Fertigungsplan' : nearFitAccepted ? 'Ihre Maßalternative ohne Zuschnitt' : 'Ihre ausgewählte Restposten-Lösung', margin, 84, 19, { bold: true, color: colors.white });
      text(overview, `Plan-ID ${orderId}`, pageWidth - margin - 142, 29, 8, { bold: true, color: colors.white });

      text(overview, 'AUFTRAGSÜBERSICHT', margin, 149, 8, { bold: true, color: colors.orange });
      wrappedText(overview, requestSummary, margin, 164, pageWidth - margin * 2, 17, { bold: true, leading: 21 });
      wrappedText(
        overview,
        `${this.capitalize(plan.color)} · ${this.surfaceLabel(plan.surface)} · ${this.meshLabel(plan.mesh)} · ${this.formatProductHeight(plan.height, plan.family, { detailed: true })}${criteria.maxPieceLength ? ` · max. Teilstücklänge ${criteria.maxPieceLength} mm` : ''}${reducedArea ? ` · ${this.areaReductionText({ lengthReduction: criteria.areaReductionLengthMm, widthReduction: criteria.areaReductionWidthMm }, ', ')}, keine Teilstücke unter ${this.areaMinimumPiece()} mm` : narrowStrip ? ` · bewusst gewählter ${narrowSizeLabel} Randstreifen` : ''}`,
        margin,
        205,
        pageWidth - margin * 2,
        10,
        { color: colors.muted, leading: 14 },
      );

      const metricWidth = (pageWidth - margin * 2 - 16) / 3;
      metric(overview, multiPieceOrder ? 'Fertigteile' : 'Rohplatten', multiPieceOrder ? String(plan.requestedPieceCount) : String(rawPlateCount), margin, 248, metricWidth);
      metric(overview, 'Posten / Schnitte', `${plan.distinctProducts} / ${plan.cutCount}`, margin + metricWidth + 8, 248, metricWidth);
      metric(overview, 'Gesamt inkl. Zuschnitt', totalPrice, margin + (metricWidth + 8) * 2, 248, metricWidth);

      rect(overview, margin, 318, pageWidth - margin * 2, 86, { fill: colors.light });
      text(overview, 'PREISÜBERSICHT', margin + 14, 332, 8, { bold: true, color: colors.orange });
      text(overview, 'Material', margin + 14, 353, 9, { color: colors.muted });
      text(overview, this.formatMoney(pricing.materialPrice), margin + 14, 368, 12, { bold: true });
      text(overview, 'Zuschnitt', margin + 175, 353, 9, { color: colors.muted });
      text(overview, pricing.cutPrice == null ? 'wird berechnet' : this.formatMoney(pricing.cutPrice), margin + 175, 368, 12, { bold: true });
      text(overview, 'Gesamt', margin + 348, 353, 9, { color: colors.muted });
      text(overview, totalPrice, margin + 348, 368, 14, { bold: true, color: colors.orange });

      text(overview, 'VERWENDETE RESTPOSTEN', margin, 434, 8, { bold: true, color: colors.orange });
      let productTop = 451;
      plan.products.slice(0, 7).forEach((group, index) => {
        const code = this.productCode(group.product, index);
        const cutSummary = Array.from(group.cuts.entries()).map(([size, quantity]) => `${quantity}× ${size} mm`).join(', ');
        rect(overview, margin, productTop, 34, 22, { fill: palette[index % palette.length] });
        text(overview, code, margin + 5, productTop + 7, 8, { bold: true, color: colors.white });
        const title = `${group.quantity}× ${group.product.title}`;
        text(overview, title.length > 70 ? `${title.slice(0, 67)}...` : title, margin + 44, productTop + 1, 8.5, { bold: true });
        text(overview, `Rohmaß ${group.product.length}×${group.product.width} mm · ${cutSummary}`, margin + 44, productTop + 13, 7, { color: colors.muted });
        productTop += 34;
      });
      if (plan.products.length > 7) {
        text(overview, `+ ${plan.products.length - 7} weitere Restposten - vollständig im Zuschnittsplan`, margin + 44, productTop + 2, 8, { bold: true, color: colors.muted });
      }

      rect(overview, margin, 747, pageWidth - margin * 2, 42, { fill: '#fff7ef', stroke: colors.orange });
      wrappedText(
        overview,
        'Wichtiger Hinweis: Dieser Plan bildet die geometrische Aufteilung und die bestellten Zuschnitte ab. Auflager, Tragfähigkeit, Laufrichtung, Fugen, Befestigung und Einbausituation müssen vor der Fertigung technisch geprüft werden.',
        margin + 12,
        757,
        pageWidth - margin * 2 - 24,
        7.2,
        { color: colors.muted, leading: 10 },
      );

      const layoutPage = pdf.addPage([pageWidth, pageHeight]);
      header(layoutPage, multiPieceOrder ? 'Bedarfsliste' : layoutLabel, multiPieceOrder ? 'IHRE WUNSCHMASSE' : 'VERLEGEPLAN');
      if (this.splitDirectionLabel(criteria)) {
        text(layoutPage, this.splitDirectionLabel(criteria), margin, 132, 9, { bold: true });
      }
      if (multiPieceOrder) {
        text(layoutPage, 'Gewünschte Fertigteile', margin, 144, 13, { bold: true });
        let rowTop = 170;
        (plan.requestedCuts || []).slice(0, 12).forEach((cut, index) => {
          rect(layoutPage, margin, rowTop, pageWidth - margin * 2, 38, { fill: index % 2 ? colors.white : colors.light, stroke: colors.line });
          rect(layoutPage, margin + 9, rowTop + 8, 22, 22, { fill: palette[index % palette.length] });
          text(layoutPage, `M${index + 1}`, margin + 14, rowTop + 14, 8, { bold: true, color: colors.white });
          text(layoutPage, `${cut.length} × ${cut.width} mm`, margin + 44, rowTop + 10, 11, { bold: true });
          text(layoutPage, `${cut.quantity} ${cut.quantity === 1 ? 'Rost' : 'Roste'}`, pageWidth - margin - 74, rowTop + 11, 9, { bold: true, color: colors.muted });
          rowTop += 42;
        });
        const explanationTop = Math.min(690, rowTop + 24);
        rect(layoutPage, margin, explanationTop, pageWidth - margin * 2, 70, { fill: colors.light });
        text(layoutPage, 'SO LESEN SIE DEN ZUSCHNITTSPLAN', margin + 14, explanationTop + 14, 8, { bold: true, color: colors.orange });
        wrappedText(layoutPage, 'Auf den folgenden Seiten ist jede verwendete Rohplatte einzeln dargestellt. M1, M2 usw. entsprechen den Nummern dieser Bedarfsliste. Hellgraue Flächen bleiben als Reststück erhalten.', margin + 14, explanationTop + 32, pageWidth - margin * 2 - 28, 9, { color: colors.muted, leading: 13 });
      } else {
        const diagramTop = 148;
        const maxDiagramWidth = pageWidth - margin * 2;
        const maxDiagramHeight = 360;
        const scale = Math.min(maxDiagramWidth / plan.targetLength, maxDiagramHeight / plan.targetWidth);
        const diagramWidth = plan.targetLength * scale;
        const diagramHeight = plan.targetWidth * scale;
        const diagramX = margin + (maxDiagramWidth - diagramWidth) / 2;
        text(layoutPage, `${plan.targetLength} mm`, diagramX, diagramTop - 18, 8, { bold: true, color: colors.muted });
        rect(layoutPage, diagramX, diagramTop, diagramWidth, diagramHeight, { fill: '#f7f7f5', stroke: colors.ink, strokeWidth: 1 });
        plan.placements.forEach((placement) => {
          const productIndex = Math.max(0, plan.products.findIndex((group) => String(group.product.variantId) === String(placement.product.variantId)));
          const x = diagramX + (placement.x / plan.targetLength) * diagramWidth;
          const top = diagramTop + (placement.y / plan.targetWidth) * diagramHeight;
          const width = (placement.width / plan.targetLength) * diagramWidth;
          const height = (placement.height / plan.targetWidth) * diagramHeight;
          rect(layoutPage, x, top, width, height, { fill: palette[productIndex % palette.length], stroke: colors.white, strokeWidth: 1.2 });
          if (width > 44 && height > 28) {
            text(layoutPage, this.productCode(placement.product, productIndex), x + 5, top + 7, 8, { bold: true, color: colors.white });
            text(layoutPage, `${placement.width}×${placement.height}`, x + 5, top + 18, 6.5, { bold: true, color: colors.white });
          }
        });
        text(layoutPage, `${plan.targetWidth} mm`, diagramX + diagramWidth + 8, diagramTop + diagramHeight / 2, 8, { bold: true, color: colors.muted });
        let legendTop = diagramTop + diagramHeight + 30;
        text(layoutPage, 'Verwendete Restposten', margin, legendTop, 11, { bold: true });
        legendTop += 22;
        plan.products.slice(0, 8).forEach((group, index) => {
          rect(layoutPage, margin, legendTop, 34, 18, { fill: palette[index % palette.length] });
          text(layoutPage, this.productCode(group.product, index), margin + 5, legendTop + 5, 7, { bold: true, color: colors.white });
          const title = `${group.quantity}× ${group.product.title}`;
          text(layoutPage, title.length > 74 ? `${title.slice(0, 71)}...` : title, margin + 44, legendTop + 3, 8, { bold: true });
          legendTop += 23;
        });
      }

      const drawCutCard = (page, placement, plateIndex, top) => {
        const product = placement.product;
        const rawLength = Number(placement.rawLength || product.length);
        const rawWidth = Number(placement.rawWidth || product.width);
        const cardHeight = 305;
        rect(page, margin, top, pageWidth - margin * 2, cardHeight, { fill: colors.white, stroke: colors.line });
        text(page, `ROHPLATTE ${plateIndex + 1}`, margin + 12, top + 13, 7, { bold: true, color: colors.orange });
        text(page, `${this.productCode(product, plateIndex)} · ${rawLength}×${rawWidth} mm`, margin + 12, top + 28, 11, { bold: true });
        const maxWidth = pageWidth - margin * 2 - 28;
        const maxHeight = 188;
        const scale = Math.min(maxWidth / rawLength, maxHeight / rawWidth);
        const drawingWidth = rawLength * scale;
        const drawingHeight = rawWidth * scale;
        const drawingX = margin + 14 + (maxWidth - drawingWidth) / 2;
        const drawingTop = top + 54;
        rect(page, drawingX, drawingTop, drawingWidth, drawingHeight, { fill: colors.light, stroke: colors.ink });

        const platePieces = placement.pieces || [];
        if (platePieces.length) {
          platePieces.forEach((piece) => {
            const x = drawingX + (piece.x / rawLength) * drawingWidth;
            const pieceTop = drawingTop + (piece.y / rawWidth) * drawingHeight;
            const width = (piece.width / rawLength) * drawingWidth;
            const height = (piece.height / rawWidth) * drawingHeight;
            const productIndex = Math.max(0, plan.products.findIndex((group) => String(group.product.variantId) === String(product.variantId)));
            const pieceColor = multiPieceOrder ? palette[piece.requestIndex % palette.length] : palette[productIndex % palette.length];
            rect(page, x, pieceTop, width, height, { fill: pieceColor, stroke: colors.white, strokeWidth: 1 });
            if (width > 42 && height > 24) {
              text(page, multiPieceOrder ? `M${piece.requestIndex + 1}` : `Teil ${piece.placementIndex + 1}`, x + 5, pieceTop + 6, 8, { bold: true, color: colors.white });
              text(page, `${piece.baseLength}×${piece.baseWidth}`, x + 5, pieceTop + 17, 6.3, { bold: true, color: colors.white });
            }
          });
        } else {
          const cutLength = placement.rotated ? placement.height : placement.width;
          const cutWidth = placement.rotated ? placement.width : placement.height;
          const productIndex = Math.max(0, plan.products.findIndex((group) => String(group.product.variantId) === String(product.variantId)));
          const pieceWidth = Math.min(drawingWidth, (cutLength / rawLength) * drawingWidth);
          const pieceHeight = Math.min(drawingHeight, (cutWidth / rawWidth) * drawingHeight);
          rect(page, drawingX, drawingTop, pieceWidth, pieceHeight, { fill: palette[productIndex % palette.length], stroke: colors.white, strokeWidth: 1 });
          if (pieceWidth > 58 && pieceHeight > 28) {
            text(page, this.productCode(product, productIndex), drawingX + 6, drawingTop + 7, 8, { bold: true, color: colors.white });
            text(page, `${placement.width}×${placement.height} mm`, drawingX + 6, drawingTop + 18, 6.5, { bold: true, color: colors.white });
          }
        }

        const usedArea = platePieces.length
          ? platePieces.reduce((sum, piece) => sum + piece.width * piece.height, 0)
          : placement.width * placement.height;
        const rawArea = rawLength * rawWidth;
        const restPercent = rawArea ? Math.max(0, ((rawArea - usedArea) / rawArea) * 100) : 0;
        const infoTop = drawingTop + drawingHeight + 18;
        text(page, platePieces.length ? `${platePieces.length} Fertigteile` : `Fertigteil ${placement.width}×${placement.height} mm`, margin + 14, infoTop, 8, { bold: true });
        text(page, `${Number(placement.cutCount ?? placement.cuts ?? 0)} Schnitte`, margin + 210, infoTop, 8, { bold: true });
        text(page, `${this.formatNumber(restPercent, 1)} % Restfläche`, margin + 350, infoTop, 8, { bold: true });
        const title = safe(product.title);
        text(page, title.length > 88 ? `${title.slice(0, 85)}...` : title, margin + 14, top + cardHeight - 24, 7.2, { color: colors.muted });
      };

      const cutPlanPlates = multiPieceOrder ? plan.placements : rawPlates;
      cutPlanPlates.forEach((placement, index) => {
        if (index % 2 === 0) {
          const page = pdf.addPage([pageWidth, pageHeight]);
          header(page, 'Zuschnittsplan', 'VERTEILUNG AUF DIE ROHPLATTEN');
        }
        drawCutCard(pdf.getPages()[pdf.getPageCount() - 1], placement, index, index % 2 === 0 ? 136 : 456);
      });

      pdf.getPages().forEach((page, index, pages) => {
        line(page, margin, 808, pageWidth - margin, 808, colors.line, 0.7);
        text(page, `Plan-ID ${orderId}`, margin, 816, 7, { color: colors.muted });
        text(page, `Seite ${index + 1} von ${pages.length}`, pageWidth - margin - 58, 816, 7, { color: colors.muted });
      });

      return new Blob([await pdf.save()], { type: 'application/pdf' });
    }

    async addPlanToCartLegacy(button) {
      const plan = this.activePlan;
      if (!plan || button.disabled) return;
      const status = this.querySelector('[data-plan-order-status]');
      const orderId = `RF-${Date.now().toString(36).toUpperCase()}`;
      const multiPieceOrder = plan.isMultiPieceOrder === true;
      const nearFitAccepted = plan.acceptedNearFit === true;
      const requestSummary = multiPieceOrder
        ? (plan.requestedCuts || []).map((cut) => `${cut.quantity}× ${cut.length}×${cut.width} mm`).join(' · ')
        : nearFitAccepted
          ? `Wunsch ${plan.requestedLength}×${plan.requestedWidth} mm · Liefermaß ${plan.deliveredLength}×${plan.deliveredWidth} mm`
          : `${plan.targetLength}×${plan.targetWidth} mm`;
      const projectSize = multiPieceOrder ? `${plan.requestedPieceCount} Roste` : requestSummary;
      const planSummary = `${this.planRawPlateCount(plan)} Rohplatten / ${plan.distinctProducts} Posten / ${plan.cutCount} Schnitte`;
      const positionRecords = plan.placements.map((placement, index) => ({
        variantId: String(placement.product.variantId),
        text: multiPieceOrder
          ? `Rohplatte ${index + 1} ${this.productCode(placement.product)}: ${placement.pieces.map((piece) => `M${piece.requestIndex + 1} ${piece.baseLength}×${piece.baseWidth} mm bei x${piece.x}/y${piece.y}`).join(', ')}`
          : `P${index + 1} ${this.productCode(placement.product)}: x${placement.x}/y${placement.y}, ${placement.width}×${placement.height} mm`,
      }));
      const completePlanText = positionRecords.map((record) => record.text).join(' | ');
      const cutRecords = plan.placements.map((placement, index) => {
        if (multiPieceOrder) {
          return `Rohplatte ${index + 1} ${this.productCode(placement.product)} ${placement.product.length}×${placement.product.width} mm → ${placement.pieces.map((piece) => `M${piece.requestIndex + 1} ${piece.baseLength}×${piece.baseWidth} mm`).join(', ')}, ${placement.cutCount} ${placement.cutCount === 1 ? 'Schnitt' : 'Schnitte'}`;
        }
        const rotation = placement.rotated ? ' (gedreht)' : '';
        return `P${index + 1} ${this.productCode(placement.product)}: Rohplatte ${placement.product.length}×${placement.product.width} mm → Fertigteil ${placement.width}×${placement.height} mm${rotation}, ${placement.cuts} ${placement.cuts === 1 ? 'Schnitt' : 'Schnitte'}`;
      });
      const completeCutPlanText = cutRecords.join(' | ');
      const items = plan.products.map((group) => {
        const groupCutPlan = Array.from(group.cuts.entries()).map(([size, quantity]) => `${quantity}× ${size} mm`).join(', ');
        const properties = {
          Zuschnittauftrag: orderId,
          'Plan-ID': orderId,
          ...(multiPieceOrder ? { Bestellmaße: requestSummary, Planart: 'Mehrfachzuschnitt' } : { Projektfläche: projectSize }),
          Rohmaß: `${group.product.length}×${group.product.width} mm`,
          Wunschmaß: groupCutPlan,
          Gesamthöhe: this.formatProductHeight(group.product.height, group.product.family),
          Oberfläche: this.surfaceLabel(group.product.surface),
          Maschenweite: this.meshLabel(group.product.mesh),
          Zuschnittsplan: `${group.product.length}×${group.product.width} mm → ${groupCutPlan}`,
          [multiPieceOrder ? 'Bedarfsplan' : 'Verlegeplan']: planSummary,
          Konfigurator: multiPieceOrder ? 'Restposten Bedarf- und Zuschnittsplan' : 'Restposten Verlege- und Zuschnittsplan',
          _cut_mode: 'gitterrost',
          _restposten_plan_id: orderId,
          _restposten_plan_role: 'material',
        };
        const groupPositions = positionRecords
          .filter((record) => record.variantId === String(group.product.variantId))
          .map((record) => record.text)
          .join(' | ');
        this.addChunkedValues(properties, 'Verlegepositionen', groupPositions);
        return {
          id: Number(group.product.variantId),
          quantity: group.quantity,
          properties,
        };
      });

      if (plan.cutCount > 0) {
        const serviceProperties = {
          Zuschnittauftrag: orderId,
          'Plan-ID': orderId,
          'Gehört zu': planSummary,
          ...(multiPieceOrder ? { Bestellmaße: requestSummary, Planart: 'Mehrfachzuschnitt' } : { Projektfläche: projectSize }),
          Zuschnittsplan: planSummary,
          Konfigurator: multiPieceOrder ? 'Restposten Bedarf- und Zuschnittsplan' : 'Restposten Verlege- und Zuschnittsplan',
          _cut_mode: 'gitterrost',
          _restposten_plan_id: orderId,
          _restposten_plan_role: 'service',
        };
        this.addChunkedValues(serviceProperties, 'Vollständiger Verlegeplan', completePlanText);
        this.addChunkedValues(serviceProperties, 'Vollständiger Zuschnittsplan', completeCutPlanText);
        items.push({ id: Number(this.config.firstCutVariantId), quantity: 1, properties: serviceProperties });
        if (plan.cutCount > 1) {
          items.push({ id: Number(this.config.additionalCutVariantId), quantity: plan.cutCount - 1, properties: serviceProperties });
        }
      }

      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      if (status) status.textContent = 'Ihre Restposten und Zuschnitte werden in den Warenkorb gelegt …';

      try {
        const addUrl = String(this.config.cartAddUrl || '/cart/add').replace(/\.js$/, '') + '.js';
        const addResponse = await fetch(addUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!addResponse.ok) {
          const error = await addResponse.json().catch(() => ({}));
          throw new Error(error.description || `Warenkorbfehler ${addResponse.status}`);
        }

        const updateUrl = String(this.config.cartUpdateUrl || '/cart/update').replace(/\.js$/, '') + '.js';
        const orderAttributes = {
          'Bestellung mit Zuschnitt': 'Ja',
          'Zuschnittsplan-ID': orderId,
          'Letzter Zuschnittauftrag': orderId,
          [multiPieceOrder ? 'Restposten-Bedarfsplan' : 'Restposten-Verlegeplan']: multiPieceOrder ? requestSummary : projectSize,
          'Restposten-Zuschnittsplan': planSummary,
          'Planübersicht': planSummary,
        };
        this.addChunkedValues(orderAttributes, 'Verlegepositionen', completePlanText);
        this.addChunkedValues(orderAttributes, 'Zuschnittsteile', completeCutPlanText);
        await fetch(updateUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            attributes: orderAttributes,
          }),
        });
        window.location.assign(this.safeInternalUrl(this.config.cartUrl || '/cart'));
      } catch (error) {
        console.error('Restposten-Finder: Flächenplan konnte nicht in den Warenkorb gelegt werden.', error);
        if (status) status.textContent = `Der Plan konnte nicht in den Warenkorb gelegt werden: ${error.message}. Bitte versuchen Sie es erneut oder senden Sie uns Ihre Anfrage.`;
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }

    async addPlanToCart(button) {
      const plan = this.activePlan;
      if (!plan || button.disabled) return;
      const criteria = this.activePlanCriteria || this.lastCriteria || {};
      const status = this.querySelector('[data-plan-order-status]');
      const orderId = `RF-${Date.now().toString(36).toUpperCase()}`;
      const multiPieceOrder = plan.isMultiPieceOrder === true;
      const nearFitAccepted = plan.acceptedNearFit === true;
      const reducedArea = criteria.areaFallbackKind === 'reduced-area';
      const narrowStrip = criteria.areaFallbackKind === 'narrow-strip';
      const narrowSizeLabel = (criteria.narrowPieceSizes || []).map((size) => `${size} mm`).join(' / ');
      const requestSummary = multiPieceOrder
        ? (plan.requestedCuts || []).map((cut) => `${cut.quantity}× ${cut.length}×${cut.width} mm`).join(' · ')
        : nearFitAccepted
          ? `Wunsch ${plan.requestedLength}×${plan.requestedWidth} mm · Liefermaß ${plan.deliveredLength}×${plan.deliveredWidth} mm`
          : reducedArea
            ? `Wunsch ${criteria.requestedLength}×${criteria.requestedWidth} mm · gewählt ${plan.targetLength}×${plan.targetWidth} mm`
            : narrowStrip
              ? `${plan.targetLength}×${plan.targetWidth} mm · ${narrowSizeLabel} Randstreifen`
              : `${plan.targetLength}×${plan.targetWidth} mm`;
      const projectSize = multiPieceOrder ? `${plan.requestedPieceCount} Roste` : requestSummary;
      const planSummary = `${this.planRawPlateCount(plan)} Rohplatten / ${plan.distinctProducts} Posten / ${plan.cutCount} Schnitte`;
      const parcelShipping = this.parcelShippingProfile(plan);
      const unloadingDelivery = JSON.stringify(this.unloadingDelivery(plan));
      const unloadingCounts = new Map();
      plan.products.forEach(group => {
        const id = String(group.product.variantId);
        unloadingCounts.set(id, (unloadingCounts.get(id) || 0) + Number(group.quantity));
      });
      const unloadingMaterials = Array.from(unloadingCounts).sort((a,b) => a[0].localeCompare(b[0])).map(([id,q]) => `${id}:${q}`).join(',');
      const sharedProperties = (role) => ({
        _restposten_plan_id: orderId,
        _restposten_plan_role: role,
        ...(role === 'material' ? {_unloading_kind:'grating', _unloading_delivery:unloadingDelivery, _unloading_materials:unloadingMaterials} : {}),
        ...(parcelShipping.eligible
          ? {
              _shipping_after_cut: 'parcel',
              _shipping_finished_length_mm: parcelShipping.length,
              _shipping_finished_width_mm: parcelShipping.width,
              _shipping_finished_height_mm: parcelShipping.height,
              _shipping_finished_weight_g: parcelShipping.weightGrams,
              _shipping_weight_estimated: parcelShipping.weightEstimated ? 'true' : 'false',
            }
          : {}),
      });
      const items = plan.products.map((group) => ({
        id: Number(group.product.variantId),
        quantity: group.quantity,
        properties: sharedProperties('material'),
      }));

      if (plan.cutCount > 0) {
        items.push({
          id: parcelShipping.eligible
            ? Number(this.config.parcelCutVariantId || 54667718000986)
            : Number(this.config.firstCutVariantId),
          quantity: 1,
          properties: sharedProperties('service'),
        });
        if (plan.cutCount > 1) {
          items.push({
            id: Number(this.config.additionalCutVariantId),
            quantity: plan.cutCount - 1,
            properties: sharedProperties('service'),
          });
        }
      }

      const pdfOwnerIndex = plan.cutCount > 0
        ? items.findIndex((item) => item.properties._restposten_plan_role === 'service')
        : 0;
      const pdfOwner = items[pdfOwnerIndex];
      Object.assign(pdfOwner.properties, {
        'Plan-ID': orderId,
        ...(nearFitAccepted
          ? {
              Bestellmaße: requestSummary,
              Wunschmaß: `${plan.requestedLength}×${plan.requestedWidth} mm`,
              Liefermaß: `${plan.deliveredLength}×${plan.deliveredWidth} mm`,
              Maßabweichung: `Länge ${this.signedMillimetres(plan.deltaLength)}, Breite ${this.signedMillimetres(plan.deltaWidth)}`,
              Planart: 'Maßalternative ohne Zuschnitt',
            }
          : multiPieceOrder
          ? { Bestellmaße: requestSummary, Planart: 'Mehrfachzuschnitt' }
          : {
              Projektfläche: projectSize,
              Planart: criteria.mode === 'piece' ? 'Teilungsplan' : reducedArea ? 'Verlegeplan – kleinere Fläche' : narrowStrip ? 'Verlegeplan – schmaler Randstreifen' : 'Verlegeplan',
              ...(reducedArea ? { 'Ursprüngliche Projektfläche': `${criteria.requestedLength}×${criteria.requestedWidth} mm`, Abweichung: this.areaReductionText({ lengthReduction: criteria.areaReductionLengthMm, widthReduction: criteria.areaReductionWidthMm }, ', ') } : {}),
              ...(narrowStrip ? { Ausnahme: `${narrowSizeLabel} Randstreifen bewusst ausgewählt` } : {}),
            }),
        ...(criteria.maxPieceLength ? { 'Max. Teilstücklänge': `${criteria.maxPieceLength} mm` } : {}),
        ...(this.splitDirectionLabel(criteria) ? { Teilungsrichtung: this.splitDirectionLabel(criteria) } : {}),
        Planübersicht: planSummary,
        ...(parcelShipping.eligible
          ? {
              Versandprüfung: `Paketmaß nach Zuschnitt: ${parcelShipping.length}×${parcelShipping.width}×${parcelShipping.height} mm, ca. ${this.formatNumber(parcelShipping.weightGrams / 1000, 1)} kg`,
            }
          : {}),
      });

      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      if (status) status.textContent = 'Ihr persönlicher Verlege- und Zuschnittsplan wird als PDF erstellt …';

      let uploadedOwnerKey = '';
      try {
        const pdfBlob = await this.createPlanPdf(plan, orderId);
        if (!pdfBlob?.size) throw new Error('Die Plan-PDF konnte nicht erstellt werden');

        const addUrl = String(this.config.cartAddUrl || '/cart/add').replace(/\.js$/, '') + '.js';
        const formData = new FormData();
        formData.append('id', String(pdfOwner.id));
        formData.append('quantity', String(pdfOwner.quantity));
        Object.entries(pdfOwner.properties).forEach(([name, value]) => {
          formData.append(`properties[${name}]`, String(value));
        });
        formData.append(
          'properties[Plan-PDF]',
          pdfBlob,
          nearFitAccepted
            ? `FIBROLUX-${orderId}-Massvergleich.pdf`
            : `FIBROLUX-${orderId}-Verlege-und-Zuschnittsplan.pdf`,
        );

        if (status) status.textContent = 'Die Plan-PDF wird sicher mit Ihrem Zuschnittauftrag verknüpft …';
        const ownerResponse = await fetch(addUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          body: formData,
        });
        const ownerData = await ownerResponse.json().catch(() => ({}));
        if (!ownerResponse.ok) {
          throw new Error(ownerData.description || `Warenkorbfehler ${ownerResponse.status}`);
        }
        uploadedOwnerKey = ownerData.key || '';
        const pdfUrl = ownerData.properties?.['Plan-PDF'];
        if (!pdfUrl || !String(pdfUrl).includes('/uploads/')) {
          throw new Error('Die Plan-PDF wurde nicht korrekt an den Auftrag angehängt');
        }

        const remainingItems = items.filter((item, index) => index !== pdfOwnerIndex);
        if (remainingItems.length) {
          const remainingResponse = await fetch(addUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: remainingItems }),
          });
          if (!remainingResponse.ok) {
            const error = await remainingResponse.json().catch(() => ({}));
            throw new Error(error.description || `Warenkorbfehler ${remainingResponse.status}`);
          }
        }

        window.location.assign(this.safeInternalUrl(this.config.cartUrl || '/cart'));
      } catch (error) {
        console.error('Restposten-Finder: Plan-PDF oder Warenkorbposition konnte nicht erstellt werden.', error);
        if (uploadedOwnerKey) {
          const addUrl = String(this.config.cartAddUrl || '/cart/add').replace(/\.js$/, '') + '.js';
          const changeUrl = addUrl.replace(/\/add\.js$/, '/change.js');
          await fetch(changeUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: uploadedOwnerKey, quantity: 0 }),
          }).catch(() => {});
        }
        if (status) status.textContent = `Der Auftrag wurde nicht hinzugefügt: ${error.message}. Bitte versuchen Sie es erneut oder senden Sie uns Ihre Anfrage.`;
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }

    addChunkedValues(target, label, value, maxLength = 220) {
      const source = String(value || '').trim();
      if (!source) return target;
      const tokens = source.split(' | ');
      const chunks = [];
      let current = '';

      tokens.forEach((token) => {
        const next = current ? `${current} | ${token}` : token;
        if (current && next.length > maxLength) {
          chunks.push(current);
          current = token;
        } else {
          current = next;
        }
      });
      if (current) chunks.push(current);
      chunks.forEach((chunk, index) => {
        target[`${label} ${index + 1}/${chunks.length}`] = chunk;
      });
      return target;
    }

    safeInternalUrl(value) {
      try {
        const url = new URL(String(value || '/'), window.location.origin);
        return url.origin === window.location.origin ? `${url.pathname}${url.search}${url.hash}` : '/';
      } catch (error) {
        return '/';
      }
    }

    updateVisual(match, criteria) {
      const { product, fit } = match;
      this.visualMetrics.hidden = false;
      this.metricRaw.textContent = `${product.length}×${product.width} mm`;
      this.metricWaste.textContent = `${this.formatNumber(fit.wastePercent, 1)} %`;
      this.drawingLength.textContent = `${criteria.length} mm`;
      this.drawingWidth.textContent = `${criteria.width} mm`;
      this.updateDrawingGeometry(criteria.length, criteria.width);

      if (criteria.mode === 'area') {
        const metrics = this.matchAreaMetrics(match);
        this.visualKicker.textContent = 'Passender Flächenvorschlag';
        this.visualTitle.textContent = `${metrics.rawPlateCount} ${metrics.rawPlateCount === 1 ? 'Rohplatte' : 'Rohplatten'} für Ihre Fläche`;
        this.visualText.textContent = `${this.capitalize(product.color)}, ${this.surfaceLabel(product.surface)}, ${this.meshLabel(product.mesh)}, ${this.formatProductHeight(product.height, product.family, { detailed: true })}. Voraussichtlicher Verschnitt: ${this.formatNumber(metrics.wastePercent, 1)} %.`;
        this.metricSecondaryLabel.textContent = 'Raster';
        this.metricSecondary.textContent = `${fit.columns} × ${fit.rows} / ${metrics.rawPlateCount} Platten`;
        this.metricWaste.textContent = `${this.formatNumber(metrics.wastePercent, 1)} %`;
      } else {
        this.visualKicker.textContent = fit.cuts ? 'Passender Restposten mit Zuschnitt' : 'Passender Restposten ohne Zuschnitt';
        this.visualTitle.textContent = fit.cuts ? `${product.length}×${product.width} mm passt zu Ihrem Maß` : 'Rohmaß und Wunschmaß stimmen überein.';
        this.visualText.textContent = `${this.capitalize(product.color)}, ${this.surfaceLabel(product.surface)}, ${this.meshLabel(product.mesh)}, ${this.formatProductHeight(product.height, product.family, { detailed: true })}. ${this.getFitText(match, criteria)}`;
        this.metricSecondaryLabel.textContent = 'Zuschnitt';
        this.metricSecondary.textContent = fit.cuts ? `${fit.cuts} ${fit.cuts === 1 ? 'Schnitt' : 'Schnitte'}` : 'nicht nötig';
      }
    }

    updateDrawingLabels() {
      this.syncSplitDirection();
      const criteria = this.readCriteria();
      if (this.isMultiPieceCriteria(criteria)) {
        this.drawingLength.textContent = `${criteria.totalQuantity} Roste`;
        this.drawingWidth.textContent = `${criteria.cuts.length} ${criteria.cuts.length === 1 ? 'Maß' : 'Maße'}`;
        this.updateDrawingGeometry(criteria.length, criteria.width);
        return;
      }
      this.drawingLength.textContent = criteria.length ? `${criteria.length} mm` : 'Länge';
      this.drawingWidth.textContent = criteria.width ? `${criteria.width} mm` : 'Breite';
      this.updateDrawingGeometry(criteria.length, criteria.width);
    }

    updateDrawingGeometry(length, width) {
      if (!this.drawingPanel || !this.drawingGrid || !this.drawingGlow) return;
      const safeLength = Math.max(1, Number(length) || 1600);
      const safeWidth = Math.max(1, Number(width) || 1000);
      const ratio = Math.max(0.18, Math.min(8, safeLength / safeWidth));
      const maxWidth = 430;
      const maxHeight = 190;
      const minWidth = 72;
      const minHeight = 48;
      let plateWidth;
      let plateHeight;

      if (ratio >= maxWidth / maxHeight) {
        plateWidth = maxWidth;
        plateHeight = Math.max(minHeight, plateWidth / ratio);
      } else {
        plateHeight = maxHeight;
        plateWidth = Math.max(minWidth, plateHeight * ratio);
      }

      const x = 310 - plateWidth / 2;
      const y = 184 - plateHeight / 2;
      const lengthY = y - 24;
      const widthX = x + plateWidth + 24;
      const setRect = (element) => {
        element.setAttribute('x', x.toFixed(1));
        element.setAttribute('y', y.toFixed(1));
        element.setAttribute('width', plateWidth.toFixed(1));
        element.setAttribute('height', plateHeight.toFixed(1));
      };

      [this.drawingPanel, this.drawingGrid, this.drawingGlow].forEach(setRect);
      this.drawingLengthLine?.setAttribute(
        'd',
        `M${x.toFixed(1)} ${lengthY.toFixed(1)}H${(x + plateWidth).toFixed(1)}M${x.toFixed(1)} ${(lengthY - 5).toFixed(1)}V${(lengthY + 5).toFixed(1)}M${(x + plateWidth).toFixed(1)} ${(lengthY - 5).toFixed(1)}V${(lengthY + 5).toFixed(1)}`
      );
      this.drawingWidthLine?.setAttribute(
        'd',
        `M${widthX.toFixed(1)} ${y.toFixed(1)}V${(y + plateHeight).toFixed(1)}M${(widthX - 5).toFixed(1)} ${y.toFixed(1)}H${(widthX + 5).toFixed(1)}M${(widthX - 5).toFixed(1)} ${(y + plateHeight).toFixed(1)}H${(widthX + 5).toFixed(1)}`
      );
      this.drawingLength.setAttribute('x', '310');
      this.drawingLength.setAttribute('y', (lengthY - 10).toFixed(1));
      const widthLabelX = widthX + 18;
      const widthLabelY = y + plateHeight / 2;
      this.drawingWidth.setAttribute('x', widthLabelX.toFixed(1));
      this.drawingWidth.setAttribute('y', widthLabelY.toFixed(1));
      this.drawingWidth.setAttribute('transform', `rotate(90 ${widthLabelX.toFixed(1)} ${widthLabelY.toFixed(1)})`);
    }

    resetVisual() {
      this.visualMetrics.hidden = true;
      this.drawingLength.textContent = 'Länge';
      this.drawingWidth.textContent = 'Breite';
      this.updateDrawingGeometry(1600, 1000);
    }

    formatMoney(cents) {
      return new Intl.NumberFormat(this.config.locale || 'de-DE', {
        style: 'currency',
        currency: this.config.currency || 'EUR',
      }).format(Number(cents || 0) / 100);
    }

    formatNumber(value, digits = 0) {
      return new Intl.NumberFormat(this.config.locale || 'de-DE', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      }).format(Number(value || 0));
    }

    capitalize(value) {
      const source = String(value || '');
      return source ? source.charAt(0).toLocaleUpperCase('de-DE') + source.slice(1) : '';
    }

    escapeHtml(value) {
      return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }
  }

  customElements.define('restposten-finder', RestpostenFinder);
})();
